import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  IonButton,
  IonCard,
  IonIcon,
  IonSpinner,
  IonLoading,
  IonAlert,
  IonActionSheet,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonInput,
  IonItem,
  IonModal,
  IonText,
  IonTitle,
  IonToast,
  IonToolbar,
  IonTextarea,
  IonPage,
  IonButtons,
} from '@ionic/react';
import {
  addOutline,
  arrowRedoOutline,
  arrowUndoOutline,
  briefcaseOutline,
  cameraOutline,
  chevronBackOutline,
  chatbubblesOutline,
  closeCircleOutline,
  closeOutline,
  copyOutline,
  createOutline,
  calendarOutline,
  checkmarkCircleOutline,
  chevronForwardOutline,
  clipboardOutline,
  documentTextOutline,
  imagesOutline,
  informationCircleOutline,
  micOutline,
  personOutline,
  removeOutline,
  openOutline,
  refreshOutline,
  send,
  searchOutline,
  settingsOutline,
  shareOutline,
  stopCircleOutline,
  timeOutline,
  trashOutline,
  thumbsDown,
  thumbsDownOutline,
  thumbsUp,
  thumbsUpOutline,
} from 'ionicons/icons';
import styled from 'styled-components';
import {
  chatApi,
  type AssistantMode,
  type ChatApiError,
  type ManufacturerOption,
  type MessageFeedback,
  type PartsModelChoicePromptArtifact,
  type ServiceTitanChoicePromptArtifact,
  type ServiceTitanConnectionStatus,
  type ServiceTitanEquipmentReviewArtifact,
  type ServiceTitanSelection,
} from '../services/chatApi';
import {
  getCachedTimeline,
  getChatSessionLiveSnapshot,
  setChatSessionLiveSnapshot,
} from '../services/chatSessionCache';
import {
  getTransientTimelineItems,
  mergeServerTimeline,
  timelineTimestampValue,
} from '../services/chatTimelineState';
import { queryCache, queryKeys } from '../services/queryCache';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import api, { BACKEND_BASE_URL, WEBSOCKET_URL } from '../services/api';
import HeaderLogo from '../components/common/HeaderLogo';
import AppHeader from '../components/common/AppHeader';
import AssistantScopeControls from '../components/chat/AssistantScopeControls';
import PartsModelChoiceCard, { type PartsModelChoice } from '../components/chat/PartsModelChoiceCard';
import ServiceTitanChoiceCard, { type ServiceTitanChoice } from '../components/chat/ServiceTitanChoiceCard';
import ServiceTitanEquipmentReviewCard from '../components/chat/ServiceTitanEquipmentReviewCard';
import ScreenLoader from '../components/common/ScreenLoader';
import { useMarketingCampaign } from '../hooks/useMarketingCampaign';
import { useSimpleS3Handler } from '../hooks/useSimpleS3Handler';
import { Banner } from '../components/Marketing/Banner';
import { Keyboard } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Share } from '@capacitor/share';
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import io from 'socket.io-client';
import { formatAllLinks, initializeMarkdownLinkHandlers, openUrl } from '../utils/markdownLinkFormatter';
import { htmlToPrettyPlainText } from '../utils/htmlToPrettyPlainText';
import { getUserCapabilities } from '../utils/userCapabilities';
import { normalizeBackendAgentArtifactUrl, normalizeOemPdfPageImageUrl } from '../utils/agentArtifactUrls';
import {
  buildSelectedImageFromCameraPhoto,
  getImageSelectionErrorMessage,
  isCameraCancellationError,
  readImageFile,
  type SelectedImage,
} from '../utils/imageAttachment';
// Removed custom Container - using Ionic's IonPage instead

type SpeechRecognitionResultLike = {
  isFinal: boolean;
  [index: number]: { transcript: string };
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
};

type SpeechRecognitionErrorEventLike = {
  error?: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
};

type SpeechRecognitionConstructorLike = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructorLike;
    webkitSpeechRecognition?: SpeechRecognitionConstructorLike;
  }
}

// Generic HTML content styling component
const HtmlContent = styled.div<{ $compact?: boolean }>`
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  /* Style HTML content */
  h1 {
    margin: ${props => props.$compact ? '6px 0 4px 0' : '12px 0 8px 0'};
    font-weight: 700;
    line-height: 1.2;
    font-size: ${props => props.$compact ? '16px' : '20px'};
    color: ${props => props.theme?.token?.colorText || '#000'};
  }

  h2 {
    margin: ${props => props.$compact ? '5px 0 3px 0' : '10px 0 6px 0'};
    font-weight: 600;
    line-height: 1.3;
    font-size: ${props => props.$compact ? '14px' : '18px'};
    color: ${props => props.theme?.token?.colorText || '#000'};
  }

  h3 {
    margin: ${props => props.$compact ? '4px 0 2px 0' : '8px 0 4px 0'};
    font-weight: 600;
    line-height: 1.3;
    font-size: ${props => props.$compact ? '13px' : '16px'};
    color: ${props => props.theme?.token?.colorText || '#000'};
  }

  h4, h5, h6 {
    margin: ${props => props.$compact ? '3px 0 2px 0' : '6px 0 3px 0'};
    font-weight: 600;
    line-height: 1.3;
    font-size: ${props => props.$compact ? '12px' : '14px'};
    color: ${props => props.theme?.token?.colorText || '#000'};
  }

  p {
    margin: ${props => props.$compact ? '4px 0' : '8px 0'};
    line-height: 1.55;
    white-space: normal;
  }

  ul, ol {
    margin: ${props => props.$compact ? '4px 0' : '8px 0'};
    padding-left: ${props => props.$compact ? '18px' : '24px'};
  }

  li {
    margin: ${props => props.$compact ? '2px 0' : '4px 0'};
    line-height: 1.5;
    white-space: normal;
  }

  strong, b {
    font-weight: 600;
  }

  em, i {
    font-style: italic;
  }

  code {
    background: #edf0f3;
    color: #172033;
    padding: ${props => props.$compact ? '1px 4px' : '2px 6px'};
    border-radius: ${props => props.$compact ? '4px' : '6px'};
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: ${props => props.$compact ? '0.85em' : '0.88em'};
  }

  pre {
    background: #0f1720;
    color: #f8fafc;
    padding: ${props => props.$compact ? '10px' : '14px'};
    border-radius: ${props => props.$compact ? '6px' : '8px'};
    overflow-x: auto;
    margin: ${props => props.$compact ? '8px 0' : '12px 0'};
    font-size: ${props => props.$compact ? '0.82em' : '0.88em'};
    line-height: 1.5;
  }

  pre code {
    background: none;
    color: inherit;
    padding: 0;
    border-radius: 0;
  }

  table.markdown-table {
    width: 100%;
    border-collapse: collapse;
    margin: ${props => props.$compact ? '8px 0' : '12px 0'};
    font-size: ${props => props.$compact ? '0.86rem' : '0.92rem'};
  }

  table.markdown-table th,
  table.markdown-table td {
    border: 1px solid #dbe2ec;
    padding: ${props => props.$compact ? '6px 8px' : '8px 10px'};
    text-align: left;
    vertical-align: top;
  }

  table.markdown-table th {
    background: #f3f6fb;
    font-weight: 700;
  }

  .markdown-table-wrap {
    width: 100%;
    overflow-x: auto;
  }

  .mermaid-diagram-wrap {
    width: 100%;
    max-width: 100%;
    margin: ${props => props.$compact ? '8px 0' : '12px 0'};
    overflow-x: auto;
    border: 1px solid #dbe2ec;
    border-radius: 8px;
    background: #ffffff;
    padding: ${props => props.$compact ? '8px' : '12px'};
  }

  .mermaid-diagram-wrap svg {
    display: block;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
  }

  blockquote {
    margin: ${props => props.$compact ? '6px 0' : '10px 0'};
    padding: ${props => props.$compact ? '4px 0 4px 10px' : '6px 0 6px 14px'};
    border-left: 3px solid #c7d0dd;
    color: #495465;
  }

  .oem-artifact-table-wrap {
    width: 100%;
    max-width: 100%;
    margin: ${props => props.$compact ? '6px 0' : '10px 0'};
    overflow-x: auto;
    border: 1px solid #d7dee8;
    border-radius: 8px;
    background: #ffffff;
  }

  table.oem-artifact-table {
    width: 100%;
    min-width: 560px;
    border-collapse: collapse;
    table-layout: auto;
    font-size: ${props => props.$compact ? '0.82rem' : '0.9rem'};
    line-height: 1.35;
  }

  table.oem-artifact-table th,
  table.oem-artifact-table td {
    padding: ${props => props.$compact ? '7px 9px' : '9px 11px'};
    border-bottom: 1px solid #e7ecf3;
    border-right: 1px solid #e7ecf3;
    text-align: left;
    vertical-align: top;
    white-space: normal;
  }

  table.oem-artifact-table th:last-child,
  table.oem-artifact-table td:last-child {
    border-right: 0;
  }

  table.oem-artifact-table tr:last-child td {
    border-bottom: 0;
  }

  table.oem-artifact-table th {
    background: #f3f6fb;
    color: #172033;
    font-weight: 650;
  }

  table.oem-artifact-table tbody tr:nth-child(even) td {
    background: #fafcff;
  }

  table.oem-artifact-table td:first-child {
    color: #172033;
    font-weight: 600;
    background: #f8fafd;
  }

  table.oem-artifact-table .is-empty {
    color: #9aa5b5;
  }

  a.oem-artifact-link {
    display: inline-flex;
    align-items: center;
    margin-top: ${props => props.$compact ? '4px' : '6px'};
    font-weight: 600;
  }

  a.oem-artifact-link.oem-artifact-preview-hidden {
    display: none;
  }

  blockquote {
    border-left: ${props => props.$compact ? '2px' : '3px'} solid rgba(0, 0, 0, 0.2);
    margin: ${props => props.$compact ? '2px 0' : '4px 0'};
    padding-left: ${props => props.$compact ? '8px' : '12px'};
    font-style: italic;
  }

  /* S3 Link styles */
  a.s3-link {
    color: ${props => props.theme?.token?.colorPrimary || '#2672be'};
    text-decoration: underline;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  a.s3-link:hover {
    opacity: 0.8;
  }

  a.s3-link.s3-link-loading {
    opacity: 0.7;
    pointer-events: none;
    position: relative;
  }

  a.s3-link.s3-link-loading::after {
    content: '⏳';
    margin-left: 4px;
    font-size: 0.8em;
  }

  a.s3-link.s3-link-error {
    color: #ff6b6b;
    opacity: 0.8;
  }

  a.s3-link.s3-link-error::after {
    content: '⚠️';
    margin-left: 4px;
    font-size: 0.8em;
  }

  /* Markdown link styles */
  a.markdown-link {
    color: ${props => props.theme?.token?.colorPrimary || '#2672be'};
    text-decoration: underline;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  a.markdown-link:hover {
    opacity: 0.8;
  }

  a.markdown-link.ahri-link,
  a.markdown-link[href*="/api/work-orders/pdf"],
  a.markdown-link[href*="%2Fapi%2Fwork-orders%2Fpdf"] {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    margin-top: 8px;
    padding: 9px 14px;
    border-radius: 8px;
    background: ${props => props.theme?.token?.colorPrimary || '#2672be'};
    color: #ffffff;
    font-weight: 800;
    text-decoration: none;
    box-shadow: 0 8px 18px rgba(38, 114, 190, 0.22);
  }

  a.markdown-link.ahri-link {
    background: #ffffff;
    color: ${props => props.theme?.token?.colorPrimary || '#2672be'};
    border: 1px solid ${props => props.theme?.token?.colorPrimary || '#2672be'};
    box-shadow: none;
  }

  a.markdown-link.ahri-link::after {
    content: '>';
    margin-left: 8px;
    font-size: 0.9em;
    line-height: 1;
  }

  a.markdown-link.ahri-link:hover,
  a.markdown-link[href*="/api/work-orders/pdf"]:hover,
  a.markdown-link[href*="%2Fapi%2Fwork-orders%2Fpdf"]:hover {
    opacity: 0.92;
  }
`;

// Removed custom Header - using Ionic's IonHeader + IonToolbar instead


const BackButton = styled(IonButton)`
  --color: ${props => props.theme?.token?.colorIcon};
  --background: transparent;
  --background-hover: rgba(0, 0, 0, 0.05);
  --background-activated: rgba(0, 0, 0, 0.1);
  --padding-start: 8px;
  --padding-end: 8px;
  margin-left: 16px;
  height: 40px;
  width: 40px;

  ion-icon {
    color: ${props => props.theme?.token?.colorIcon};
    font-size: 24px;
  }
`;

// Updated ChatContainer to work with Ionic's IonContent
const ChatContainer = styled.div`
  background: ${props => props.theme?.token?.colorBgContainer};
  -webkit-overflow-scrolling: touch;
  position: relative;
  min-height: 100%;
  height: 100%;
  overflow-x: hidden;
  /* Add scroll padding to prevent last message from hiding behind input */
  scroll-padding-bottom: 80px;
  display: flex;
  flex-direction: column;
`;

const Message = styled.div<{ $isUser: boolean }>`
  width: ${props => props.$isUser ? 'auto' : 'calc(100% - 32px)'};
  max-width: ${props => props.$isUser ? 'calc(85% - 32px)' : 'calc(100% - 32px)'};
  margin: ${props => props.$isUser ? '8px 16px' : '14px 16px'};
  padding: ${props => props.$isUser ? '10px 14px' : '0'};
  border-radius: ${props => props.$isUser ? '18px 18px 2px 18px' : '0'};
  background: ${props => props.$isUser ? '#eef1f5' : 'transparent'};
  color: ${props => props.theme?.token?.colorText ?? '#111827'};
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
  border: none;
  box-shadow: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  line-height: ${props => props.$isUser ? '1.45' : '1.65'};
  font-size: ${props => props.$isUser ? '16px' : 'clamp(17px, 1.8vw, 22px)'};
`;

const FeedbackActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 10px;
`;

const FEEDBACK_ICON_PX = 18;
const CHAT_SESSION_TITLE_MAX_LENGTH = 60;
const hasControlCharacter = (value: string): boolean => Array.from(value).some((character) => {
  const codePoint = character.codePointAt(0) ?? 0;
  return codePoint <= 0x1f || codePoint === 0x7f;
});
const DEFAULT_MESSAGE_END_SPACER_PX = 140;
const PINNED_MESSAGE_END_SPACER_PX = 420;
const SMARTDOCS_SHARE_FOOTER = 'Shared from SmartDocs\nAI help for HVAC techs: https://usesmartdocs.com';
const SOURCE_TAB_ORDER = ['all', 'exact', 'service', 'install', 'engineering'] as const;
type SourceTabKey = typeof SOURCE_TAB_ORDER[number];

const FeedbackButton = styled(IonButton)<{ $selected?: boolean }>`
  --border-radius: 999px;
  --padding-start: 4px;
  --padding-end: 4px;
  margin: 0;
  /* Override Ionic's default min-width so icons sit flush without gaps */
  --min-width: unset;
  width: 32px;
  height: 32px;
  --background: transparent;
  --background-hover: ${props => props.theme?.token?.colorBgTextHover ?? 'rgba(0, 0, 0, 0.04)'};
  --background-activated: ${props => props.theme?.token?.colorBgTextHover ?? 'rgba(0, 0, 0, 0.06)'};
  --color: ${props =>
    props.$selected
      ? (props.theme?.token?.colorPrimary || '#2672be')
      : (props.theme?.token?.colorIcon ?? props.theme?.token?.colorTextSecondary ?? '#737980')};
  --box-shadow: none;
`;

const ReferenceDocuments = styled.div`
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid ${props => props.theme?.token?.colorBorder ?? '#e6eaf0'};
`;

const ReferenceDocumentsSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
`;

const ReferenceDocumentsToggle = styled(IonButton)`
  width: auto;
  min-height: 32px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  --padding-top: 6px;
  --padding-bottom: 6px;
  --padding-start: 2px;
  --padding-end: 4px;
  --border-radius: 10px;
  --background: transparent;
  --background-hover: rgba(100, 116, 139, 0.08);
  --background-activated: rgba(100, 116, 139, 0.12);
  --box-shadow: none;
  --color: ${props => props.theme?.token?.colorTextSecondary ?? '#697586'};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0;
  text-transform: none;
`;

const ReferenceDocumentsInfoIcon = styled(IonIcon)`
  width: 14px;
  height: 14px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#7f8a9b'};
`;

const ReferenceDocumentThumbnail = styled.span`
  width: 48px;
  height: 66px;
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 7px 6px;
  border: 1px solid #d8dee8;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: 0 3px 8px rgba(15, 23, 42, 0.12);
  overflow: hidden;
`;

const ReferenceDocumentThumbnailHeader = styled.span`
  font-size: 4px;
  font-weight: 800;
  line-height: 1;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ReferenceDocumentThumbnailLine = styled.span<{ $short?: boolean }>`
  display: block;
  width: ${props => props.$short ? '70%' : '100%'};
  height: 2px;
  border-radius: 999px;
  background: #d6dce5;
`;

const ReferenceDocumentThumbnailTable = styled.span`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  margin-top: 1px;

  span {
    height: 5px;
    border-radius: 1px;
    background: #edf1f6;
  }
`;

const ReferenceDocumentsToggleIcon = styled(IonIcon)<{ $open?: boolean }>`
  width: 16px;
  height: 16px;
  transform: rotate(${props => props.$open ? '90deg' : '0deg'});
  transition: transform 0.15s ease;
`;

const ReferenceSourceDetails = styled.div`
  margin-top: 10px;
  border: 1px solid #dfe7f0;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.08);
`;

const ReferenceSourceTabs = styled.div`
  display: flex;
  gap: 6px;
  padding: 10px 10px 8px;
  overflow-x: auto;
  border-bottom: 1px solid #e8edf3;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ReferenceSourceTab = styled(IonButton)<{ $active?: boolean }>`
  flex: 0 0 auto;
  min-height: 34px;
  --border-radius: 999px;
  --padding-top: 7px;
  --padding-bottom: 7px;
  --padding-start: 12px;
  --padding-end: 12px;
  --border-width: 1px;
  --border-style: solid;
  --border-color: ${props => props.$active ? '#91b5f7' : '#dbe3ee'};
  --background: ${props => props.$active ? '#eef4ff' : '#ffffff'};
  --background-hover: ${props => props.$active ? '#eef4ff' : '#f8fafc'};
  --background-activated: ${props => props.$active ? '#eef4ff' : '#f3f6fa'};
  --color: ${props => props.$active ? '#2451a6' : '#465467'};
  --box-shadow: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
`;

const ReferenceSourceTableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 72px;
  gap: 8px;
  padding: 9px 10px;
  color: #697586;
  font-size: 11px;
  font-weight: 750;
  text-transform: uppercase;
  letter-spacing: 0;
  border-bottom: 1px solid #edf1f6;

  span:last-child {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
  }
`;

const ReferenceSourceRows = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReferenceSourceRow = styled.button`
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 54px 16px;
  align-items: center;
  gap: 8px;
  padding: 12px 10px;
  border: none;
  border-bottom: 1px solid #edf1f6;
  background: transparent;
  color: #111827;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:not(:disabled):active {
    background: #f7faff;
  }

  &:disabled {
    cursor: default;
    opacity: 0.72;
  }

  ${ReferenceDocumentThumbnail} {
    width: 34px;
    height: 48px;
    padding: 5px 4px;
    gap: 3px;
  }
`;

const ReferenceSourceRowMain = styled.span`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const ReferenceSourceRowTitle = styled.span`
  font-size: 13px;
  font-weight: 750;
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const ReferenceSourceTags = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  span {
    padding: 2px 5px;
    border-radius: 999px;
    background: #f2f4f7;
    color: #526070;
    font-size: 10px;
    font-weight: 650;
    line-height: 1.2;
  }
`;

const ReferenceSourceMatchExact = styled.span`
  && {
    background: #eafaf2;
    color: #177245;
  }
`;

const ReferenceSourceMatchPartial = styled.span`
  && {
    background: #fff7e6;
    color: #956100;
  }
`;

const ReferenceSourceMatchScanned = styled.span`
  && {
    background: #f2f4f7;
    color: #526070;
  }
`;

const ReferenceSourceRelevance = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  color: #465467;
  font-size: 11px;
  font-weight: 750;
`;

const ReferenceSourceRelevanceDot = styled.span<{ $level: 'high' | 'medium' | 'low' }>`
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${props =>
    props.$level === 'high'
      ? '#17a56b'
      : props.$level === 'medium'
        ? '#f4a62a'
        : '#9aa4b2'};
`;

const ReferenceSourceOpenIcon = styled(IonIcon)`
  width: 15px;
  height: 15px;
  color: #697586;
`;

/** Bottom-sheet style modal: rounded top, shadow (Ionic sheet + our tokens). */
const DownvoteFeedbackModal = styled(IonModal)`
  --border-radius: 16px 16px 0 0;
  --box-shadow: 0 -8px 32px rgba(15, 23, 42, 0.12);
`;

const SessionTitleModal = styled(IonModal)`
  --background: ${props => props.theme?.token?.colorBgContainer ?? 'var(--ion-background-color)'};
`;

const SessionTitleInputItem = styled(IonItem)`
  --background: transparent;
  --border-color: ${props => props.theme?.token?.colorBorder ?? 'var(--ion-item-border-color)'};
  --inner-padding-end: 0;
  --padding-start: 0;
  --min-height: 48px;
  margin-top: 18px;
`;

const SessionTitleInput = styled(IonInput)`
  --color: ${props => props.theme?.token?.colorText ?? '#111827'};
  --placeholder-color: ${props => props.theme?.token?.colorTextTertiary ?? '#8a9099'};
  --padding-start: 0;
  --padding-end: 0;
  color: ${props => props.theme?.token?.colorText ?? '#111827'};
  font-size: 20px;

  &::part(native) {
    color: ${props => props.theme?.token?.colorText ?? '#111827'};
    opacity: 1;
  }
`;

const DownvoteReasonCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${props => props.theme?.token?.colorBorder ?? 'var(--ion-color-medium-tint)'};
  background: ${props => props.theme?.token?.colorBgContainer ?? 'var(--ion-card-background)'};
  overflow: hidden;
`;

const DownvoteReasonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  min-height: 48px;
  border-bottom: 1px solid ${props => props.theme?.token?.colorBorder ?? 'var(--ion-item-border-color)'};
  cursor: pointer;
  color: ${props => props.theme?.token?.colorText ?? 'var(--ion-text-color)'};
  font-size: 15px;
  line-height: 1.35;
  -webkit-tap-highlight-color: transparent;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: ${props => props.theme?.token?.colorBgTextHover ?? 'var(--ion-color-step-50, rgba(0, 0, 0, 0.04))'};
  }

  span {
    flex: 1;
    color: ${props => props.theme?.token?.colorText ?? 'var(--ion-text-color)'};
  }

  ion-checkbox {
    flex-shrink: 0;
    --size: 22px;
    --checkbox-background-checked: var(--ion-color-primary);
    --border-color-checked: var(--ion-color-primary);
    --checkmark-color: var(--ion-color-primary-contrast, #ffffff);
  }
`;

/** Matches ChatInput / app theme tokens for readable text and padding. */
const DownvoteDetailsTextarea = styled(IonTextarea)`
  margin-top: 4px;
  flex-shrink: 0;
  --padding-start: 14px;
  --padding-end: 14px;
  --padding-top: 12px;
  --padding-bottom: 12px;
  --background: ${props => props.theme?.token?.colorBgContainer ?? 'var(--ion-background-color)'};
  --color: ${props => props.theme?.token?.colorText ?? 'var(--ion-text-color)'};
  --placeholder-color: ${props => props.theme?.token?.colorTextSecondary ?? 'var(--ion-color-medium)'};
  border: 1px solid ${props => props.theme?.token?.colorBorder ?? 'var(--ion-color-medium-tint)'};
  border-radius: 10px;

  &::part(textarea) {
    min-height: 72px;
    max-height: 112px;
    overflow-y: auto;
    resize: none;
  }
`;

/** Action row lives inside IonContent — no height dependency on the delegate-host. */
const DownvoteActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const MessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 8px 0 0 0; /* Top padding only - bottom spacing handled by scroll anchor */
  box-sizing: border-box;
  background: ${props => props.theme?.token?.colorBgContainer};
  position: relative;
  flex: 1; /* Take up all available space in the flex container */
  min-height: 100%;
  overflow-x: hidden;
`;

// New styled components for the input area
const InputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
  padding: 8px;
  background: ${props => props.theme?.token?.colorBgContainer};
  flex-shrink: 0;
  position: relative;
  z-index: 80;
`;

const InputWrapper = styled.div`
  flex: 1;
  position: relative;
  z-index: 90;
  max-height: 220px;
  overflow: visible;

  /* Target the actual textarea element inside ion-textarea */
  ion-textarea textarea {
    max-height: 120px !important;
    overflow-y: auto !important;
    resize: none !important;
  }
`;

const SendButton = styled(IonButton)<{ $stopping?: boolean }>`
  && {
    --background: ${props => props.$stopping ? '#dc2626' : props.theme?.token?.colorPrimary};
    --background-hover: ${props => props.$stopping ? '#b91c1c' : props.theme?.token?.colorPrimary};
    --background-activated: ${props => props.$stopping ? '#991b1b' : props.theme?.token?.colorPrimary};
    --color: white;
    --border-radius: 50%;
    --padding-start: 0;
    --padding-end: 0;
    --padding-top: 0;
    --padding-bottom: 0;
    --box-shadow: none;
    width: 40px;
    min-width: 40px;
    max-width: 40px;
    height: 40px;
    min-height: 40px;
    max-height: 40px;
    margin: 0 0 0 4px;
    flex-shrink: 0;
    flex-grow: 0;

    &::part(native) {
      width: 40px;
      height: 40px;
      padding: 0;
      border-radius: 50%;
    }

    ion-icon {
      font-size: 18px;
    }
  }
`;

const AttachButton = styled(IonButton)`
  --background: ${props => props.theme?.token?.colorBgContainer || '#eef2f7'};
  --background-hover: ${props => props.theme?.token?.colorBgContainer || '#e5ebf3'};
  --background-activated: ${props => props.theme?.token?.colorBgContainer || '#dde5ef'};
  --color: ${props => props.theme?.token?.colorText || '#263238'};
  --border-radius: 10px;
  --padding-start: 0;
  --padding-end: 0;
  min-width: 24px;
  margin: 0;
  flex-shrink: 0;

  ion-icon {
    font-size: 20px;
    color: ${props => props.theme?.token?.colorText || '#263238'};
  }
`;

const MicButton = styled(IonButton)<{ $listening?: boolean; $unsupported?: boolean }>`
  --background: ${props => props.$listening
    ? (props.theme?.token?.colorPrimary || '#2563eb')
    : (props.theme?.token?.colorBgContainer || '#eef2f7')};
  --background-hover: ${props => props.$listening
    ? (props.theme?.token?.colorPrimary || '#2563eb')
    : (props.theme?.token?.colorBgContainer || '#e5ebf3')};
  --background-activated: ${props => props.$listening
    ? (props.theme?.token?.colorPrimary || '#2563eb')
    : (props.theme?.token?.colorBgContainer || '#dde5ef')};
  --color: ${props => props.$listening ? '#ffffff' : (props.theme?.token?.colorText || '#263238')};
  --border-radius: 10px;
  --padding-start: 0;
  --padding-end: 0;
  min-width: 24px;
  margin: 0;
  flex-shrink: 0;
  opacity: ${props => props.$unsupported ? 0.55 : 1};

  ion-icon {
    font-size: 20px;
    color: ${props => props.$listening ? '#ffffff' : (props.theme?.token?.colorText || '#263238')};
  }
`;

const VoiceStatusText = styled.div<{ $error?: boolean }>`
  margin: 4px 2px 0;
  color: ${props => props.$error ? '#b42318' : (props.theme?.token?.colorTextSecondary || '#64748b')};
  font-size: 11px;
  font-weight: 650;
  line-height: 1.25;
`;

const DisclaimerText = styled.div`
  text-align: center;
  font-size: 11px;
  color: ${props => props.theme?.token?.colorTextTertiary || '#9ca3af'};
  padding: 4px 16px 8px 16px;
  line-height: 1.4;
  opacity: 0.7;
`;


const SuggestionChipsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  box-sizing: border-box;
  padding: 8px 16px 4px;
  background: ${props => props.theme?.token?.colorBgContainer ?? 'var(--ion-background-color)'};
`;

const SuggestionChipsLabel = styled.span`
  font-size: 12px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? 'var(--ion-color-medium)'};
`;

const SuggestionChip = styled.button`
  background: transparent;
  border: none;
  border-left: 2px solid ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  border-radius: 0;
  padding: 8px 12px;
  font-size: 13px;
  color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s;

  &:active {
    background: ${props => props.theme?.token?.colorBgTextHover ?? 'rgba(0, 0, 0, 0.06)'};
  }
`;

/**
 * Brand-specific suggestion chips for the chat session.
 *
 * HOW TO ADD A NEW BRAND:
 * 1. Add a new entry to BRAND_SUGGESTIONS with the distributor name as the key
 * 2. The key should match the user's `distributor_name` field exactly (case-insensitive matching is used)
 * 3. Each entry should have 3 example prompts that are specific to that brand's products
 * 4. Use real model numbers and error codes from that brand for maximum impact
 *
 * Example:
 *   'Koch Air': [
 *     'What does fault code 44 mean on a Carrier 24ACC636A003?',
 *     'Find the blower motor part number for a Carrier 58STA090',
 *     'My Carrier 25HPA660A300 is short cycling, what should I check?',
 *   ],
 */
const BRAND_SUGGESTIONS: Record<string, string[]> = {
  'Habegger': [
    'Help me draft a work order for a customer signature.',
    'Find the wire schematic for a Carrier 38MAQB24 unit.',
    'Find the main board part number on a Carrier 38MAQB24 unit.',
  ],
};

const DEFAULT_SUGGESTIONS = [
  'Help me draft a work order for a customer signature.',
  'Find a wire schematic for a unit.',
  'Find the main board part number for a unit.',
];

const SERVICE_TITAN_SUGGESTIONS = [
  'Show the installed equipment at a ServiceTitan location.',
  'Help me add equipment from nameplate photos to this job.',
  'Check whether this serial number already exists at the jobsite.',
];

function getSuggestionChips(
  distributorName: string | null | undefined,
  mode: AssistantMode,
): string[] {
  if (mode === 'servicetitan') return SERVICE_TITAN_SUGGESTIONS;
  if (!distributorName) return DEFAULT_SUGGESTIONS;
  const key = Object.keys(BRAND_SUGGESTIONS).find(
    k => k.toLowerCase() === distributorName.toLowerCase()
  );
  return key ? BRAND_SUGGESTIONS[key] : DEFAULT_SUGGESTIONS;
}

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 12px;
  max-width: calc(95% - 32px);
  margin: 8px 16px;
  border-radius: 18px 18px 18px 2px;
  background: ${props => props.theme?.token?.colorBgContainer};
  color: ${props => props.theme?.token?.colorText};
  align-self: flex-start;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .thinking-text {
    font-size: 14px;
    font-weight: 500;
    background: linear-gradient(
      90deg,
      #888 0%,
      #aaa 25%,
      ${props => props.theme?.token?.colorPrimary} 50%,
      #aaa 75%,
      #888 100%
    );
    background-size: 200% 100%;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: thinkingGradient 2s ease-in-out infinite;
  }

  @keyframes thinkingGradient {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const ThinkingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: calc(90% - 32px);
  margin: 8px 16px;
  padding: 12px;
  border-radius: 18px 18px 18px 2px;
  background: ${props => props.theme?.token?.colorBgContainer};
  color: ${props => props.theme?.token?.colorText};
  align-self: flex-start;
  max-height: 200px;
  border: 1px solid ${props => props.theme?.token?.colorPrimary};
  box-shadow: 0 2px 8px rgba(38, 114, 190, 0.2);

  .thinking-header {
    font-size: 12px;
    color: ${props => props.theme?.token?.colorPrimary};
    margin-bottom: 0px;
    font-style: italic;
    font-weight: 600;
    position: sticky;
    top: 0;
    background: ${props => props.theme?.token?.colorBgContainer};
    z-index: 1;
    padding-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;

    &::before {
      content: '💭';
      font-size: 14px;
    }
  }

  .thinking-content {
    font-size: 14px;
    line-height: 1.4;
    white-space: pre-wrap;
    max-height: 150px;
    overflow-y: auto;

    /* Add subtle blue accent to bullet points */
    ul li::marker {
      color: ${props => props.theme?.token?.colorPrimary};
    }
  }

  ul {
    margin-left: -15px;
    margin-top: 5px;
  }
`;

const RuntimeStreamPanel = styled.div`
  width: calc(100% - 32px);
  max-width: 980px;
  margin: 10px 16px 14px;
  align-self: flex-start;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RuntimeStreamHeader = styled.div`
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#6b7280'};
  font-size: 13px;
  font-weight: 450;
  letter-spacing: 0;
  padding: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  opacity: 0.95;
  border-bottom: 1px solid ${props => props.theme?.token?.colorBorderSecondary ?? '#d8dce2'};
`;

const RuntimeStreamBubble = styled.div<{ $kind?: string }>`
  width: 100%;
  max-width: 100%;
  min-height: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' ? '22px' : 'auto'};
  padding: 0;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#64748b'};
  display: grid;
  grid-template-columns: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' || props.$kind === 'handoff' ? '22px minmax(0, 1fr)' : 'minmax(0, 1fr)'};
  gap: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' || props.$kind === 'handoff' ? '8px' : '0'};
  align-items: start;
  opacity: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' ? 0.78 : 1};
`;

const RuntimeStreamIcon = styled.span<{ $kind?: string }>`
  width: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' || props.$kind === 'handoff' ? '18px' : '0'};
  height: 22px;
  color: ${props => props.$kind === 'tool'
    ? (props.theme?.token?.colorTextTertiary ?? '#8b9098')
    : (props.theme?.token?.colorPrimary ?? '#2672be')};
  display: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' || props.$kind === 'handoff' ? 'inline-flex' : 'none'};
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 400;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  line-height: 1;
  transform: translateY(0);
`;

const RuntimeStreamText = styled.div<{ $kind?: string }>`
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
  color: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted'
    ? (props.theme?.token?.colorTextSecondary ?? '#8a8f98')
    : (props.theme?.token?.colorTextSecondary ?? '#555f6d')};
  font-size: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted'
    ? '12px'
    : '14px'};
  line-height: ${props => props.$kind === 'tool' || props.$kind === 'codex-muted' ? '1.35' : '1.45'};
  font-weight: ${props => props.$kind === 'thinking' ? 500 : 400};

  code {
    display: inline-block;
    padding: 0.02em 0.46em 0.08em;
    border-radius: 10px;
    background: #ececec;
    color: #1f2328;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.86em;
    line-height: 1.2;
    white-space: nowrap;
  }
`;

const RuntimeStreamMessage = styled.div`
  min-width: 0;
  color: inherit;
`;

const RuntimeStreamToolDetails = styled.details`
  width: 100%;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#64748b'};
  font-size: 12px;
  line-height: 1.35;

  &[open] {
    margin-bottom: 2px;
  }
`;

const RuntimeStreamToolSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  display: grid;
  grid-template-columns: 18px minmax(0, 1fr) 16px;
  gap: 8px;
  align-items: start;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#8a8f98'};
  opacity: 0.82;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '›';
    font-size: 16px;
    line-height: 1;
    transform: rotate(0deg);
    transition: transform 120ms ease;
    color: ${props => props.theme?.token?.colorTextTertiary ?? '#9aa1ad'};
  }

  ${RuntimeStreamToolDetails}[open] &::after {
    transform: rotate(90deg);
  }
`;

const RuntimeStreamToolBody = styled.div`
  margin: 6px 0 2px 26px;
  padding: 7px 9px;
  border-left: 1px solid ${props => props.theme?.token?.colorBorderSecondary ?? '#d8dce2'};
  color: ${props => props.theme?.token?.colorTextTertiary ?? '#7c8491'};
  background: ${props => props.theme?.token?.colorFillTertiary ?? '#f5f7fa'};
  border-radius: 6px;
  overflow-wrap: anywhere;
`;

const RuntimeCommandGroupDetails = styled.details`
  width: fit-content;
  max-width: 100%;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#64748b'};
  font-size: 13px;
  line-height: 1.35;
`;

const RuntimeCommandGroupSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  display: inline-grid;
  grid-template-columns: 16px auto 14px;
  align-items: center;
  gap: 8px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#4b5563'};
  white-space: nowrap;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '›';
    font-size: 16px;
    line-height: 1;
    color: ${props => props.theme?.token?.colorTextTertiary ?? '#9aa1ad'};
    transform: rotate(0deg);
    transition: transform 120ms ease;
  }

  ${RuntimeCommandGroupDetails}[open] &::after {
    transform: rotate(90deg);
  }
`;

const RuntimeCommandGroupIcon = styled.span`
  width: 13px;
  height: 13px;
  border: 1px solid ${props => props.theme?.token?.colorTextTertiary ?? '#9aa1ad'};
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme?.token?.colorTextTertiary ?? '#9aa1ad'};
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 8px;
  line-height: 1;
`;

const RuntimeCommandGroupRows = styled.div`
  margin: 7px 0 2px 21px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ArtifactImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 10px;
  margin-top: 10px;
`;

const ArtifactImagePreviewButton = styled.button`
  appearance: none;
  border: 1px solid #d7dee8;
  background: #ffffff;
  padding: 0;
  border-radius: 8px;
  overflow: hidden;
  cursor: zoom-in;
  text-align: left;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);

  &:hover {
    border-color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  }
`;

const ArtifactImagePreviewFrame = styled.div`
  position: relative;
  min-height: 280px;
  height: clamp(360px, 48vw, 760px);
  max-height: 760px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
`;

const ArtifactImagePreview = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f8fafc;
  transform-origin: center center;
  transition: transform 120ms ease;
`;

const ArtifactImageCaption = styled.div`
  padding: 8px 10px;
  color: ${props => props.theme?.token?.colorTextSecondary ?? '#5f6b7a'};
  font-size: 12px;
  font-weight: 650;
`;

const ArtifactImagePreviewControls = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  z-index: 2;
`;

const ArtifactImagePreviewControl = styled.button`
  appearance: none;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.94);
  color: #172033;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.12);

  &:hover {
    background: #ffffff;
    border-color: ${props => props.theme?.token?.colorPrimary ?? '#2672be'};
  }

  ion-icon {
    font-size: 18px;
  }
`;

const ArtifactImageModal = styled(IonModal)`
  --width: 100%;
  --height: 100dvh;
  --max-height: 100dvh;
  --border-radius: 0;

  &::part(content) {
    height: 100dvh;
    max-height: 100dvh;
    overflow: hidden;
  }

  .artifact-image-modal-content {
    --background: #0b1220;
    --overflow: hidden;
  }
`;

const ArtifactImageModalBody = styled.div`
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  padding-top: calc(env(safe-area-inset-top, 0px) + 12px);
  background: #0b1220;
  color: #ffffff;
`;

const ArtifactImageModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.16);

  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    align-items: start;
    gap: 10px;
    padding: 10px 12px;
  }
`;

const ArtifactImageModalTitle = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
`;

const ArtifactImageModalActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  min-width: 0;

  ion-button {
    --padding-start: 8px;
    --padding-end: 8px;
    min-width: 36px;
  }

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-end;
    gap: 4px;

    ion-button {
      --padding-start: 6px;
      --padding-end: 6px;
      min-width: 34px;
      height: 34px;
    }
  }
`;

const ArtifactImageModalCanvas = styled.div`
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const ArtifactImageModalImg = styled.img`
  display: block;
  max-width: calc(100vw - 36px);
  max-height: calc(100dvh - 116px);
  object-fit: contain;
  background: #ffffff;
  transform-origin: center center;
  transition: transform 120ms ease;
  pointer-events: none;
  user-select: none;

  @media (max-width: 640px) {
    max-width: calc(100vw - 20px);
    max-height: calc(100dvh - 150px);
  }
`;

const WorkOrderModal = styled(IonModal)`
  --width: min(1100px, 100%);
  --height: 100dvh;
  --max-height: 100dvh;
  --border-radius: 0;

  &::part(content) {
    height: 100dvh;
    max-height: 100dvh;
    overflow: hidden;
  }

  .work-order-modal-content {
    --background: #f5f7fb;
    --overflow: hidden;
    height: 100%;
  }
`;

const WorkOrderModalBody = styled.div`
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  padding-top: calc(env(safe-area-inset-top, 0px) + 12px);
  background: #f5f7fb;
  color: #111827;
  overflow: hidden;
`;

const WorkOrderModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #dce3ef;
  background: #ffffff;

  ${ArtifactImageModalActions} {
    width: auto;
    flex: 0 0 auto;
    justify-content: flex-end;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const WorkOrderModalTitle = styled.div`
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const WorkOrderModalTitleMain = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 15px;
  font-weight: 800;
`;

const WorkOrderModalSubtitle = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 550;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WorkOrderModalContent = styled.div`
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 14px;
  padding: 14px;
  overflow: hidden;

  @media (max-width: 860px) {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: calc(18px + env(safe-area-inset-bottom, 0px));
  }
`;

const WorkOrderPdfFrame = styled.iframe`
  width: 100%;
  min-height: 0;
  height: 100%;
  border: 1px solid #dce3ef;
  border-radius: 8px;
  background: #ffffff;

  @media (max-width: 860px) {
    height: clamp(220px, 34vh, 360px);
    min-height: 220px;
    flex: 0 0 auto;
  }
`;

const WorkOrderSignPanel = styled.div`
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #dce3ef;
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;

  @media (max-width: 860px) {
    flex: 0 0 auto;
    height: auto;
    min-height: auto;
    overflow: visible;
  }
`;

const JobsModal = styled(IonModal)`
  --width: min(920px, 100%);
  --height: 100dvh;
  --max-height: 100dvh;
  --border-radius: 0;

  &::part(content) {
    height: 100dvh;
    max-height: 100dvh;
    overflow: hidden;
  }

  .jobs-modal-content {
    --background: #f4f7fb;
    --overflow: hidden;
    height: 100%;
  }
`;

const JobsModalBody = styled.div`
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  padding-top: calc(env(safe-area-inset-top, 0px) + 12px);
  color: #111827;
  background: #f4f7fb;
`;

const JobsModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #dce3ef;
  background: #ffffff;

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const JobsModalHeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const JobsModalTitle = styled.div`
  font-size: 20px;
  font-weight: 850;
  letter-spacing: 0;
`;

const JobsModalSubtitle = styled.div`
  margin-top: 3px;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: normal;
  word-break: normal;
`;

const JobsModalCloseActions = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  ion-button {
    --padding-start: 6px;
    --padding-end: 6px;
    min-width: 34px;
    height: 34px;
  }
`;

const ModalHeaderCopy = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const JobsModalContent = styled.div`
  min-height: 0;
  display: block;
  padding: 14px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const JobsBrowser = styled.div`
  max-width: 720px;
  margin: 0 auto;
  display: grid;
  gap: 12px;
`;

const JobSearchRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  align-items: center;
`;

const JobSearchBox = styled.label`
  min-height: 38px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d9e2ef;
  border-radius: 8px;
  background: #ffffff;
  padding: 0 10px;
  color: #7b8797;

  ion-icon {
    font-size: 16px;
  }

  input {
    width: 100%;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    color: #111827;
    font-size: 14px;
    font-weight: 600;
  }

  input::placeholder {
    color: #94a3b8;
  }
`;

const JobFilterButton = styled.button`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #d9e2ef;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 750;
`;

const JobRows = styled.div`
  display: grid;
  gap: 8px;
`;

const JobRow = styled.button`
  width: 100%;
  min-height: 72px;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  border: 1px solid #dfe7f2;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.05);
`;

const JobRowIcon = styled.span`
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eef4ff;
  color: #1d6ff2;

  ion-icon {
    font-size: 18px;
  }
`;

const JobRowMain = styled.span`
  min-width: 0;
  display: grid;
  gap: 3px;
`;

const JobRowTitle = styled.span`
  color: #111827;
  font-size: 14px;
  font-weight: 850;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const JobRowMeta = styled.span`
  color: #64748b;
  font-size: 12px;
  font-weight: 650;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const JobRowUpdated = styled.span`
  color: #94a3b8;
  font-size: 11px;
  font-weight: 650;
`;

const JobRowSide = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const JobStatusPill = styled.span<{ $tone?: 'blue' | 'green' | 'yellow' | 'gray' }>`
  border-radius: 999px;
  background: ${props => {
    if (props.$tone === 'green') return '#e8f6ee';
    if (props.$tone === 'yellow') return '#fff6d9';
    if (props.$tone === 'gray') return '#eef2f7';
    return '#eef4ff';
  }};
  color: ${props => {
    if (props.$tone === 'green') return '#17633a';
    if (props.$tone === 'yellow') return '#8a5b00';
    if (props.$tone === 'gray') return '#475569';
    return '#1d4ed8';
  }};
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 850;
  white-space: nowrap;
`;

const JobDetailBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: start;
  border: none;
  background: transparent;
  color: #1d4ed8;
  padding: 0;
  font-size: 13px;
  font-weight: 850;
  cursor: pointer;

  ion-icon {
    font-size: 17px;
  }
`;

const JobsList = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
`;

const JobCard = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: 1px solid ${props => props.$active ? '#1d6ff2' : '#d7e0ec'};
  border-radius: 8px;
  background: #ffffff;
  padding: 12px;
  text-align: left;
  box-shadow: ${props => props.$active ? '0 8px 20px rgba(29, 111, 242, 0.12)' : '0 4px 12px rgba(15, 23, 42, 0.05)'};
  cursor: pointer;
`;

const JobCardTitle = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #111827;
`;

const JobCardSubtitle = styled.div`
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
`;

const JobBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const JobBadge = styled.span<{ $tone?: 'blue' | 'green' | 'gray' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: ${props => props.$tone === 'green' ? '#e8f6ee' : props.$tone === 'gray' ? '#eef2f7' : '#eef4ff'};
  color: ${props => props.$tone === 'green' ? '#17633a' : props.$tone === 'gray' ? '#475569' : '#1d4ed8'};
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;

  ion-icon {
    font-size: 14px;
  }
`;

const JobDetail = styled.div`
  min-height: 0;
  overflow-y: auto;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  padding: 0;
`;

const JobDetailHero = styled.div`
  display: grid;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #e5edf7;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
`;

const JobDetailTitle = styled.div`
  font-size: 22px;
  font-weight: 850;
  color: #0f172a;
  letter-spacing: 0;
`;

const JobDetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  color: #64748b;
  font-size: 13px;
  font-weight: 650;
`;

const JobDetailActions = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  padding: 12px 16px 0;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const JobActionButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 40px;
  border: 1px solid ${props => props.$primary ? '#1d6ff2' : '#bdd3f2'};
  border-radius: 8px;
  background: ${props => props.$primary ? '#1d6ff2' : '#ffffff'};
  color: ${props => props.$primary ? '#ffffff' : '#1d4ed8'};
  padding: 0 12px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: ${props => props.$primary ? '0 8px 18px rgba(29, 111, 242, 0.18)' : 'none'};

  ion-icon {
    font-size: 18px;
  }
`;

const JobSection = styled.section`
  margin: 16px;
`;

const JobSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 850;
  margin-bottom: 10px;

  ion-icon {
    color: #1d6ff2;
    font-size: 18px;
  }
`;

const JobStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const JobStat = styled.div`
  border: 1px solid #e1e9f4;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
`;

const JobStatValue = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #0f172a;
  font-size: 17px;
  font-weight: 850;

  ion-icon {
    color: #1d6ff2;
    font-size: 18px;
  }
`;

const JobStatLabel = styled.div`
  margin-top: 3px;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
`;

const JobPhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
`;

const JobPhotoThumb = styled.button`
  width: 100%;
  height: 100%;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  overflow: hidden;
  padding: 0;
  background: #f8fafc;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`;

const JobPhotoTile = styled.div`
  position: relative;
  aspect-ratio: 1;
  min-width: 0;
`;

const JobPhotoRemoveButton = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  color: #dc2626;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.18);
  cursor: pointer;

  ion-icon {
    font-size: 16px;
  }

  &:disabled {
    cursor: not-allowed;
    color: #94a3b8;
  }
`;

const JobTimelineRow = styled.button<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  border: 1px solid #e5edf7;
  border-radius: 8px;
  background: #ffffff;
  padding: 10px;
  color: #334155;
  font-size: 13px;
  font-weight: 650;
  text-align: left;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  span:last-child {
    text-align: right;
  }

  &:hover {
    border-color: ${({ $clickable }) => ($clickable ? '#b8cdf5' : '#e5edf7')};
    background: ${({ $clickable }) => ($clickable ? '#f8fbff' : '#ffffff')};
  }

  &:disabled {
    cursor: default;
  }
`;

const EmptyJobState = styled.div`
  border: 1px dashed #c8d5e6;
  border-radius: 8px;
  background: #f8fafc;
  color: #64748b;
  padding: 12px;
  font-size: 13px;
  line-height: 1.45;
`;

const JobsLoadingState = styled.div`
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  padding: 12px;
  font-size: 13px;
  font-weight: 700;
`;

const WorkOrderSignHeading = styled.div`
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 4px;
`;

const WorkOrderSignHelp = styled.div`
  color: #64748b;
  font-size: 12px;
  line-height: 1.35;
  margin-bottom: 12px;
`;

const SignaturePadWrap = styled.div`
  margin: 12px 0;
  min-width: 0;
`;

const SignaturePadLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 750;
  color: #334155;
  margin-bottom: 6px;
`;

const SignatureCanvas = styled.canvas`
  width: 100%;
  height: 132px;
  box-sizing: border-box;
  display: block;
  border: 1px dashed #aeb9c9;
  border-radius: 8px;
  background: #ffffff;
  touch-action: none;
`;

const SignatureClearButton = styled.button`
  border: 0;
  background: transparent;
  color: #2563eb;
  font-size: 12px;
  font-weight: 750;
  cursor: pointer;
`;

const WorkOrderFieldLabel = styled.label`
  display: block;
  margin: 12px 0 6px;
  color: #334155;
  font-size: 12px;
  font-weight: 750;
`;

const WorkOrderInput = styled.input`
  width: 100%;
  border: 1px solid #cfd8e6;
  border-radius: 8px;
  padding: 9px 10px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`;

const WorkOrderResults = styled.div`
  display: grid;
  gap: 12px;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  font-size: 16px;
  line-height: 1.35;

  @media (max-width: 520px) {
    gap: 10px;
    font-size: 14px;
  }
`;

const WorkOrderResultCard = styled(IonCard)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: grid;
  gap: 18px;
  margin: 0;
  padding: 24px clamp(18px, 4vw, 30px);
  border: 1px solid rgba(213, 223, 237, 0.82);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(250, 252, 255, 0.96));
  box-shadow:
    0 18px 44px rgba(15, 23, 42, 0.08),
    0 2px 10px rgba(15, 23, 42, 0.05);

  @media (max-width: 520px) {
    gap: 12px;
    padding: 16px 14px;
    border-radius: 16px;
  }
`;

const WorkOrderCardHeader = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  min-width: 0;

  @media (max-width: 520px) {
    gap: 10px;
  }
`;

const WorkOrderDocumentBadge = styled.div`
  position: relative;
  width: 74px;
  height: 74px;
  flex: 0 0 74px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef5ff, #e9f1ff 52%, #ffffff);
  color: #1672f3;
  box-shadow: inset 0 0 0 1px rgba(22, 114, 243, 0.06);

  ion-icon {
    font-size: 38px;
    filter: drop-shadow(0 8px 12px rgba(22, 114, 243, 0.12));
  }

  &::after {
    content: '';
    position: absolute;
    right: -6px;
    bottom: -6px;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: #1473f8;
    border: 4px solid #ffffff;
    box-shadow: 0 10px 18px rgba(20, 115, 248, 0.24);
  }

  &::before {
    content: '✓';
    position: absolute;
    right: 3px;
    bottom: -1px;
    z-index: 1;
    color: #ffffff;
    font-size: 16px;
    font-weight: 900;
  }

  @media (max-width: 520px) {
    width: 52px;
    height: 52px;
    flex-basis: 52px;
    border-radius: 12px;

    ion-icon {
      font-size: 29px;
    }

    &::after {
      right: -5px;
      bottom: -5px;
      width: 22px;
      height: 22px;
      border-width: 3px;
    }

    &::before {
      right: 1px;
      bottom: -2px;
      font-size: 13px;
    }
  }
`;

const WorkOrderResultBody = styled.div`
  min-width: 0;
  display: grid;
  gap: 8px;

  @media (max-width: 520px) {
    gap: 6px;
  }
`;

const WorkOrderResultTitle = styled.div`
  color: #071225;
  font-size: 28px;
  font-weight: 900;
  line-height: 1.15;
  overflow-wrap: anywhere;
  letter-spacing: 0;

  @media (max-width: 520px) {
    font-size: 20px;
    line-height: 1.14;
  }
`;

const WorkOrderResultMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.2;

  ion-icon {
    font-size: 17px;
    color: #64748b;
  }
`;

const WorkOrderStatusPill = styled.span<{ $signed: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 8px 12px;
  background: ${props => props.$signed ? '#e8f6eb' : '#edf5ff'};
  color: ${props => props.$signed ? '#1f7a3f' : '#1267ea'};
  font-size: 14px;
  font-weight: 850;

  ion-icon {
    color: ${props => props.$signed ? '#1f8d46' : '#1267ea'};
  }

  @media (max-width: 520px) {
    gap: 5px;
    padding: 6px 9px;
    font-size: 12px;

    ion-icon {
      font-size: 15px;
    }
  }
`;

const WorkOrderDetailGrid = styled.div`
  display: grid;
  border-top: 1px solid #dfe6ef;
`;

const WorkOrderResultDetail = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) minmax(0, 1.1fr);
  gap: 10px;
  align-items: center;
  padding: 11px 0;
  border-bottom: 1px solid #e6edf5;
  color: #273142;
  font-size: 16px;
  line-height: 1.32;

  ion-icon {
    color: #1267ea;
    font-size: 25px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 34px minmax(82px, 0.8fr) minmax(0, 1.2fr);
    gap: 8px;
    padding: 8px 0;
    font-size: 13px;

    ion-icon {
      font-size: 18px;
    }
  }
`;

const WorkOrderDetailIcon = styled.span`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eef5ff;

  @media (max-width: 520px) {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }
`;

const WorkOrderDetailLabel = styled.span`
  color: #273142;
  font-weight: 650;
`;

const WorkOrderDetailValue = styled.span`
  min-width: 0;
  color: #1f2937;
  font-weight: 650;
  text-align: right;
  overflow-wrap: anywhere;

  @media (max-width: 520px) {
    grid-column: auto;
    text-align: right;
    color: #536072;
    font-weight: 600;
  }
`;

const WorkOrderResultActions = styled.div`
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;

  @media (max-width: 520px) {
    gap: 8px;
  }
`;

const WorkOrderResultButton = styled(IonButton)<{ $variant?: 'primary' | 'secondary' }>`
  min-width: 0;
  min-height: 42px;
  width: 100%;
  --min-height: 42px;
  --border-radius: 10px;
  --border-width: 1px;
  --border-style: solid;
  --border-color: ${props => props.$variant === 'secondary' ? '#c7d9f4' : '#1267ea'};
  --background: ${props => props.$variant === 'secondary' ? '#ffffff' : '#1267ea'};
  --background-hover: ${props => props.$variant === 'secondary' ? '#f8fbff' : '#0f5bd0'};
  --background-activated: ${props => props.$variant === 'secondary' ? '#eef6ff' : '#0f5bd0'};
  --color: ${props => props.$variant === 'secondary' ? '#1267ea' : '#ffffff'};
  --box-shadow: ${props => props.$variant === 'secondary' ? 'none' : '0 8px 18px rgba(18, 103, 234, 0.2)'};
  margin: 0;
  font-size: 14px;
  font-weight: 900;
  text-transform: none;
  white-space: nowrap;

  &::part(native) {
    gap: 8px;
  }

  ion-icon {
    flex: 0 0 auto;
    font-size: 18px;
  }

  @media (max-width: 520px) {
    min-height: 36px;
    --min-height: 36px;
    --border-radius: 9px;

    ion-icon {
      font-size: 15px;
    }
  }
`;

const CompactWorkOrderList = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 10px;
`;

const CompactWorkOrderRow = styled.button`
  width: 100%;
  min-width: 0;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid #dbe6f4;
  border-radius: 10px;
  background: #ffffff;
  color: #071225;
  text-align: left;
  cursor: pointer;

  &:active {
    transform: translateY(1px);
  }

  ion-icon {
    flex: 0 0 auto;
  }
`;

const CompactWorkOrderIcon = styled.span`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eef5ff;
  color: #1672f3;

  ion-icon {
    font-size: 25px;
  }
`;

const CompactWorkOrderMain = styled.span`
  min-width: 0;
  display: grid;
  gap: 5px;
`;

const CompactWorkOrderTitle = styled.span`
  min-width: 0;
  color: #071225;
  font-size: 16px;
  font-weight: 850;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CompactWorkOrderMeta = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const CompactWorkOrderDate = styled.span`
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
`;

const CompactWorkOrderChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;

  ion-icon {
    font-size: 20px;
  }
`;

const WorkOrderStatus = styled.div<{ $error?: boolean }>`
  margin-top: 10px;
  color: ${props => props.$error ? '#b42318' : '#236f52'};
  font-size: 12px;
  font-weight: 650;
  line-height: 1.35;
`;

const WorkOrderActionStack = styled.div`
  display: grid;
  gap: 8px;
  margin-top: 12px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
`;

const HeaderActionButton = styled(IonButton)`
  --color: ${props => props.theme?.token?.colorIcon};
  --background: transparent;
  --background-hover: rgba(0, 0, 0, 0.05);
  --background-activated: rgba(0, 0, 0, 0.1);
  --padding-start: 8px;
  --padding-end: 8px;
  margin: 0;
  height: 40px;
  width: 40px;

  ion-icon {
    color: ${props => props.theme?.token?.colorIcon};
    font-size: 22px;
  }
`;


const TimeIndicator = styled.div`
  font-size: 12px;
  opacity: 0.7;
  color: ${props => props.theme?.token?.colorTextSecondary};
  font-style: italic;
`;

const EscalationSeparator = styled.div`
  text-align: center;
  padding: 0;
  margin: 0;
  background: ${props => props.theme?.token?.colorBgContainer};
  border-radius: 8px;
  color: ${props => props.theme?.token?.colorTextSecondary};
  font-size: 14px;
  font-style: italic;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

type MessageSender = 'user' | 'assistant' | 'agent';

interface ReferenceDocumentItem {
  managed_document_id?: string | null;
  filename: string;
  manufacturer?: string | null;
  openai_file_id?: string | null;
  storage_bucket?: string | null;
  storage_object_key?: string | null;
  url?: string | null;
  page_image_url?: string | null;
  citation_context?: string | null;
  page_hint?: string | null;
  manual_type?: string | null;
  category?: string | null;
  subcategory?: string | null;
  family?: string | null;
  match_type?: string | null;
  relevance_score?: number | string | null;
  source_rank?: number | string | null;
  unavailable_reason?: string | null;
  delivery_type?: string | null;
}

interface AgentArtifactChoiceItem {
  choice_number?: number;
  display_label?: string;
  choice_id?: string;
  label?: string;
  description?: string | null;
}

interface AgentArtifactItem {
  artifact_id?: string | null;
  id?: string | null;
  kind?: string | null;
  title?: string | null;
  filename?: string | null;
  url?: string | null;
  render_url?: string | null;
  content_type?: string | null;
  status?: string;
  draft_id?: string;
  can_commit?: boolean;
  confirmation_text?: string;
  expires_at?: string | null;
  job?: Record<string, any>;
  location?: Record<string, any>;
  equipment_draft?: Record<string, any>;
  duplicate_check?: ServiceTitanEquipmentReviewArtifact['duplicate_check'];
  source_photos?: ServiceTitanEquipmentReviewArtifact['source_photos'];
  equipment?: Record<string, any>;
  commit_result?: Record<string, any>;
  read_back_verified?: boolean;
  prompt?: string;
  manufacturer_slug?: string;
  selection_token?: string;
  selection_type?: ServiceTitanChoicePromptArtifact['selection_type'] | PartsModelChoicePromptArtifact['selection_type'];
  choices?: AgentArtifactChoiceItem[];
}

interface MessageDisplay {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: string;
  sender: MessageSender;
  has_image?: boolean;
  image_url?: string | null;
  image_filename?: string | null;
  image_mime_type?: string | null;
  image_size_bytes?: number | null;
  feedback_eligible?: boolean;
  feedback?: MessageFeedback | null;
  reference_documents?: ReferenceDocumentItem[];
  artifacts?: AgentArtifactItem[];
  adaptive_trace?: AdaptiveTraceStep[];
}

const isServiceTitanChoicePromptArtifact = (
  artifact: AgentArtifactItem,
): artifact is AgentArtifactItem & ServiceTitanChoicePromptArtifact => (
  artifact.kind === 'servicetitan_choice_prompt'
  && typeof artifact.title === 'string'
  && typeof artifact.prompt === 'string'
  && typeof artifact.selection_token === 'string'
  && artifact.selection_token.length > 0
  && Array.isArray(artifact.choices)
  && artifact.choices.length > 0
  && artifact.choices.every(choice => (
    Number.isInteger(choice.choice_number)
    && choice.choice_number > 0
    && typeof choice.display_label === 'string'
    && choice.display_label.trim().length > 0
  ))
);

const isPartsModelChoicePromptArtifact = (
  artifact: AgentArtifactItem,
): artifact is AgentArtifactItem & PartsModelChoicePromptArtifact => (
  artifact.kind === 'parts_model_choice_prompt'
  && artifact.selection_type === 'parts_model'
  && typeof artifact.manufacturer_slug === 'string'
  && artifact.manufacturer_slug.trim().length > 0
  && typeof artifact.title === 'string'
  && artifact.title.trim().length > 0
  && typeof artifact.prompt === 'string'
  && artifact.prompt.trim().length > 0
  && Array.isArray(artifact.choices)
  && artifact.choices.length > 0
  && artifact.choices.every(choice => (
    typeof choice.choice_id === 'string'
    && choice.choice_id.trim().length > 0
    && typeof choice.label === 'string'
    && choice.label.trim().length > 0
    && choice.choice_id.trim() === choice.label.trim()
  ))
);

const EVIDENCE_TRAY_REQUEST_PATTERN =
  /\b(?:sources?|citations?|cites?|evidence|references?|documents\s+used|source\s+cards?|evidence\s+tray|where\s+(?:did|does)\s+(?:this|that|the\s+answer|it)\s+(?:come\s+from|come\s+out\s+of|get\s+pulled\s+from)|what\s+documents?)\b/i;

export const getRenderedPageReferences = (references?: ReferenceDocumentItem[]): ReferenceDocumentItem[] => {
  return (references || []).filter(reference => String(reference.page_image_url || '').trim().length > 0);
};

const getGeneratedImageArtifacts = (artifacts?: AgentArtifactItem[]): AgentArtifactItem[] => {
  return (artifacts || []).filter((artifact) => {
    const kind = String(artifact.kind || '').toLowerCase();
    const contentType = String(artifact.content_type || '').toLowerCase();
    const url = String(artifact.render_url || artifact.url || '').trim();
    return Boolean(url) && (kind === 'generated_image' || contentType.startsWith('image/'));
  });
};

export const getCitationOnlyReferences = (references?: ReferenceDocumentItem[]): ReferenceDocumentItem[] => {
  return (references || []).filter(reference => String(reference.page_image_url || '').trim().length === 0);
};

export const shouldShowEvidenceTray = (
  assistantMessage?: string | null,
  previousUserMessage?: string | null,
): boolean => {
  return EVIDENCE_TRAY_REQUEST_PATTERN.test(`${previousUserMessage || ''}\n${assistantMessage || ''}`);
};

export const shouldShowReferenceDocumentsTray = (
  references?: ReferenceDocumentItem[],
  assistantMessage?: string | null,
  previousUserMessage?: string | null,
): boolean => {
  if (shouldShowEvidenceTray(assistantMessage, previousUserMessage)) {
    return true;
  }

  const pageReferences = getRenderedPageReferences(references);
  const citationOnlyReferences = getCitationOnlyReferences(references);
  return pageReferences.length === 0 && citationOnlyReferences.length > 0;
};

interface AdaptiveTraceStep {
  label: string;
  action?: string;
  status?: string;
  quality?: string;
  quality_label?: string;
  scope_label?: string;
  expectation?: string;
  observation?: string;
  evaluation?: string;
  prediction_error?: string;
  next_action?: string;
  next_step?: string;
}

// New interface for escalation metadata
interface EscalationEvent {
  id: string;
  type: 'start' | 'connect' | 'resolve';
  timestamp: string;
  agentName?: string;
  escalationId?: string | null;
}

interface QueuedDraft {
  text: string;
  image: SelectedImage | null;
  queuedAt: string;
  serverQueued?: boolean;
}

interface AssistantMessageSubmissionOptions {
  serviceTitanSelection?: ServiceTitanSelection;
  preserveComposer?: boolean;
  choiceSubmission?: boolean;
}

interface ArtifactImagePreviewItem {
  id: string;
  label: string;
  originalUrl: string;
  bucket?: string;
  objectKey?: string;
  displayUrl?: string;
  error?: boolean;
}

interface WorkOrderPreviewItem {
  url: string;
  title: string;
}

interface WorkOrderResultItem {
  index: number;
  number: string;
  date?: string;
  signed: boolean;
  customer?: string;
  address?: string;
  summary?: string;
  scheduled?: string;
  part?: string;
  technician?: string;
  serviceType?: string;
  status?: string;
  url: string;
  actionLabel: string;
}

type JobPanelWorkOrder = WorkOrderResultItem;

interface JobPanelPhoto {
  id: string;
  url: string;
  filename?: string | null;
  timestamp?: string | null;
}

interface JobPanelConversation {
  id: string;
  sessionId?: string;
  title: string;
  subtitle: string;
}

interface JobPanelJob {
  id: string;
  name: string;
  subtitle: string;
  updatedLabel: string;
  photos: JobPanelPhoto[];
  workOrders: JobPanelWorkOrder[];
  conversations: JobPanelConversation[];
  conversationCount: number;
  workOrderCount: number;
  status: 'unassigned' | 'saved';
  agentJobId?: string;
}

interface SavedAgentJob {
  id: string;
  name: string;
  customer_name?: string;
  service_address?: string;
  work_order_number?: string;
  notes?: string[];
  created_at?: string;
  updated_at?: string;
  is_active?: boolean;
  sessions?: Array<{
    id?: string;
    session_id?: string;
    title?: string;
    summary?: string;
    conversation_summary?: string;
    conversation_excerpt?: string;
    created_at?: string;
    updated_at?: string;
  }>;
  work_orders?: Array<{
    id?: string;
    number?: string;
    title?: string;
    filename?: string;
    date?: string;
    created_at?: string;
    signed?: boolean;
    customer?: string;
    customer_name?: string;
    address?: string;
    service_address?: string;
    summary?: string;
    issue_summary?: string;
    scheduled_at?: string;
    completed_at?: string;
    job_description?: string;
    technician?: string;
    service_type?: string;
    parts?: string[];
    services?: string[];
    url?: string;
    render_url?: string;
  }>;
  photos?: Array<{
    id: string;
    url: string;
    filename?: string | null;
    created_at?: string | null;
  }>;
}

const slugifyRuntimeSegment = (value: string | null | undefined, fallback: string): string => {
  const cleaned = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || fallback;
};

const fileFromSelectedImage = async (image: SelectedImage): Promise<File> => {
  const response = await fetch(image.dataUrl);
  const blob = await response.blob();
  return new File([blob], image.name || 'job-photo.jpg', {
    type: image.type || blob.type || 'image/jpeg',
  });
};

const isWorkOrderPdfUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.href);
    return /^\/api\/work-orders\/\d+\/pdf$/i.test(parsed.pathname)
      || parsed.pathname.endsWith('/api/work-orders/pdf')
      || (parsed.hostname === 'api.work-orders' && parsed.pathname.endsWith('/pdf'));
  } catch (_error) {
    return false;
  }
};

const cleanWorkOrderArtifactId = (value: string | null): string | null => {
  const match = String(value || '').toLowerCase().match(/[a-f0-9]{16}/);
  return match?.[0] || null;
};

const workOrderIdFromUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url, window.location.href);
    const match = parsed.pathname.match(/^\/api\/work-orders\/(\d+)\/pdf$/i);
    return match?.[1] || null;
  } catch (_error) {
    return null;
  }
};

const getWorkOrderArtifactBaseUrl = (): string => {
  return BACKEND_BASE_URL.replace(/\/$/, '') || window.location.origin;
};

const getAgentArtifactBackendBaseUrl = (): string => BACKEND_BASE_URL.replace(/\/$/, '');

const normalizeWorkOrderPdfUrl = (url: string): string | null => {
  try {
    const parsed = new URL(url, window.location.href);
    const stableMatch = parsed.pathname.match(/^\/api\/work-orders\/(\d+)\/pdf$/i);
    if (stableMatch) {
      if (!parsed.searchParams.get('version')) {
        parsed.searchParams.set('version', 'current');
      }
      const query = parsed.searchParams.toString();
      if (parsed.origin && parsed.origin !== window.location.origin) {
        return `${parsed.origin}${parsed.pathname}?${query}`;
      }
      return `${getWorkOrderArtifactBaseUrl()}${parsed.pathname}?${query}`;
    }
    const artifactId = cleanWorkOrderArtifactId(parsed.searchParams.get('artifact_id'));
    if (!artifactId) {
      return null;
    }
    parsed.searchParams.set('artifact_id', artifactId);
    const query = parsed.searchParams.toString();
    if (parsed.hostname === 'api.work-orders' && parsed.pathname.endsWith('/pdf')) {
      return `${getWorkOrderArtifactBaseUrl()}/api/work-orders/pdf?${query}`;
    }
    if (parsed.pathname.endsWith('/api/work-orders/pdf')) {
      if (parsed.origin && parsed.origin !== window.location.origin) {
        return `${parsed.origin}${parsed.pathname}?${query}`;
      }
      return `${getWorkOrderArtifactBaseUrl()}${parsed.pathname}?${query}`;
    }
  } catch (_error) {
    return null;
  }
  return null;
};

const addCacheBustToUrl = (url: string): string => {
  try {
    const parsed = new URL(url, window.location.href);
    parsed.searchParams.set('_ts', `${Date.now()}`);
    return parsed.toString();
  } catch (_error) {
    return url;
  }
};

const WORK_ORDER_SIGNED_STATE_STORAGE_KEY = 'smartdocs.workOrderSignedState.v1';

interface WorkOrderSignedStateRecord {
  signedUrl: string;
  signedAt?: string;
  emailedAt?: string;
}

const workOrderStateKeyFromUrl = (url: string): string | null => {
  const normalized = normalizeWorkOrderPdfUrl(url);
  if (!normalized) {
    return null;
  }
  try {
    const parsed = new URL(normalized);
    const stableId = workOrderIdFromUrl(normalized);
    if (stableId) {
      return `work-order:${stableId}`;
    }
    const tenantId = parsed.searchParams.get('tenant_id') || 'local';
    const userId = parsed.searchParams.get('user_id') || 'web';
    const sessionId = parsed.searchParams.get('session_id') || 'web';
    const artifactId = cleanWorkOrderArtifactId(parsed.searchParams.get('artifact_id'));
    if (!artifactId) {
      return null;
    }
    return `${tenantId}:${userId}:${sessionId}:${artifactId}`;
  } catch (_error) {
    return null;
  }
};

const readWorkOrderSignedState = (url: string): WorkOrderSignedStateRecord | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const key = workOrderStateKeyFromUrl(url);
  if (!key) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(WORK_ORDER_SIGNED_STATE_STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : {};
    const record = state?.[key];
    const signedUrl = normalizeWorkOrderPdfUrl(record?.signedUrl || '');
    return signedUrl ? { ...record, signedUrl } : null;
  } catch (_error) {
    return null;
  }
};

const writeWorkOrderSignedState = (sourceUrl: string, signedUrl: string, updates: Partial<WorkOrderSignedStateRecord> = {}) => {
  if (typeof window === 'undefined') {
    return;
  }
  const key = workOrderStateKeyFromUrl(sourceUrl);
  const normalizedSignedUrl = normalizeWorkOrderPdfUrl(signedUrl);
  if (!key || !normalizedSignedUrl) {
    return;
  }
  try {
    const raw = window.localStorage.getItem(WORK_ORDER_SIGNED_STATE_STORAGE_KEY);
    const state = raw ? JSON.parse(raw) : {};
    state[key] = {
      ...(state?.[key] || {}),
      ...updates,
      signedUrl: normalizedSignedUrl,
    };
    window.localStorage.setItem(WORK_ORDER_SIGNED_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (_error) {
    // Local signed-state memory is a convenience; server headers remain source of truth.
  }
};

const normalizeAgentArtifactUrl = (url: string): string => {
  try {
    const normalizedOemImageUrl = normalizeOemPdfPageImageUrl(url, BACKEND_BASE_URL);
    if (normalizedOemImageUrl) {
      return normalizedOemImageUrl;
    }

    const normalizedBackendArtifactUrl = normalizeBackendAgentArtifactUrl(url, getAgentArtifactBackendBaseUrl());
    if (normalizedBackendArtifactUrl) {
      return normalizedBackendArtifactUrl;
    }
    const parsed = new URL(url, window.location.href);
    return parsed.toString();
  } catch (_error) {
    return url;
  }
};

const getOemPdfPageImageUrl = (url: string): string | null => {
  return normalizeOemPdfPageImageUrl(url, BACKEND_BASE_URL);
};

const WORK_ORDER_URL_PATTERN = String.raw`(?:api\.work-orders\/pdf|\/api\/work-orders\/pdf|\/api\/work-orders\/\d+\/pdf)`;
const WORK_ORDER_RAW_URL_REGEX = new RegExp(
  String.raw`((?:https?:\/\/api\.work-orders\/pdf\?[^\s<>)"']+)|(?:(?:https?:\/\/[^/\s<>)"']+)?\/api\/work-orders\/pdf\?[^\s<>)"']+)|(?:(?:https?:\/\/[^/\s<>)"']+)?\/api\/work-orders\/\d+\/pdf(?:\?[^\s<>)"']+)?))`,
  'gi',
);
const WORK_ORDER_MARKDOWN_LINK_REGEX = new RegExp(String.raw`\[([^\]]+)\]\(([^)]*${WORK_ORDER_URL_PATTERN}[^)]*)\)`, 'gi');
const WORK_ORDER_MARKDOWN_URL_REGEX = new RegExp(String.raw`\[[^\]]+\]\(([^)]*${WORK_ORDER_URL_PATTERN}[^)]*)\)`, 'gi');
const WORK_ORDER_HTML_URL_REGEX = new RegExp(String.raw`href=["']([^"']*${WORK_ORDER_URL_PATTERN}[^"']*)["']`, 'gi');
const WORK_ORDER_LIST_ITEM_PATTERN = /(?:^|\n)\s*(?:(?:\d+[.)])|-)\s+WO-/i;

const decodeBasicHtmlEntities = (input: string): string => {
  if (!input) {
    return '';
  }
  if (typeof document === 'undefined') {
    return input
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&nbsp;/g, ' ');
  }
  const textarea = document.createElement('textarea');
  textarea.innerHTML = input;
  return textarea.value;
};

const stripWorkOrderLinks = (value: string): string => (
  String(value || '')
    .replace(WORK_ORDER_MARKDOWN_URL_REGEX, '')
    .replace(WORK_ORDER_HTML_URL_REGEX, '')
    .replace(WORK_ORDER_RAW_URL_REGEX, '')
    .replace(/\bPDF:\s*/gi, '')
    .trim()
);

const normalizeWorkOrderParseInput = (text: string): string => (
  decodeBasicHtmlEntities(String(text || ''))
    .replace(/<a\b[^>]*href=["']([^"']*(?:api\.work-orders\/pdf|\/api\/work-orders\/pdf|\/api\/work-orders\/\d+\/pdf)[^"']*)["'][^>]*>([\s\S]*?)<\/a>/gi, (_match, href, label) => {
      const cleanLabel = String(label || 'Open work order').replace(/<[^>]+>/g, '').trim() || 'Open work order';
      return `[${cleanLabel}](${href})`;
    })
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\u00a0/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
);

const normalizeWorkOrderSummary = (value: string): string => (
  stripWorkOrderLinks(value)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/\s*\|\s*(?:signed|unsigned)\s*$/i, '')
    .trim()
);

const splitCustomerAddress = (value: string): { customer?: string; address?: string } => {
  const cleaned = normalizeWorkOrderSummary(value);
  if (!cleaned) {
    return {};
  }
  const parts = cleaned.split(/\s+(?:—|–|-)\s+/);
  if (parts.length <= 1) {
    return { customer: cleaned };
  }
  return {
    customer: parts[0]?.trim() || undefined,
    address: parts.slice(1).join(' - ').trim() || undefined,
  };
};

const extractWorkOrderIntro = (text: string): string => {
  const raw = normalizeWorkOrderParseInput(text);
  const firstItemIndex = raw.search(WORK_ORDER_LIST_ITEM_PATTERN);
  if (firstItemIndex <= 0) {
    return '';
  }
  return raw.slice(0, firstItemIndex).trim();
};

const parseWorkOrderResults = (text: string): WorkOrderResultItem[] => {
  const raw = normalizeWorkOrderParseInput(text);
  if (!/(?:\/api\/work-orders\/pdf|api\.work-orders\/pdf|\/api\/work-orders\/\d+\/pdf)/i.test(raw) || !WORK_ORDER_LIST_ITEM_PATTERN.test(raw)) {
    return [];
  }

  const itemRegex = /(?:^|\n)\s*(?:(\d+)[.)]|-)\s+(WO-[A-Z0-9-]+)[\s\S]*?(?=(?:^|\n)\s*(?:(?:\d+[.)])|-)\s+WO-|(?:^|\n)\s*(?:Want me to|What do you want|Options:|Next steps)\b|$)/gmi;
  const items: WorkOrderResultItem[] = [];
  let itemMatch: RegExpExecArray | null;

  while ((itemMatch = itemRegex.exec(raw)) !== null) {
    const block = itemMatch[0] || '';
    const index = Number.parseInt(itemMatch[1] || `${items.length + 1}`, 10);
    const workOrderNumber = itemMatch[2] || `Work order ${index}`;

    const markdownUrlRegex = new RegExp(WORK_ORDER_MARKDOWN_URL_REGEX.source, 'gi');
    const rawUrlRegex = new RegExp(WORK_ORDER_RAW_URL_REGEX.source, 'gi');
    const urlMatch = markdownUrlRegex.exec(block) || rawUrlRegex.exec(block);
    const normalizedUrl = normalizeWorkOrderPdfUrl(urlMatch?.[1] || urlMatch?.[0] || '');
    if (!normalizedUrl) {
      continue;
    }

    const firstLine = (block.split('\n')[0] || '').replace(/^\s*(?:(?:\d+[.)])|-)\s+/, '');
    const fields = firstLine
      .split(/\s*(?:\||\u2014|\u2013)\s*/)
      .map(field => stripWorkOrderLinks(field))
      .filter(Boolean);
    const statusText = fields.join(' ').toLowerCase();
    const blockText = normalizeWorkOrderSummary(block).toLowerCase();
    const isUnsigned = /\bunsigned\b/.test(statusText);
    const signed = (/\bsigned\b/.test(statusText) || /\bsigned\b/.test(blockText)) && !isUnsigned;
    const date = fields.find(field => /\b\d{4}-\d{2}-\d{2}\b/.test(field))?.match(/\b\d{4}-\d{2}-\d{2}\b/)?.[0];
    const customerField = fields.find(field => (
      field !== workOrderNumber
      && !/\b\d{4}-\d{2}-\d{2}\b/.test(field)
      && !/\b(?:signed|unsigned)\b/i.test(field)
      && !/work order/i.test(field)
    ));
    const customerMatch = block.match(/(?:^|\n)\s*Customer:\s*([\s\S]*?)(?=\n\s*(?:Summary:|PDF:|\[[^\]]+\]\(|(?:(?:\d+[.)])|-)\s+WO-|Want me to|What do you want|Options:|Next steps)|$)/i);
    const customerSource = customerField || customerMatch?.[1] || '';
    const { customer, address } = customerSource ? splitCustomerAddress(customerSource) : {};
    const summaryMatch = block.match(/(?:^|\n)\s*Summary:\s*([\s\S]*?)(?=\n\s*(?:PDF:|\[[^\]]+\]\(|(?:(?:\d+[.)])|-)\s+WO-|Want me to|What do you want|Options:|Next steps)|$)/i);
    const summary = summaryMatch?.[1] ? normalizeWorkOrderSummary(summaryMatch[1]) : undefined;

    items.push({
      index,
      number: workOrderNumber,
      date,
      signed,
      customer,
      address,
      summary,
      scheduled: date,
      url: normalizedUrl,
      actionLabel: signed ? 'View work order' : 'Open & Sign',
    });
  }

  return items;
};

const formatWorkOrderDate = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (/\btoday\b/i.test(String(value))) {
    return 'Today';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    const match = String(value).match(/\b\d{4}-\d{2}-\d{2}\b/);
    return match?.[0];
  }
  return parsed.toISOString().slice(0, 10);
};

const formatWorkOrderScheduledLabel = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  const raw = String(value);
  if (/\btoday\b/i.test(raw)) {
    return 'Today';
  }
  const dateOnly = raw.match(/\b\d{4}-\d{2}-\d{2}\b/)?.[0];
  if (dateOnly) {
    const today = new Date().toISOString().slice(0, 10);
    return dateOnly === today ? 'Today' : dateOnly;
  }
  return normalizeWorkOrderSummary(raw);
};

export const stringifyWorkOrderValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (Array.isArray(value)) {
    const rendered = value
      .map(item => stringifyWorkOrderValue(item))
      .filter((item): item is string => Boolean(item));
    return rendered.length > 0 ? rendered.join(', ') : undefined;
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const primary = record.name
      ?? record.full_name
      ?? record.label
      ?? record.title
      ?? record.description
      ?? record.value
      ?? record.phone;
    if (primary !== undefined && primary !== null) {
      return stringifyWorkOrderValue(primary);
    }
    return undefined;
  }
  const rendered = normalizeWorkOrderSummary(String(value));
  return rendered || undefined;
};

const firstWorkOrderValue = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    const rendered = stringifyWorkOrderValue(value);
    if (rendered) {
      return rendered;
    }
  }
  return undefined;
};

const getWorkOrderItemsSignature = (items: WorkOrderResultItem[]): string => (
  items
    .map(item => [
      item.index,
      item.number,
      item.date || '',
      item.signed ? 'signed' : 'unsigned',
      item.customer || '',
      item.address || '',
      item.summary || '',
      item.scheduled || '',
      item.part || '',
      item.technician || '',
      item.serviceType || '',
      item.status || '',
      item.url,
      item.actionLabel,
    ].join('\u001f'))
    .join('\u001e')
);

export const hasWorkOrderLinks = (content: string): boolean => (
  /(?:api\.work-orders\/pdf|\/api\/work-orders\/pdf|\/api\/work-orders\/\d+\/pdf)/i.test(String(content || ''))
);

const hasWorkOrderField = (workOrder: any, field: string): boolean => (
  Boolean(workOrder && typeof workOrder === 'object' && Object.prototype.hasOwnProperty.call(workOrder, field))
);

const statusFieldValue = (
  workOrder: any,
  fields: string[],
  fallback?: string,
): string | undefined => {
  const hasAnyField = fields.some(field => hasWorkOrderField(workOrder, field));
  if (!hasAnyField) {
    return fallback;
  }
  return firstWorkOrderValue(...fields.map(field => workOrder?.[field]));
};

const workOrderNumberFromLabel = (label: string, fallbackIndex: number, url?: string, contextNumber?: string): string => {
  const match = String(label || '').match(/\bWO-[A-Z0-9-]+\b/i);
  if (match?.[0]) {
    return match[0].toUpperCase();
  }
  const contextMatch = String(contextNumber || '').match(/\bWO-[A-Z0-9-]+\b/i);
  const contextualWorkOrderNumber = contextMatch?.[0]?.toUpperCase();
  const stableId = url ? workOrderIdFromUrl(url) : null;
  const cleaned = normalizeWorkOrderSummary(label);
  const genericLabel = !cleaned || /^open work order$/i.test(cleaned) || /^work order\s+\d+$/i.test(cleaned);
  if (genericLabel && contextualWorkOrderNumber) {
    return contextualWorkOrderNumber;
  }
  if (genericLabel && stableId) {
    return `Work order #${stableId}`;
  }
  return cleaned && !/^open work order$/i.test(cleaned) ? cleaned : `Work order ${fallbackIndex}`;
};

export const extractWorkOrderLinks = (text: string, options?: { date?: string; startIndex?: number }): WorkOrderResultItem[] => {
  const parsedList = parseWorkOrderResults(text);
  if (parsedList.length > 0) {
    return parsedList.map((item, offset) => ({
      ...item,
      index: (options?.startIndex || 1) + offset,
      date: item.date || options?.date,
    }));
  }

  const raw = normalizeWorkOrderParseInput(text);
  const contextualWorkOrderNumber = raw.match(/\bWO-[A-Z0-9-]+\b/i)?.[0]?.toUpperCase();
  const items: WorkOrderResultItem[] = [];
  const seen = new Set<string>();
  let markdownMatch: RegExpExecArray | null;
  const markdownRegex = new RegExp(WORK_ORDER_MARKDOWN_LINK_REGEX.source, 'gi');
  while ((markdownMatch = markdownRegex.exec(raw)) !== null) {
    const label = markdownMatch[1] || 'Work order';
    const url = normalizeWorkOrderPdfUrl(markdownMatch[2] || '');
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    const index = (options?.startIndex || 1) + items.length;
    const isUnsigned = /\bunsigned\b/i.test(label);
    const signed = /\bsigned\b/i.test(label) && !isUnsigned;
    items.push({
      index,
      number: workOrderNumberFromLabel(label, index, url, contextualWorkOrderNumber),
      date: options?.date,
      signed,
      url,
      actionLabel: signed ? 'View work order' : 'Open & Sign',
    });
  }

  let rawMatch: RegExpExecArray | null;
  const rawRegex = new RegExp(WORK_ORDER_RAW_URL_REGEX.source, 'gi');
  while ((rawMatch = rawRegex.exec(raw)) !== null) {
    const url = normalizeWorkOrderPdfUrl(rawMatch[0] || '');
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    const index = (options?.startIndex || 1) + items.length;
    items.push({
      index,
      number: workOrderNumberFromLabel('', index, url, contextualWorkOrderNumber),
      date: options?.date,
      signed: false,
      url,
      actionLabel: 'Open & Sign',
    });
  }

  return items;
};

const normalizeSavedWorkOrders = (workOrders?: SavedAgentJob['work_orders']): WorkOrderResultItem[] => {
  if (!Array.isArray(workOrders)) {
    return [];
  }

  const seen = new Set<string>();
  return workOrders.reduce<WorkOrderResultItem[]>((items, workOrder) => {
    const url = normalizeWorkOrderPdfUrl(workOrder.url || workOrder.render_url || '');
    if (!url || seen.has(url)) {
      return items;
    }
    seen.add(url);
    const index = items.length + 1;
    const label = workOrder.number || workOrder.title || workOrder.filename || `Work order ${index}`;
    const signed = Boolean(workOrder.signed);
    items.push({
      index,
      number: workOrderNumberFromLabel(label, index, url),
      date: formatWorkOrderDate(workOrder.date || workOrder.created_at),
      signed,
      customer: firstWorkOrderValue(workOrder.customer, workOrder.customer_name),
      address: firstWorkOrderValue(workOrder.address, workOrder.service_address),
      summary: firstWorkOrderValue(workOrder.job_description, workOrder.summary, workOrder.issue_summary),
      scheduled: formatWorkOrderScheduledLabel(workOrder.scheduled_at || workOrder.date),
      part: firstWorkOrderValue(workOrder.parts),
      technician: firstWorkOrderValue(workOrder.technician, workOrder.technician_name),
      serviceType: firstWorkOrderValue(workOrder.service_type, workOrder.services),
      url,
      actionLabel: signed ? 'View work order' : 'Open & Sign',
    });
    return items;
  }, []);
};

const normalizeSavedJobConversations = (sessions?: SavedAgentJob['sessions']): JobPanelConversation[] => {
  if (!Array.isArray(sessions)) {
    return [];
  }

  const seen = new Set<string>();
  return sessions.reduce<JobPanelConversation[]>((items, session, index) => {
    const id = String(session.session_id || session.id || '').trim();
    const marker = id || `conversation-${index}`;
    if (seen.has(marker)) {
      return items;
    }
    seen.add(marker);
    const summary = String(
      session.summary
      || session.conversation_summary
      || session.conversation_excerpt
      || '',
    ).trim();
    const dateValue = session.updated_at || session.created_at || '';
    const timestamp = dateValue ? new Date(dateValue) : null;
    const dateLabel = timestamp && !Number.isNaN(timestamp.getTime())
      ? `Chat ${timestamp.toLocaleDateString()}`
      : '';
    items.push({
      id: marker,
      sessionId: id || undefined,
      title: String(session.title || 'Assistant chat').trim() || 'Assistant chat',
      subtitle: dateLabel || summary || 'Saved conversation',
    });
    return items;
  }, []);
};

const simplifyWorkOrderResponse = (text: string): string => {
  const raw = String(text || '');
  const normalizedUrls = new Set<string>();
  let markdownMatch: RegExpExecArray | null;
  const markdownUrlRegex = new RegExp(WORK_ORDER_MARKDOWN_URL_REGEX.source, 'gi');
  while ((markdownMatch = markdownUrlRegex.exec(raw)) !== null) {
    const normalized = normalizeWorkOrderPdfUrl(markdownMatch[1] || '');
    if (normalized) {
      normalizedUrls.add(normalized);
    }
  }
  let rawMatch: RegExpExecArray | null;
  const rawUrlRegex = new RegExp(WORK_ORDER_RAW_URL_REGEX.source, 'gi');
  while ((rawMatch = rawUrlRegex.exec(raw)) !== null) {
    const normalized = normalizeWorkOrderPdfUrl(rawMatch[0] || '');
    if (normalized) {
      normalizedUrls.add(normalized);
    }
  }
  const shouldPreserveList = normalizedUrls.size > 1
    || /\b(found|matching|which one|reply with|numbered list|previous|history|recent work orders|work orders:)\b/i.test(raw);
  if (shouldPreserveList) {
    return raw;
  }
  const normalizedUrl = Array.from(normalizedUrls)[0] || null;
  if (!normalizedUrl) {
    return raw;
  }
  return `Work order is ready. Open it to review, sign, and email a copy.\n\n[Open work order](${normalizedUrl})`;
};

const SignaturePad: React.FC<{
  label: string;
  onChange: (dataUrl: string | null) => void;
}> = ({ label, onChange }) => {
  type SignaturePoint = { x: number; y: number };
  type SignatureStroke = SignaturePoint[];

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const strokesRef = useRef<SignatureStroke[]>([]);
  const activeStrokeRef = useRef<SignatureStroke | null>(null);

  const configureContext = useCallback((context: CanvasRenderingContext2D, ratio: number) => {
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 2.2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#111827';
  }, []);

  const drawStroke = useCallback((context: CanvasRenderingContext2D, stroke: SignatureStroke) => {
    if (stroke.length === 0) {
      return;
    }
    context.beginPath();
    context.moveTo(stroke[0].x, stroke[0].y);
    if (stroke.length === 1) {
      context.lineTo(stroke[0].x + 0.1, stroke[0].y + 0.1);
    } else {
      stroke.slice(1).forEach(point => context.lineTo(point.x, point.y));
    }
    context.stroke();
  }, []);

  const redrawStrokes = useCallback((context: CanvasRenderingContext2D, canvas: HTMLCanvasElement, ratio: number) => {
    context.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    strokesRef.current.forEach(stroke => drawStroke(context, stroke));
  }, [drawStroke]);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 20 || rect.height < 20) {
      return;
    }
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = canvas.getContext('2d');
    if (!context) return;
    configureContext(context, ratio);
    redrawStrokes(context, canvas, ratio);
  }, [configureContext, redrawStrokes]);

  useEffect(() => {
    let frame = 0;
    const scheduleResize = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(resizeCanvas);
    };

    scheduleResize();
    const timer = window.setTimeout(scheduleResize, 120);
    const observer = typeof ResizeObserver !== 'undefined' && canvasRef.current
      ? new ResizeObserver(scheduleResize)
      : null;
    if (observer && canvasRef.current) {
      observer.observe(canvasRef.current);
    }
    window.addEventListener('resize', scheduleResize);
    return () => {
      window.cancelAnimationFrame(frame);
      if (timer) {
        window.clearTimeout(timer);
      }
      observer?.disconnect();
      window.removeEventListener('resize', scheduleResize);
    };
  }, [resizeCanvas]);

  const pointForEvent = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const emitSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasInkRef.current) {
      onChange(null);
      return;
    }
    onChange(canvas.toDataURL('image/png'));
  }, [onChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    const ratio = window.devicePixelRatio || 1;
    context.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    strokesRef.current = [];
    activeStrokeRef.current = null;
    hasInkRef.current = false;
    onChange(null);
  }, [onChange]);

  return (
    <SignaturePadWrap>
      <SignaturePadLabel>
        <span>{label}</span>
        <SignatureClearButton type="button" onClick={clear}>Clear</SignatureClearButton>
      </SignaturePadLabel>
      <SignatureCanvas
        ref={canvasRef}
        onPointerDown={(event) => {
          const context = canvasRef.current?.getContext('2d');
          if (!context) return;
          event.currentTarget.setPointerCapture(event.pointerId);
          const point = pointForEvent(event);
          const stroke = [point];
          activeStrokeRef.current = stroke;
          strokesRef.current.push(stroke);
          drawingRef.current = true;
          context.beginPath();
          context.moveTo(point.x, point.y);
        }}
        onPointerMove={(event) => {
          if (!drawingRef.current) return;
          const context = canvasRef.current?.getContext('2d');
          if (!context) return;
          const point = pointForEvent(event);
          const stroke = activeStrokeRef.current;
          const previousPoint = stroke?.[stroke.length - 1];
          if (stroke) {
            stroke.push(point);
          }
          if (previousPoint) {
            context.beginPath();
            context.moveTo(previousPoint.x, previousPoint.y);
          }
          context.lineTo(point.x, point.y);
          context.stroke();
          hasInkRef.current = true;
        }}
        onPointerUp={(event) => {
          drawingRef.current = false;
          activeStrokeRef.current = null;
          try {
            event.currentTarget.releasePointerCapture(event.pointerId);
          } catch (_error) {
            // Pointer may already be released by the browser.
          }
          emitSignature();
        }}
        onPointerCancel={() => {
          drawingRef.current = false;
          activeStrokeRef.current = null;
          emitSignature();
        }}
      />
    </SignaturePadWrap>
  );
};

const WorkOrderResultList: React.FC<{
  intro?: string;
  items: WorkOrderResultItem[];
  onOpen: (item: WorkOrderResultItem) => void;
  onOpenExternal: (url: string) => void;
  compact?: boolean;
  refreshKey?: string | number;
}> = ({ intro, items, onOpen, onOpenExternal, compact = false, refreshKey }) => {
  const signedAwareItems = items.map(applyKnownWorkOrderSignedState);
  const [hydratedItems, setHydratedItems] = useState<WorkOrderResultItem[]>(signedAwareItems);
  const itemsSignature = getWorkOrderItemsSignature(signedAwareItems);
  const hydrationSignature = `${itemsSignature}\u001d${refreshKey ?? ''}`;
  const lastHydrationSignatureRef = useRef<string>('');

  useEffect(() => {
    if (lastHydrationSignatureRef.current === hydrationSignature) {
      return;
    }
    lastHydrationSignatureRef.current = hydrationSignature;
    let cancelled = false;
    setHydratedItems(signedAwareItems);
    const hydrate = async () => {
      const nextItems = await Promise.all(signedAwareItems.map(async (item) => {
        const statusUrl = statusUrlFromWorkOrderPdfUrl(item.url);
        if (!statusUrl) {
          return item;
        }
        try {
          const response = await api.get(statusUrl);
          const payload = response?.data;
          return payload?.work_order
            ? applyKnownWorkOrderSignedState(mergeWorkOrderStatus(item, payload.work_order))
            : item;
        } catch (_error) {
          return item;
        }
      }));
      if (!cancelled) {
        setHydratedItems(nextItems);
      }
    };
    void hydrate();
    return () => {
      cancelled = true;
    };
  // The signature already captures every item field that should trigger hydration.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrationSignature]);

  const detailRows = (item: WorkOrderResultItem): Array<{ icon: string; label: string; value: string }> => ([
    { icon: calendarOutline, label: 'Scheduled', value: firstWorkOrderValue(item.scheduled) },
    { icon: personOutline, label: 'Customer', value: firstWorkOrderValue(item.customer) },
    { icon: briefcaseOutline, label: 'Part', value: firstWorkOrderValue(item.part) },
    { icon: createOutline, label: 'Technician', value: firstWorkOrderValue(item.technician) },
    { icon: clipboardOutline, label: 'Service type', value: firstWorkOrderValue(item.serviceType) },
  ]).filter((row): row is { icon: string; label: string; value: string } => Boolean(row.value));

  return (
    <>
      {intro ? <HtmlContent>{decodeBasicHtmlEntities(intro)}</HtmlContent> : null}
      {compact ? (
        <CompactWorkOrderList>
          {hydratedItems.map(item => (
            <CompactWorkOrderRow
              key={`${item.index}-${item.number}-${item.url}`}
              type="button"
              onClick={() => onOpen(item)}
              aria-label={`Open work order ${item.number}`}
            >
              <CompactWorkOrderIcon aria-hidden="true">
                <IonIcon icon={documentTextOutline} />
              </CompactWorkOrderIcon>
              <CompactWorkOrderMain>
                <CompactWorkOrderTitle>{item.number}</CompactWorkOrderTitle>
                <CompactWorkOrderMeta>
                  <WorkOrderStatusPill $signed={item.signed}>
                    <IonIcon icon={item.signed ? checkmarkCircleOutline : timeOutline} />
                    {item.signed ? 'Signed' : 'Needs signature'}
                  </WorkOrderStatusPill>
                  <CompactWorkOrderDate>{firstWorkOrderValue(item.scheduled, item.date, 'No date')}</CompactWorkOrderDate>
                </CompactWorkOrderMeta>
              </CompactWorkOrderMain>
              <CompactWorkOrderChevron aria-hidden="true">
                <IonIcon icon={chevronForwardOutline} />
              </CompactWorkOrderChevron>
            </CompactWorkOrderRow>
          ))}
        </CompactWorkOrderList>
      ) : (
      <WorkOrderResults>
        {hydratedItems.map(item => {
          const rows = detailRows(item);
          return (
          <WorkOrderResultCard key={`${item.index}-${item.number}-${item.url}`}>
            <WorkOrderCardHeader>
              <WorkOrderDocumentBadge aria-hidden="true">
                <IonIcon icon={documentTextOutline} />
              </WorkOrderDocumentBadge>
              <WorkOrderResultBody>
                <WorkOrderResultTitle>{item.number}</WorkOrderResultTitle>
                <WorkOrderResultMeta>
                  <WorkOrderStatusPill $signed={item.signed}>
                    <IonIcon icon={item.signed ? checkmarkCircleOutline : timeOutline} />
                    {item.signed ? 'Signed' : 'Awaiting signature'}
                  </WorkOrderStatusPill>
                </WorkOrderResultMeta>
              </WorkOrderResultBody>
            </WorkOrderCardHeader>

            {rows.length > 0 ? (
            <WorkOrderDetailGrid>
              {rows.map(row => (
                <WorkOrderResultDetail key={`${item.url}-${row.label}`}>
                  <WorkOrderDetailIcon>
                    <IonIcon icon={row.icon} />
                  </WorkOrderDetailIcon>
                  <WorkOrderDetailLabel>{row.label}</WorkOrderDetailLabel>
                  <WorkOrderDetailValue>{row.value}</WorkOrderDetailValue>
                </WorkOrderResultDetail>
              ))}
            </WorkOrderDetailGrid>
            ) : null}

            <WorkOrderResultActions>
              <WorkOrderResultButton type="button" onClick={() => onOpen(item)}>
                <IonIcon slot="start" icon={documentTextOutline} />
                {item.actionLabel}
                <IonIcon slot="end" icon={chevronForwardOutline} />
              </WorkOrderResultButton>
              <WorkOrderResultButton
                type="button"
                $variant="secondary"
                onClick={() => onOpenExternal(item.url)}
              >
                Details
                <IonIcon slot="end" icon={chevronForwardOutline} />
              </WorkOrderResultButton>
            </WorkOrderResultActions>
          </WorkOrderResultCard>
          );
        })}
      </WorkOrderResults>
      )}
    </>
  );
};

const IMAGE_ONLY_PLACEHOLDER = '[Image attached]';
const RUNTIME_DEAD_AIR_FALLBACK_SECONDS = 8;
const RUNTIME_WORK_ENTRY_BUFFER_LIMIT = 80;
type LiveWorkEntryKind = 'note' | 'tool' | 'handoff' | 'thinking' | 'group' | 'codex-message' | 'codex-muted';
type LiveWorkEntry = {
  eventName: string;
  label: string;
  kind: LiveWorkEntryKind;
  detail?: string;
  hidden?: boolean;
};
type RuntimeCommandGroup = {
  kind: 'command-group';
  entries: LiveWorkEntry[];
  isContinuation?: boolean;
};
type RuntimeDisplayItem = LiveWorkEntry | RuntimeCommandGroup;

const statusUrlFromWorkOrderPdfUrl = (url: string): string | null => {
  const normalized = normalizeWorkOrderPdfUrl(url);
  if (!normalized) {
    return null;
  }
  try {
    const parsed = new URL(normalized);
    const match = parsed.pathname.match(/^\/api\/work-orders\/(\d+)\/pdf$/i);
    if (!match?.[1]) {
      return null;
    }
    const statusUrl = new URL(`${parsed.origin}/api/work-orders/${match[1]}/status`);
    const token = parsed.searchParams.get('token');
    if (token) {
      statusUrl.searchParams.set('token', token);
    }
    return statusUrl.toString();
  } catch (_error) {
    return null;
  }
};

const mergeWorkOrderStatus = (item: WorkOrderResultItem, workOrder: any): WorkOrderResultItem => {
  const signed = Boolean(workOrder?.signed);
  return {
    ...item,
    number: workOrderNumberFromLabel(workOrder?.work_order_number || item.number, item.index, item.url),
    signed,
    status: statusFieldValue(workOrder, ['status'], item.status),
    customer: statusFieldValue(workOrder, ['customer_name', 'customer'], item.customer),
    address: statusFieldValue(workOrder, ['service_address', 'address'], item.address),
    summary: statusFieldValue(workOrder, ['job_description', 'issue_summary', 'summary'], item.summary),
    date: hasWorkOrderField(workOrder, 'created_at') ? formatWorkOrderDate(workOrder?.created_at) : item.date,
    scheduled: hasWorkOrderField(workOrder, 'scheduled_at') ? formatWorkOrderScheduledLabel(workOrder?.scheduled_at) : item.scheduled,
    part: statusFieldValue(workOrder, ['parts', 'part'], item.part),
    technician: statusFieldValue(workOrder, ['technician', 'technician_name'], item.technician),
    serviceType: statusFieldValue(workOrder, ['service_type', 'services'], item.serviceType),
    actionLabel: signed ? 'View work order' : 'Open & Sign',
  };
};

const applyKnownWorkOrderSignedState = (item: WorkOrderResultItem): WorkOrderResultItem => {
  if (item.signed) {
    return item;
  }
  const signedState = readWorkOrderSignedState(item.url);
  if (!signedState?.signedUrl) {
    return item;
  }
  return {
    ...item,
    signed: true,
    actionLabel: 'View work order',
  };
};
type CodexDisplayEvent = {
  label: string;
  kind: LiveWorkEntryKind;
  detail?: string;
  hidden?: boolean;
};

const PRIVATE_REASONING_PATTERNS = [
  /\bchain[- ]?of[- ]?thought\b/i,
  /\breasoning\b/i,
  /\brationale\b/i,
  /\bhidden prompt\b/i,
  /\bsystem prompt\b/i,
  /\binternal policy\b/i,
  /\bconfidence score\b/i,
  /\bheuristic\b/i,
];

const ADAPTIVE_ACTION_LABELS: Record<string, string> = {
  inspect_oem_graph: 'Checking matching product families.',
  read_graph_node: 'Reading the selected product family.',
  search_tables: 'Checking extracted tables.',
  search_figures: 'Checking extracted diagrams.',
  ensure_oem_artifacts: 'Preparing missing diagrams from the source manual.',
  search_figures_after_ensure: 'Rechecking diagrams after artifact preparation.',
  read_artifact_context: 'Reading the selected evidence context.',
  search_exact_tokens: 'Checking exact model, code, connector, or value tokens.',
  search_scoped_docs: 'Searching scoped OEM manuals.',
  fallback_broad_search: 'Expanding the manual search carefully.',
};

const sanitizeLiveWorkLabel = (value: string | null | undefined): string => {
  const normalized = String(value || '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) {
    return '';
  }

  if (PRIVATE_REASONING_PATTERNS.some(pattern => pattern.test(normalized))) {
    return 'Working on the next step.';
  }

  if (normalized.length > 180) {
    const firstSentence = normalized.match(/^.{1,180}?(?:[.!?](?:\s|$)|$)/)?.[0]?.trim();
    return firstSentence || `${normalized.slice(0, 177).trim()}...`;
  }

  return normalized;
};

const publicAdaptiveStepLabel = (step: Partial<AdaptiveTraceStep> | null | undefined): string => {
  if (!step) {
    return '';
  }

  const action = String(step.action || '').trim();
  const status = String(step.status || '').trim();
  const predictionError = String(step.prediction_error || '').trim();

  if (status === 'revising' || predictionError) {
    if (action === 'search_tables') {
      return 'Table evidence was not enough, so I am checking manuals.';
    }
    if (action === 'search_figures') {
      return 'Diagram evidence was not enough, so I am checking another source.';
    }
    if (action === 'search_exact_tokens') {
      return 'Exact-token evidence is not enough yet, so I am continuing the search.';
    }
    if (action === 'search_scoped_docs') {
      return 'Scoped manual evidence is weak, so I am expanding carefully.';
    }
    if (action === 'fallback_broad_search') {
      return 'Broad manual evidence is still weak, so I am preserving uncertainty.';
    }
  }

  if (status === 'approved') {
    if (action === 'search_tables') {
      return 'Accepted a relevant table as evidence.';
    }
    if (action === 'search_figures' || action === 'search_figures_after_ensure') {
      return 'Accepted a relevant diagram as evidence.';
    }
    if (action === 'read_artifact_context') {
      return 'Accepted the selected page context.';
    }
    if (action === 'verify_visual_artifact') {
      return 'Verified the selected diagram.';
    }
  }

  return sanitizeLiveWorkLabel(ADAPTIVE_ACTION_LABELS[action] || step.label || 'Working on the next step.');
};

const normalizeDraftText = (value: string | null | undefined): string =>
  String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();

const doesMessageMatchQueuedDraft = (item: any, draft: QueuedDraft): boolean => {
  if (!item || item.sender !== 'user') {
    return false;
  }

  const draftText = normalizeDraftText(draft.text || (draft.image ? IMAGE_ONLY_PLACEHOLDER : ''));
  const itemText = normalizeDraftText(item.content);
  if (draftText && itemText !== draftText) {
    return false;
  }

  if (draft.image && !item.has_image && !item.image_url) {
    return false;
  }

  const queuedAtMs = timelineTimestampValue(draft.queuedAt);
  const itemAtMs = timelineTimestampValue(item.timestamp);
  return !queuedAtMs || !itemAtMs || itemAtMs >= queuedAtMs - 5000;
};

const isPreviewableArtifactImage = (label: string, url: string, objectKey: string): boolean => {
  const normalized = `${label} ${url} ${objectKey}`.toLowerCase();
  if (normalized.includes('table')) {
    return false;
  }
  if (/\/api\/oem\/(?:carrier-live-|managed-)?pdf-page-image/.test(normalized)) {
    return true;
  }
  if (normalized.includes('/api/job-photos/image')) {
    return true;
  }
  if (normalized.includes('/api/generated-images/image')) {
    return true;
  }
  return /\.(png|jpe?g|webp|gif)(\?|#|$)/i.test(url) || /\.(png|jpe?g|webp|gif)$/i.test(objectKey);
};

const parseCodexEventPayload = (label: string): any | null => {
  try {
    const parsed = JSON.parse(label);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_error) {
    return null;
  }
};

const formatCodexTypeLabel = (value: string): string =>
  value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
    .trim();

const compactJsonForCodex = (value: any): string => {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(trimmed));
    } catch (_error) {
      return trimmed;
    }
  }
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
};

const codexMcpInvocationLabel = (item: any, isDone: boolean): string => {
  const server = String(item?.server || item?.server_name || 'mcp').trim();
  const tool = String(item?.tool || item?.tool_name || item?.name || 'tool').trim();
  const args = compactJsonForCodex(
    item?.arguments ?? item?.args ?? item?.tool_input ?? item?.input ?? item?.params,
  );
  return `${isDone ? 'Called' : 'Calling'} ${server}.${tool}(${args})`;
};

const truncateCodexToolDetail = (value: string): string => {
  const lines = String(value || '').split(/\r?\n/).filter(line => line.trim());
  const visible = lines.slice(0, 5);
  if (lines.length > visible.length) {
    visible.push('...');
  }
  return visible.join('\n').trim();
};

const codexToolResultBlockText = (block: any): string => {
  if (typeof block === 'string') {
    return block;
  }
  if (!block || typeof block !== 'object') {
    return '';
  }
  const type = String(block.type || block.kind || '').trim().toLowerCase();
  if (type === 'text' || block.text) {
    return String(block.text || '').trim();
  }
  if (type === 'image') {
    return '<image content>';
  }
  if (type === 'audio') {
    return '<audio content>';
  }
  const uri = block.uri || block.resource?.uri || block.resource_link?.uri;
  if (type === 'resource_link' && uri) {
    return `link: ${uri}`;
  }
  if (type === 'resource' && uri) {
    return `embedded resource: ${uri}`;
  }
  try {
    return JSON.stringify(block);
  } catch (_error) {
    return String(block);
  }
};

const codexMcpResultDetail = (item: any): string => {
  const result = item?.result && typeof item.result === 'object' ? item.result : null;
  const error = item?.error || item?.message || result?.error;
  if (error) {
    return truncateCodexToolDetail(`Error: ${String(error)}`);
  }

  const content = Array.isArray(result?.content) ? result.content : [];
  const contentText = content
    .map(codexToolResultBlockText)
    .filter(Boolean)
    .join('\n');
  if (contentText.trim()) {
    return truncateCodexToolDetail(contentText);
  }

  if (result?.structured_content && typeof result.structured_content === 'object') {
    return truncateCodexToolDetail(compactJsonForCodex(result.structured_content));
  }

  return '';
};

export const codexEventDisplay = (entryOrPayload: LiveWorkEntry | any): CodexDisplayEvent | null => {
  const rawPayload = typeof entryOrPayload?.eventName === 'string'
    ? parseCodexEventPayload(entryOrPayload.label)
    : entryOrPayload;
  const payload = rawPayload?.msg && typeof rawPayload.msg === 'object' ? rawPayload.msg : rawPayload;
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const type = String(payload.type || '').trim();
  const item = payload.item && typeof payload.item === 'object' ? payload.item : null;
  const itemType = String(item?.type || '').trim();

  if (type === 'thread.started') {
    return { label: 'Started a SmartDocs conversation.', kind: 'codex-muted', hidden: true };
  }

  if (type === 'turn.started') {
    return { label: 'Started this turn.', kind: 'codex-muted', hidden: true };
  }

  if (type === 'turn.completed') {
    return { label: 'Finished this turn.', kind: 'codex-muted', hidden: true };
  }

  if (type === 'agent_message_delta') {
    return { label: '', kind: 'codex-muted', hidden: true };
  }

  if ((type === 'item.started' || type === 'item.completed') && itemType === 'mcp_tool_call') {
    const status = String(item?.status || '').trim();
    const isDone = type === 'item.completed' || status === 'completed';
    return {
      label: codexMcpInvocationLabel(item, isDone),
      kind: 'tool',
      detail: codexMcpResultDetail(item),
    };
  }

  if (type === 'item.started') {
    if (itemType === 'reasoning') {
      return { label: 'Thinking through the request.', kind: 'thinking' };
    }
    return { label: itemType ? formatCodexTypeLabel(itemType) : 'Checking the next step.', kind: 'codex-muted', hidden: true };
  }

  if (type === 'item.completed' && item) {
    if (itemType === 'reasoning') {
      return { label: '', kind: 'codex-muted', hidden: true };
    }
    if (itemType === 'agent_message' && String(item.text || '').trim()) {
      return { label: String(item.text || '').trim(), kind: 'codex-message' };
    }
    if (itemType === 'error' && String(item.message || '').trim()) {
      const message = String(item.message || '').trim();
      if (/model metadata .* not found/i.test(message)) {
        return { label: message, kind: 'codex-muted', hidden: true };
      }
      return { label: String(item.message).trim(), kind: 'codex-muted' };
    }
    if (itemType === 'command_execution' || itemType === 'command_output') {
      return { label: 'Checked the local workspace.', kind: 'codex-muted', hidden: true };
    }
    if (itemType === 'file_change' || itemType === 'patch') {
      return { label: 'Edited a file', kind: 'codex-muted' };
    }
    if (itemType) {
      return { label: formatCodexTypeLabel(itemType), kind: 'codex-muted' };
    }
  }

  if (type) {
    return { label: formatCodexTypeLabel(type), kind: 'codex-muted' };
  }

  return null;
};

const formatReferencePreviewTitle = (rawTitle: string): string => {
  const filename = rawTitle.split(/[\\/]/).pop() || rawTitle;
  return filename.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_-]+/g, ' ').trim() || 'OEM manual page';
};

const extractArtifactImagePreviewItems = (html: string): ArtifactImagePreviewItem[] => {
  if (!html) {
    return [];
  }

  const seen = new Set<string>();
  const items: ArtifactImagePreviewItem[] = [];

  const addItem = (item: ArtifactImagePreviewItem | null) => {
    if (!item || seen.has(item.id)) {
      return;
    }
    seen.add(item.id);
    items.push(item);
  };

  const markdownImageRegex = /!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;
  let markdownMatch: RegExpExecArray | null;
  while ((markdownMatch = markdownImageRegex.exec(html)) !== null) {
    const label = (markdownMatch[1] || 'Manual page').trim() || 'Manual page';
    const originalUrl = markdownMatch[2] || '';
    if (!originalUrl || !isPreviewableArtifactImage(label, originalUrl, originalUrl)) {
      continue;
    }
    addItem({
      id: originalUrl,
      label,
      originalUrl: normalizeAgentArtifactUrl(originalUrl),
      displayUrl: normalizeAgentArtifactUrl(originalUrl),
    });
  }

  if (typeof DOMParser === 'undefined') {
    return items;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const images = Array.from(doc.querySelectorAll<HTMLImageElement>('img'));
    const links = Array.from(doc.querySelectorAll<HTMLAnchorElement>('a.oem-artifact-link'));

    images.forEach((image) => {
      const originalUrl = image.getAttribute('src') || '';
      const label = (image.getAttribute('alt') || image.getAttribute('title') || 'Manual page').trim();
      if (!originalUrl || !isPreviewableArtifactImage(label, originalUrl, originalUrl)) {
        return;
      }
      const normalizedUrl = normalizeAgentArtifactUrl(originalUrl);
      addItem({
        id: originalUrl,
        label,
        originalUrl: normalizedUrl,
        displayUrl: normalizedUrl,
      });
    });

    links.forEach((link) => {
      const originalUrl = link.getAttribute('href') || '';
      const bucket = link.getAttribute('data-bucket') || '';
      const objectKey = link.getAttribute('data-object-key') || '';
      const label = (link.textContent || 'Extracted image').trim();
      if (!originalUrl || !bucket || !objectKey || !isPreviewableArtifactImage(label, originalUrl, objectKey)) {
        return;
      }
      addItem({ id: `${bucket}/${objectKey}`, label, originalUrl: normalizeAgentArtifactUrl(originalUrl), bucket, objectKey });
    });

    return items;
  } catch (_error) {
    return items;
  }
};

const hidePreviewedArtifactImageLinks = (html: string): string => {
  return html
    .replace(/^\s*Verified diagram pages\s*$/gim, '')
    .replace(/!\[([^\]]*)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g, (match, label, url) => {
      return isPreviewableArtifactImage(String(label || ''), String(url || ''), String(url || '')) ? '' : match;
    })
    .replace(/<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi, (match, url) => {
      return isPreviewableArtifactImage('', String(url || ''), String(url || '')) ? '' : match;
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const classifyLiveWorkEntry = (label: string): LiveWorkEntryKind => {
  const normalized = (label || '').trim().toLowerCase();
  if (
    normalized.startsWith('ran ') ||
    normalized.startsWith('found ') ||
    normalized.startsWith('rejected ') ||
    normalized.startsWith('using ') ||
    normalized.includes(' finished') ||
    normalized.startsWith('ran ') ||
    normalized.startsWith('running ') ||
    normalized.startsWith('checking ') ||
    normalized.startsWith('checked ') ||
    normalized.startsWith('searching ') ||
    normalized.startsWith('searched ') ||
    normalized.startsWith('reading ') ||
    normalized.startsWith('read ') ||
    normalized.startsWith('continuing ')
  ) {
    return 'tool';
  }
  if (normalized.includes('checked ') || normalized.includes('finished checking')) {
    return 'group';
  }
  if (normalized.startsWith('switching to') || normalized.startsWith('switched to')) {
    return 'handoff';
  }
  if (normalized === 'thinking' || normalized === 'working on the next step.') {
    return 'thinking';
  }
  return 'note';
};

const isRawRuntimeTelemetry = (label: string): boolean => {
  const normalized = (label || '').trim();
  if (!normalized) {
    return false;
  }
  return (
    /^Using\s+\S+\s+\{.+\}$/i.test(normalized) ||
    /^\S+\s+finished$/i.test(normalized) ||
    /"\w+"\s*:/.test(normalized)
  );
};

const isInternalRuntimeEntry = (entry: LiveWorkEntry): boolean => {
  if (!entry.label) {
    return false;
  }
  if (entry.eventName.startsWith('codex_event:')) {
    if (entry.kind === 'tool' || entry.kind === 'codex-muted') {
      return true;
    }
    const display = codexEventDisplay(entry);
    return display?.kind === 'tool' || display?.kind === 'codex-muted';
  }
  if (isRawRuntimeTelemetry(entry.label)) {
    return true;
  }
  if (entry.eventName.startsWith('runtime_event:tool_group')) {
    return true;
  }
  return (
    entry.eventName === 'agent_handoff' ||
    entry.eventName.startsWith('agent_handoff:') ||
    entry.eventName === 'tool_start' ||
    entry.eventName.startsWith('tool_start:') ||
    entry.eventName === 'tool_complete' ||
    entry.eventName.startsWith('tool_complete:') ||
    entry.eventName === 'tool_end' ||
    entry.eventName.startsWith('tool_end:')
  );
};

const isBoilerplateRuntimeEntry = (entry: LiveWorkEntry): boolean => {
  if (entry.eventName.startsWith('codex_event:')) {
    if (entry.eventName === 'codex_event:turn.completed') {
      return true;
    }
    return Boolean(codexEventDisplay(entry)?.hidden);
  }
  const normalized = (entry.label || '').trim().toLowerCase();
  if (!normalized) {
    return true;
  }
  if (['request', 'complete'].includes(entry.eventName)) {
    return true;
  }
  if (entry.eventName === 'status') {
    return (
      normalized === 'starting smartdocs agent...' ||
      normalized === 'starting smartdocs agent.' ||
      normalized === 'smartdocs agent is thinking...' ||
      normalized === 'smartdocs agent is thinking.' ||
      normalized === 'request sent to smartdocs agent.' ||
      normalized === 'getting the request ready.' ||
      normalized === 'choosing the best next step.' ||
      normalized === 'starting the smartdocs codex runtime.' ||
      normalized === 'reading the request and choosing the right smartdocs skill.' ||
      normalized === 'starting codex.' ||
      normalized === 'starting codex'
    );
  }
  if (entry.eventName.startsWith('runtime_event:')) {
    return false;
  }
  return false;
};

const isCustomerRuntimeNarration = (entry: LiveWorkEntry): boolean => {
  if (entry.eventName.startsWith('codex_event:')) {
    if (entry.kind === 'codex-message' || entry.kind === 'thinking') {
      return true;
    }
    const display = codexEventDisplay(entry);
    return Boolean(display && !display.hidden && (display.kind === 'codex-message' || display.kind === 'thinking'));
  }
  if (isBoilerplateRuntimeEntry(entry) || isInternalRuntimeEntry(entry)) {
    return false;
  }
  if (entry.eventName === 'runtime_event:reasoning' || entry.eventName === 'runtime_event:status') {
    return true;
  }
  return ['runtime_narration', 'adaptive_step', 'status', 'error', 'fallback'].includes(entry.eventName);
};

export const isCodexRuntimeEntry = (entry: { eventName?: string | null }): boolean => {
  return String(entry.eventName || '').startsWith('codex_event:');
};

const liveWorkIconForKind = (kind: LiveWorkEntryKind): string => {
  if (kind === 'group') return '~';
  if (kind === 'tool') return '>_';
  if (kind === 'handoff') return '->';
  if (kind === 'thinking') return '...';
  if (kind === 'codex-muted') return '~';
  if (kind === 'codex-message') return '';
  return 'i';
};

const renderRuntimeStreamContent = (label: string): React.ReactNode => {
  const parts = String(label || '').split(/(`[^`]+`)/g);
  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      return <code key={`${part}-${index}`}>{part.slice(1, -1)}</code>;
    }
    return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
  });
};

const runtimeToolDetailLabel = (entry: LiveWorkEntry): string => {
  const [, toolName] = String(entry.eventName || '').split(':');
  if (toolName && toolName !== 'tool') {
    return `Tool call: ${toolName.replace(/_/g, ' ')}`;
  }
  if (entry.eventName.startsWith('codex_event:')) {
    return 'Codex tool event';
  }
  return 'Tool call';
};

const isRuntimeCommandEntry = (entry: LiveWorkEntry): boolean =>
  entry.kind === 'tool' || entry.kind === 'codex-muted';

const runtimeToolLifecycle = (entry: LiveWorkEntry): { phase: 'started' | 'completed'; key: string } | null => {
  const label = String(entry.label || '').trim();
  const match = label.match(/^(Calling|Called)\s+(.+)$/);
  if (!match?.[1] || !match?.[2]) {
    return null;
  }
  return {
    phase: match[1] === 'Calling' ? 'started' : 'completed',
    key: match[2].trim(),
  };
};

export const collapseRuntimeCommandLifecycleEntries = (entries: LiveWorkEntry[]): LiveWorkEntry[] => {
  const collapsed: LiveWorkEntry[] = [];
  const pendingStartedEntryIndexByKey = new Map<string, number>();

  entries.forEach(entry => {
    const lifecycle = runtimeToolLifecycle(entry);
    if (!lifecycle) {
      collapsed.push(entry);
      return;
    }

    if (lifecycle.phase === 'started') {
      pendingStartedEntryIndexByKey.set(lifecycle.key, collapsed.length);
      collapsed.push(entry);
      return;
    }

    const startedIndex = pendingStartedEntryIndexByKey.get(lifecycle.key);
    if (startedIndex === undefined) {
      collapsed.push(entry);
      return;
    }

    collapsed[startedIndex] = entry;
    pendingStartedEntryIndexByKey.delete(lifecycle.key);
  });

  return collapsed;
};

export const groupRuntimeCommandEntries = (entries: LiveWorkEntry[]): RuntimeDisplayItem[] => {
  const grouped: RuntimeDisplayItem[] = [];
  let pendingCommands: LiveWorkEntry[] = [];
  let commandGroupCount = 0;

  const flushCommands = () => {
    const rawCommandCount = pendingCommands.length;
    const collapsedCommands = collapseRuntimeCommandLifecycleEntries(pendingCommands);
    if (rawCommandCount === 1 && collapsedCommands.length === 1) {
      grouped.push(collapsedCommands[0]);
    } else if (collapsedCommands.length > 0) {
      grouped.push({
        kind: 'command-group',
        entries: collapsedCommands,
        isContinuation: commandGroupCount > 0,
      });
      commandGroupCount += 1;
    }
    pendingCommands = [];
  };

  entries.forEach(entry => {
    if (isRuntimeCommandEntry(entry)) {
      pendingCommands.push(entry);
      return;
    }
    flushCommands();
    grouped.push(entry);
  });

  flushCommands();
  return grouped;
};

const RuntimeStreamEntryRow: React.FC<{ entry: LiveWorkEntry }> = ({ entry }) => {
  if (entry.kind === 'tool' || entry.kind === 'codex-muted') {
    return (
      <RuntimeStreamToolDetails>
        <RuntimeStreamToolSummary>
          <RuntimeStreamIcon $kind="tool">{liveWorkIconForKind('tool')}</RuntimeStreamIcon>
          <RuntimeStreamText $kind="tool">
            <RuntimeStreamMessage>{renderRuntimeStreamContent(entry.label)}</RuntimeStreamMessage>
          </RuntimeStreamText>
        </RuntimeStreamToolSummary>
        <RuntimeStreamToolBody>{renderRuntimeStreamContent(entry.detail || runtimeToolDetailLabel(entry))}</RuntimeStreamToolBody>
      </RuntimeStreamToolDetails>
    );
  }

  return (
    <RuntimeStreamBubble $kind={entry.kind}>
      <RuntimeStreamIcon $kind={entry.kind}>{liveWorkIconForKind(entry.kind)}</RuntimeStreamIcon>
      <RuntimeStreamText $kind={entry.kind}>
        <RuntimeStreamMessage>{renderRuntimeStreamContent(entry.label)}</RuntimeStreamMessage>
      </RuntimeStreamText>
    </RuntimeStreamBubble>
  );
};

const RuntimeCommandGroupRow: React.FC<{ group: RuntimeCommandGroup }> = ({ group }) => (
  <RuntimeCommandGroupDetails>
    <RuntimeCommandGroupSummary>
      <RuntimeCommandGroupIcon>&gt;</RuntimeCommandGroupIcon>
      <span>
        Ran {group.entries.length}{group.isContinuation ? ' more' : ''} {group.entries.length === 1 ? 'tool' : 'tools'}
      </span>
    </RuntimeCommandGroupSummary>
    <RuntimeCommandGroupRows>
      {group.entries.map((entry, index) => (
        <RuntimeStreamEntryRow
          entry={entry}
          key={`${entry.eventName}-${entry.label}-${index}`}
        />
      ))}
    </RuntimeCommandGroupRows>
  </RuntimeCommandGroupDetails>
);

const RuntimeStreamDisplayRow: React.FC<{ item: RuntimeDisplayItem; index: number }> = ({ item, index }) => {
  if ('entries' in item) {
    return <RuntimeCommandGroupRow group={item} key={`command-group-${index}`} />;
  }
  return <RuntimeStreamEntryRow entry={item} key={`${item.eventName}-${item.label}-${index}`} />;
};

const RUNTIME_STREAM_EVENT_SEPARATOR = '\u241f';

const encodeRuntimeStreamEntry = (eventName: string | undefined, label: string): string => {
  const trimmedEvent = String(eventName || '').trim();
  return trimmedEvent ? `${trimmedEvent}${RUNTIME_STREAM_EVENT_SEPARATOR}${label}` : label;
};

const isRawRuntimeNarrationEvent = (eventName: string | undefined): boolean => {
  const normalized = String(eventName || '');
  return (
    normalized.startsWith('codex_event') ||
    normalized === 'runtime_narration' ||
    normalized.startsWith('runtime_event') ||
    normalized === 'status'
  );
};

const decodeRuntimeStreamEntry = (entry: string): { eventName: string; label: string } => {
  const separatorIndex = entry.indexOf(RUNTIME_STREAM_EVENT_SEPARATOR);
  if (separatorIndex < 0) {
    return { eventName: '', label: entry };
  }
  return {
    eventName: entry.slice(0, separatorIndex),
    label: entry.slice(separatorIndex + RUNTIME_STREAM_EVENT_SEPARATOR.length),
  };
};

const runtimeStreamEventLabel = (event: string, message: any): string => {
  if (event === 'request') {
    return 'Starting SmartDocs.';
  }
  if (event === 'status') {
    return String(message?.content || message?.message || '').trim();
  }
  if (event === 'runtime_narration') {
    return String(message?.content || message?.message || 'Working through the next step.').trim();
  }
  if (event === 'runtime_event') {
    return String(message?.content || message?.message || message?.title || 'Working through the next step.').trim();
  }
  if (event === 'codex_event') {
    const raw = (message as any)?.codex_event || (message as any)?.extra?.codex_event;
    if (raw && typeof raw === 'object') {
      return JSON.stringify(raw);
    }
    return String(message?.content || '');
  }
  if (event === 'complete') {
    return 'Assistant turn complete.';
  }
  if (event === 'error') {
    return String(message?.content || message?.message || 'The runtime reported an error.').trim();
  }
  return '';
};

const formatWorkElapsed = (startedAt: number | null): string => {
  if (!startedAt) {
    return '0s';
  }
  const totalSeconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }
  return `${minutes}m ${seconds}s`;
};

const OemArtifactImagePreviews: React.FC<{
  html: string;
  onOpen: (item: ArtifactImagePreviewItem) => void;
}> = ({ html, onOpen }) => {
  const [items, setItems] = useState<ArtifactImagePreviewItem[]>([]);
  const [previewRotations, setPreviewRotations] = useState<Record<string, number>>({});

  useEffect(() => {
    let isMounted = true;
    const extracted = extractArtifactImagePreviewItems(html);

    if (extracted.length === 0) {
      setItems([]);
      return;
    }

    setItems(extracted);

    const loadPresignedUrls = async () => {
      const hydrated = await Promise.all(extracted.map(async (item) => {
        if (!item.bucket || !item.objectKey) {
          return {
            ...item,
            displayUrl: item.displayUrl || item.originalUrl,
          };
        }
        try {
          const response = await api.post('/s3/presigned-url', {
            filename: item.objectKey,
            bucket: item.bucket,
          });
          return {
            ...item,
            displayUrl: response.data?.presigned_url || item.originalUrl,
          };
        } catch (error) {
          console.error('[ChatSession] Error loading artifact image preview:', error);
          return {
            ...item,
            displayUrl: item.originalUrl,
            error: true,
          };
        }
      }));

      if (isMounted) {
        setItems(hydrated);
      }
    };

    void loadPresignedUrls();
    return () => {
      isMounted = false;
    };
  }, [html]);

  if (items.length === 0) {
    return null;
  }

  const openPreviewItem = (item: ArtifactImagePreviewItem) => {
    const originalUrl = normalizeAgentArtifactUrl(item.originalUrl);
    const displayUrl = normalizeAgentArtifactUrl(item.displayUrl || item.originalUrl);
    onOpen({
      ...item,
      originalUrl,
      displayUrl,
    });
  };

  return (
    <ArtifactImagePreviewGrid>
      {items.map((item) => (
        <ArtifactImagePreviewButton
          key={item.id}
          type="button"
          onClick={() => openPreviewItem(item)}
          aria-label={`Open ${item.label} full screen`}
        >
          <ArtifactImagePreviewFrame>
            <ArtifactImagePreviewControls>
              <ArtifactImagePreviewControl
                type="button"
                title="Rotate preview left"
                aria-label="Rotate preview left"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setPreviewRotations(prev => ({
                    ...prev,
                    [item.id]: (prev[item.id] || 0) - 90,
                  }));
                }}
              >
                <IonIcon icon={arrowUndoOutline} />
              </ArtifactImagePreviewControl>
              <ArtifactImagePreviewControl
                type="button"
                title="Rotate preview right"
                aria-label="Rotate preview right"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setPreviewRotations(prev => ({
                    ...prev,
                    [item.id]: (prev[item.id] || 0) + 90,
                  }));
                }}
              >
                <IonIcon icon={arrowRedoOutline} />
              </ArtifactImagePreviewControl>
            </ArtifactImagePreviewControls>
            {item.displayUrl ? (
              <ArtifactImagePreview
                src={item.displayUrl}
                alt={item.label}
                style={{
                  transform: `rotate(${previewRotations[item.id] || 0}deg)`,
                }}
              />
            ) : (
              <ArtifactImageCaption>Loading image preview...</ArtifactImageCaption>
            )}
          </ArtifactImagePreviewFrame>
          <ArtifactImageCaption>{item.label}</ArtifactImageCaption>
        </ArtifactImagePreviewButton>
      ))}
    </ArtifactImagePreviewGrid>
  );
};

const splitMarkdownTableRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());

const isMarkdownTableSeparator = (line: string): boolean => {
  const cells = splitMarkdownTableRow(line);
  return cells.length > 1 && cells.every(cell => /^:?-{3,}:?$/.test(cell));
};

const getMarkdownLinkClassName = (href: string): string => {
  try {
    const parsed = new URL(href, window.location.href);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    if (host === 'ahridirectory.org' && /^\/details\/\d+\/\d+\/?$/.test(parsed.pathname)) {
      return 'markdown-link ahri-link';
    }
  } catch (_error) {
    // Keep relative app routes clickable with the normal markdown-link style.
  }
  return 'markdown-link';
};

export const unwrapBacktickedMarkdownLinks = (text: string): string => {
  return String(text || '').replace(/`(\(?\[[^\]]+\]\([^`\n]+?\)\)?)`/g, '$1');
};

export const normalizeBareBracketedAppLinks = (text: string): string => {
  return String(text || '').replace(/\[(\/api\/[^\]\s)]+[^\]]*)\]/gi, (_match, url, offset, fullText) => {
    if (String(fullText || '').charAt(Number(offset) + String(_match || '').length) === '(') {
      return _match;
    }
    const href = String(url || '').trim();
    if (!href) {
      return _match;
    }
    if (/^\/api\/oem\/document\b/i.test(href)) {
      return `[Open document](${href})`;
    }
    if (/^\/api\/work-orders\/(?:pdf\b|\d+\/pdf\b)/i.test(href)) {
      return `[Open work order](${href})`;
    }
    return `[Open link](${href})`;
  });
};

export const normalizeBareAppUrls = (text: string): string => {
  return String(text || '').replace(
    /(^|\s)(\/api\/(?:oem\/document|oem\/pdf-page-image)\?[^\s<>"')\]]+)(?=\s|$)/gim,
    (_match, prefix, url) => {
      let href = String(url || '').trim();
      if (!href) {
        return _match;
      }
      let punctuation = '';
      const punctuationMatch = href.match(/[.,;!?]+$/);
      if (punctuationMatch) {
        punctuation = punctuationMatch[0];
        href = href.slice(0, -punctuation.length);
      }
      const label = /^\/api\/oem\/pdf-page-image\b/i.test(href) ? 'Open page image' : 'Open document';
      return `${prefix}[${label}](${href})${punctuation || ''}`;
    },
  );
};

const renderMarkdownInline = (text: string, keyPrefix: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*\s][^*]*\*|_[^_\s][^_]*_|\[[^\]]+\]\(([^)\s]+)(?:\s+"[^"]*")?\)|(https?:\/\/[^\s<]+))/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const pushText = (value: string, index: number) => {
    if (value) {
      nodes.push(<React.Fragment key={`${keyPrefix}-text-${index}`}>{value}</React.Fragment>);
    }
  };

  while ((match = pattern.exec(text)) !== null) {
    pushText(text.slice(lastIndex, match.index), match.index);
    const token = match[0];
    const key = `${keyPrefix}-${match.index}`;
    if (token.startsWith('`') && token.endsWith('`')) {
      nodes.push(<code key={key}>{token.slice(1, -1)}</code>);
    } else if ((token.startsWith('**') && token.endsWith('**')) || (token.startsWith('__') && token.endsWith('__'))) {
      nodes.push(<strong key={key}>{renderMarkdownInline(token.slice(2, -2), `${key}-strong`)}</strong>);
    } else if ((token.startsWith('*') && token.endsWith('*')) || (token.startsWith('_') && token.endsWith('_'))) {
      nodes.push(<em key={key}>{renderMarkdownInline(token.slice(1, -1), `${key}-em`)}</em>);
    } else if (token.startsWith('[')) {
      const linkMatch = token.match(/^\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)$/);
      if (linkMatch) {
        const [, label, href] = linkMatch;
        nodes.push(
          <a
            key={key}
            className={getMarkdownLinkClassName(href)}
            href={href}
            onClick={(event) => {
              event.preventDefault();
              void openUrl(href);
            }}
          >
            {renderMarkdownInline(label, `${key}-link`)}
          </a>
        );
      } else {
        pushText(token, match.index);
      }
    } else if (/^https?:\/\//i.test(token)) {
      const href = token.replace(/[),.;:!?]+$/, '');
      const suffix = token.slice(href.length);
      nodes.push(
        <a
          key={key}
          className={getMarkdownLinkClassName(href)}
          href={href}
          onClick={(event) => {
            event.preventDefault();
            void openUrl(href);
          }}
        >
          {href}
        </a>
      );
      pushText(suffix, match.index + href.length);
    } else {
      pushText(token, match.index);
    }
    lastIndex = match.index + token.length;
  }
  pushText(text.slice(lastIndex), text.length);
  return nodes;
};

export const MermaidDiagram: React.FC<{ code: string }> = ({ code }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const renderDiagram = async () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }

      container.innerHTML = '';
      setRenderError(null);

      try {
        const { default: mermaid } = await import('mermaid');
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'base',
          themeVariables: {
            primaryColor: '#eaf2fb',
            primaryTextColor: '#172033',
            primaryBorderColor: '#9fb8d3',
            lineColor: '#526173',
            secondaryColor: '#f5f7fa',
            tertiaryColor: '#ffffff',
            fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          },
        });
        const id = `smartdocs-mermaid-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const { svg, bindFunctions } = await mermaid.render(id, code);
        if (cancelled) {
          return;
        }
        container.innerHTML = svg;
        bindFunctions?.(container);
      } catch (error) {
        if (!cancelled) {
          container.innerHTML = '';
          setRenderError(error instanceof Error ? error.message : 'Unable to render Mermaid diagram.');
        }
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [code]);

  if (renderError) {
    return (
      <pre>
        <code className="language-mermaid">{code}</code>
      </pre>
    );
  }

  return (
    <div
      aria-label="Rendered Mermaid diagram"
      className="mermaid-diagram-wrap"
      ref={containerRef}
    />
  );
};

const renderMarkdownBlocks = (markdown: string): React.ReactNode[] => {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let index = 0;
  let blockIndex = 0;

  const nextKey = (kind: string) => `${kind}-${blockIndex++}`;

  while (index < lines.length) {
    const line = lines[index] ?? '';
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const fenceMatch = line.match(/^```(\S*)\s*$/);
    if (fenceMatch) {
      const language = fenceMatch[1] || '';
      const codeLines: string[] = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index] ?? '')) {
        codeLines.push(lines[index] ?? '');
        index += 1;
      }
      if (index < lines.length) {
        index += 1;
      }
      if (language.toLowerCase() === 'mermaid') {
        blocks.push(<MermaidDiagram code={codeLines.join('\n')} key={nextKey('mermaid')} />);
        continue;
      }
      blocks.push(
        <pre key={nextKey('code')}>
          <code className={language ? `language-${language}` : undefined}>{codeLines.join('\n')}</code>
        </pre>
      );
      continue;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      const level = Math.min(headingMatch[1].length, 6);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;
      blocks.push(<Tag key={nextKey('heading')}>{renderMarkdownInline(headingMatch[2], `heading-${blockIndex}`)}</Tag>);
      index += 1;
      continue;
    }

    if (line.includes('|') && index + 1 < lines.length && isMarkdownTableSeparator(lines[index + 1] ?? '')) {
      const headers = splitMarkdownTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && (lines[index] ?? '').includes('|') && (lines[index] ?? '').trim()) {
        rows.push(splitMarkdownTableRow(lines[index] ?? ''));
        index += 1;
      }
      blocks.push(
        <div className="markdown-table-wrap" key={nextKey('table')}>
          <table className="markdown-table">
            <thead>
              <tr>{headers.map((header, cellIndex) => <th key={`h-${cellIndex}`}>{renderMarkdownInline(header, `th-${blockIndex}-${cellIndex}`)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`r-${rowIndex}`}>
                  {headers.map((_, cellIndex) => (
                    <td key={`c-${cellIndex}`}>{renderMarkdownInline(row[cellIndex] || '', `td-${blockIndex}-${rowIndex}-${cellIndex}`)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    if (/^\s*[-*+]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      const ordered = /^\s*\d+\.\s+/.test(line);
      const items: string[] = [];
      while (index < lines.length && (ordered ? /^\s*\d+\.\s+/.test(lines[index] ?? '') : /^\s*[-*+]\s+/.test(lines[index] ?? ''))) {
        items.push((lines[index] ?? '').replace(ordered ? /^\s*\d+\.\s+/ : /^\s*[-*+]\s+/, ''));
        index += 1;
      }
      const ListTag = (ordered ? 'ol' : 'ul') as keyof JSX.IntrinsicElements;
      blocks.push(
        <ListTag key={nextKey('list')}>
          {items.map((item, itemIndex) => <li key={itemIndex}>{renderMarkdownInline(item, `li-${blockIndex}-${itemIndex}`)}</li>)}
        </ListTag>
      );
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^>\s?/.test(lines[index] ?? '')) {
        quoteLines.push((lines[index] ?? '').replace(/^>\s?/, ''));
        index += 1;
      }
      blocks.push(<blockquote key={nextKey('quote')}>{renderMarkdownBlocks(quoteLines.join('\n'))}</blockquote>);
      continue;
    }

    const paragraphLines: string[] = [];
    while (
      index < lines.length &&
      (lines[index] ?? '').trim() &&
      !/^```/.test(lines[index] ?? '') &&
      !/^(#{1,6})\s+/.test(lines[index] ?? '') &&
      !/^\s*[-*+]\s+/.test(lines[index] ?? '') &&
      !/^\s*\d+\.\s+/.test(lines[index] ?? '') &&
      !/^>\s?/.test(lines[index] ?? '')
    ) {
      paragraphLines.push(lines[index] ?? '');
      index += 1;
    }
    blocks.push(<p key={nextKey('paragraph')}>{renderMarkdownInline(paragraphLines.join(' '), `p-${blockIndex}`)}</p>);
  }

  return blocks;
};

const OemReferencePagePreviews: React.FC<{
  references?: ReferenceDocumentItem[];
  artifacts?: AgentArtifactItem[];
  onOpen: (item: ArtifactImagePreviewItem) => void;
}> = ({ references, artifacts, onOpen }) => {
  const items = useMemo(() => {
    const seen = new Set<string>();
    const referenceItems = (references || []).flatMap((reference) => {
      const originalUrl = String(reference.page_image_url || '').trim();
      if (!originalUrl) {
        return [];
      }
      const normalizedUrl = normalizeAgentArtifactUrl(originalUrl);
      if (!normalizedUrl || seen.has(normalizedUrl)) {
        return [];
      }
      seen.add(normalizedUrl);
      const title = formatReferencePreviewTitle(reference.filename || 'OEM manual page');
      const pageLabel = reference.page_hint ? ` page ${reference.page_hint}` : '';
      return [{
        id: normalizedUrl,
        label: `${title}${pageLabel}`,
        originalUrl: normalizedUrl,
        displayUrl: normalizedUrl,
      }];
    });

    const artifactItems = getGeneratedImageArtifacts(artifacts).flatMap((artifact) => {
      const originalUrl = String(artifact.render_url || artifact.url || '').trim();
      const normalizedUrl = normalizeAgentArtifactUrl(originalUrl);
      if (!normalizedUrl || seen.has(normalizedUrl)) {
        return [];
      }
      seen.add(normalizedUrl);
      const label = formatReferencePreviewTitle(
        String(artifact.title || artifact.filename || 'Generated mockup'),
      );
      return [{
        id: normalizedUrl,
        label,
        originalUrl: normalizedUrl,
        displayUrl: normalizedUrl,
      }];
    });

    return [...referenceItems, ...artifactItems];
  }, [references, artifacts]);

  if (items.length === 0) {
    return null;
  }

  return (
    <ArtifactImagePreviewGrid>
      {items.map((item) => (
        <ArtifactImagePreviewButton
          key={item.id}
          type="button"
          onClick={() => onOpen(item)}
          aria-label={`Open ${item.label} full screen`}
        >
          <ArtifactImagePreviewFrame>
            <ArtifactImagePreview src={item.displayUrl} alt={item.label} />
          </ArtifactImagePreviewFrame>
          <ArtifactImageCaption>{item.label}</ArtifactImageCaption>
        </ArtifactImagePreviewButton>
      ))}
    </ArtifactImagePreviewGrid>
  );
};

const DOWNVOTE_REASON_OPTIONS = [
  { value: 'incorrect_incomplete', label: 'Incorrect or incomplete' },
  { value: 'not_what_i_asked', label: 'Not what I asked for' },
  { value: 'slow_or_buggy', label: 'Slow or buggy' },
  { value: 'other', label: 'Other' },
];

export const getSessionTitleValidationError = (value: string): string | null => {
  const trimmedValue = value.trim();
  if (hasControlCharacter(value)) {
    return 'Title cannot contain line breaks or control characters.';
  }
  if (trimmedValue.length > CHAT_SESSION_TITLE_MAX_LENGTH) {
    return `Title must be ${CHAT_SESSION_TITLE_MAX_LENGTH} characters or fewer.`;
  }
  return null;
};

/**
 * A session URL can be stale, mistyped, or intentionally shared from another
 * contractor. The API deliberately uses a non-disclosing response for those
 * cases, so the client treats the same shapes as an unavailable session and
 * returns to the user's own chat list.
 */
export const isSessionUnavailableError = (error: unknown): boolean => {
  const candidate = error as {
    status?: unknown;
    statusCode?: unknown;
    status_code?: unknown;
    message?: unknown;
    messageFromApi?: unknown;
    response?: {
      status?: unknown;
      data?: {
        status_code?: unknown;
        message?: unknown;
      };
    };
  } | null;

  const status = Number(
    candidate?.status ??
      candidate?.statusCode ??
      candidate?.status_code ??
      candidate?.response?.status ??
      candidate?.response?.data?.status_code,
  );
  const message = [
    candidate?.messageFromApi,
    candidate?.message,
    candidate?.response?.data?.message,
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();

  if (status === 404) {
    return true;
  }

  // Some deployments return 403 for an inaccessible session. Do not treat
  // quota/plan 403s as a missing session; only session-specific messages qualify.
  if (status === 403) {
    return /session/.test(message) &&
      /(not found|forbidden|unauthori[sz]ed|not accessible|access)/.test(message);
  }

  return /session\s+not\s+found/.test(message) ||
    /session\s+(?:is\s+)?not\s+accessible/.test(message);
};

const getSpeechRecognitionConstructor = (): SpeechRecognitionConstructorLike | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const mergeVoiceTranscript = (base: string, transcript: string): string => {
  const cleanTranscript = transcript.replace(/\s+/g, ' ').trim();
  if (!cleanTranscript) {
    return base;
  }
  const cleanBase = base.trimEnd();
  return cleanBase ? `${cleanBase} ${cleanTranscript}` : cleanTranscript;
};

// Default values for new chat sessions - these should be replaced with actual data
const DEFAULT_DISTRIBUTOR = "SmartDocs Distributor";
const DEFAULT_REGION = "North America";

const ChatSession: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const sessionId = React.useMemo(
    () => (id && id !== 'new' ? id : undefined),
    [id],
  );
  const initialLiveSnapshot = React.useMemo(
    () => sessionId ? getChatSessionLiveSnapshot(sessionId) : undefined,
    [sessionId],
  );
  const initialTimelineSnapshot = React.useMemo(
    () => sessionId ? getCachedTimeline(sessionId) : undefined,
    [sessionId],
  );
  const [hydratedSessionId, setHydratedSessionId] = useState(sessionId);
  const { user, token, isAuthenticated } = useAuth();
  useSimpleS3Handler(isAuthenticated);
  const capabilities = getUserCapabilities(user, isAuthenticated);
  const { campaign } = useMarketingCampaign();
  const [message, setMessage] = useState('');
  const [assistantManufacturers, setAssistantManufacturers] = useState<ManufacturerOption[]>([]);
  const [selectedManufacturerSlug, setSelectedManufacturerSlug] = useState('');
  const [assistantMode, setAssistantMode] = useState<AssistantMode>('manuals');
  const [assistantOptionsLoading, setAssistantOptionsLoading] = useState(true);
  const [assistantOptionsError, setAssistantOptionsError] = useState<string | null>(null);
  const [serviceTitan, setServiceTitan] = useState<ServiceTitanConnectionStatus>({
    available: false,
    authorized: false,
    connected: false,
    service_titan_connected: false,
    requires_reauthorization: false,
    missing_scopes: [],
    status: 'disconnected',
    mode: 'read-plus-confirmed-equipment-create',
    equipment_read_enabled: false,
    equipment_write_enabled: false,
    write_tools_enabled: false,
    message: 'ServiceTitan is not connected.',
  });
  const [connectingServiceTitan, setConnectingServiceTitan] = useState(false);
  const messageInputRef = useRef<HTMLIonTextareaElement>(null);
  const composerRef = useRef<HTMLDivElement | null>(null);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const [isListeningToVoice, setIsListeningToVoice] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [showVoicePermissionAlert, setShowVoicePermissionAlert] = useState(false);
  const speechRecognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const voiceBaseMessageRef = useRef('');
  const voiceFinalTranscriptRef = useRef('');
  const voiceAcceptingTranscriptRef = useRef(false);
  const ignoredComposerEchoRef = useRef<{ value: string; until: number } | null>(null);
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [selectedArtifactImage, setSelectedArtifactImage] = useState<ArtifactImagePreviewItem | null>(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrderPreviewItem | null>(null);
  const [customerSignature, setCustomerSignature] = useState<string | null>(null);
  const [technicianSignature, setTechnicianSignature] = useState<string | null>(null);
  const [signedWorkOrderUrl, setSignedWorkOrderUrl] = useState<string | null>(null);
  const [workOrderEmail, setWorkOrderEmail] = useState('');
  const [workOrderSignStatus, setWorkOrderSignStatus] = useState<string | null>(null);
  const [workOrderSignError, setWorkOrderSignError] = useState<string | null>(null);
  const [isSigningWorkOrder, setIsSigningWorkOrder] = useState(false);
  const [isEmailingWorkOrder, setIsEmailingWorkOrder] = useState(false);
  const [isSelectedWorkOrderSigned, setIsSelectedWorkOrderSigned] = useState(false);
  const [artifactImageZoom, setArtifactImageZoom] = useState(1);
  const [artifactImageRotation, setArtifactImageRotation] = useState(0);
  const [artifactImagePan, setArtifactImagePan] = useState({ x: 0, y: 0 });
  const [queuedDraft, setQueuedDraft] = useState<QueuedDraft | null>(
    () => initialLiveSnapshot?.queuedDraft || null,
  );
  const [isQueueSubmitting, setIsQueueSubmitting] = useState(false);
  const [isSending, setIsSending] = useState(() => initialLiveSnapshot?.isSending ?? false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showAttachActionSheet, setShowAttachActionSheet] = useState(false);
  const [showJobsModal, setShowJobsModal] = useState(false);
  const [selectedJobPanelId, setSelectedJobPanelId] = useState<string | null>(null);
  const [jobsViewMode, setJobsViewMode] = useState<'list' | 'detail'>('list');
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [savedAgentJobs, setSavedAgentJobs] = useState<SavedAgentJob[]>([]);
  const [isLoadingAgentJobs, setIsLoadingAgentJobs] = useState(false);
  const [jobPhotoTargetId, setJobPhotoTargetId] = useState<string | null>(null);
  const [isSavingJobPhoto, setIsSavingJobPhoto] = useState(false);
  const [deletingJobPhotoId, setDeletingJobPhotoId] = useState<string | null>(null);
  const selectedWorkOrderUrl = React.useMemo(
    () => normalizeWorkOrderPdfUrl(selectedWorkOrder?.url || '') || selectedWorkOrder?.url || null,
    [selectedWorkOrder?.url]
  );
  const effectiveSignedWorkOrderUrl = signedWorkOrderUrl || (isSelectedWorkOrderSigned ? selectedWorkOrderUrl : null);

  // Show banner for banner-type campaigns
  const showBanner = React.useMemo(() => {
    return (
      isAuthenticated &&
      campaign &&
      campaign.communication_type === 'banner' &&
      campaign.message &&
      !bannerDismissed
    );
  }, [isAuthenticated, campaign, bannerDismissed]);

  const loadAssistantOptions = useCallback(async () => {
    setAssistantOptionsLoading(true);
    setAssistantOptionsError(null);
    try {
      const options = await chatApi.getAssistantOptions();
      setAssistantManufacturers(options.manufacturers);
      if (options.service_titan) {
        setServiceTitan(options.service_titan);
      }
      const initialSlug = options.preferred_oem_slug
        || (options.manufacturers.length === 1 ? options.manufacturers[0].slug : '');
      setSelectedManufacturerSlug(current => current || initialSlug);
    } catch (error) {
      setAssistantOptionsError(error instanceof Error ? error.message : 'Unable to load assistant options.');
    } finally {
      setAssistantOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    void loadAssistantOptions().catch(() => undefined);
  }, [isAuthenticated, loadAssistantOptions]);

  useEffect(() => {
    const callbackStatus = new URLSearchParams(location.search).get('servicetitan');
    if (!callbackStatus || !isAuthenticated) return;
    setAssistantMode('servicetitan');
    void loadAssistantOptions().finally(() => {
      history.replace(location.pathname);
    });
  }, [history, isAuthenticated, loadAssistantOptions, location.pathname, location.search]);

  const handleServiceTitanConnect = useCallback(async () => {
    if (connectingServiceTitan) return;
    setConnectingServiceTitan(true);
    setAssistantOptionsError(null);
    try {
      if (serviceTitan.authorized && !serviceTitan.requires_reauthorization) {
        const status = await chatApi.verifyServiceTitanConnection();
        setServiceTitan(status);
        if (!status.connected) {
          setAssistantOptionsError(status.message);
        }
        setConnectingServiceTitan(false);
        return;
      }
      const returnPath = id && id !== 'new' ? `/chat/${id}` : '/chat/new';
      const target = Capacitor.isNativePlatform() ? 'native' : 'web';
      const authorization = await chatApi.startServiceTitanConnection(returnPath, target);
      if (Capacitor.isNativePlatform()) {
        await Browser.open({ url: authorization.authorization_url });
        setConnectingServiceTitan(false);
      } else {
        window.location.assign(authorization.authorization_url);
      }
    } catch (error) {
      setAssistantOptionsError(
        error instanceof Error ? error.message : 'Unable to connect ServiceTitan.'
      );
      setConnectingServiceTitan(false);
    }
  }, [connectingServiceTitan, id, serviceTitan.authorized, serviceTitan.requires_reauthorization]);

  const handleManufacturerChange = useCallback((slug: string) => {
    setSelectedManufacturerSlug(slug);
    const manufacturer = assistantManufacturers.find((item) => item.slug === slug);
    if (assistantMode === 'parts' && !manufacturer?.partsSupported) {
      setAssistantMode('manuals');
    }
    setAssistantOptionsError(null);
    void chatApi.updateAssistantPreference(slug).catch((error) => {
      setAssistantOptionsError(error instanceof Error ? error.message : 'Unable to save manufacturer preference.');
    });
  }, [assistantManufacturers, assistantMode]);

  // Reset banner dismissal when campaign changes
  React.useEffect(() => {
    setBannerDismissed(false);
  }, [campaign?.id]);

  const [showGlobalLoading] = useState(false);
  const [messages] = useState<MessageDisplay[]>([]);
  const [lastUpdateTime, setLastUpdateTime] = useState<string | null>(null);
  const [escalationStatus, setEscalationStatus] = useState<'none' | 'pending' | 'connected' | 'resolved'>('none');

  // New state for unified timeline
  const [timeline, setTimeline] = useState<any[]>(
    () => mergeServerTimeline(
      initialTimelineSnapshot?.data?.timeline || [],
      initialLiveSnapshot?.pendingTimelineItems || [],
    ),
  );
  const [escalationEvents, setEscalationEvents] = useState<any[]>(
    () => initialTimelineSnapshot?.data?.escalation_events || [],
  );
  const [currentEscalationId, setCurrentEscalationId] = useState<string | null>(null);
  const jobPanelPhotos = React.useMemo<JobPanelPhoto[]>(() => (
    timeline
      .filter(item => item?.has_image && item?.image_url)
      .map((item, index) => ({
        id: String(item.id || `photo-${index}`),
        url: String(item.image_url),
        filename: item.image_filename || 'Job photo',
        timestamp: item.timestamp || null,
      }))
  ), [timeline]);
  const jobPanelWorkOrderCount = React.useMemo(() => (
    timeline.filter(item => typeof item?.content === 'string' && hasWorkOrderLinks(item.content)).length
  ), [timeline]);
  const jobPanelWorkOrders = React.useMemo<JobPanelWorkOrder[]>(() => {
    const seen = new Set<string>();
    const items: JobPanelWorkOrder[] = [];
    timeline.forEach((item) => {
      if (typeof item?.content !== 'string') {
        return;
      }
      const extracted = extractWorkOrderLinks(item.content, {
        date: formatWorkOrderDate(item.timestamp),
        startIndex: items.length + 1,
      });
      extracted.forEach((workOrder) => {
        if (seen.has(workOrder.url)) {
          return;
        }
        seen.add(workOrder.url);
        items.push({
          ...workOrder,
          index: items.length + 1,
        });
      });
    });
    return items;
  }, [timeline]);
  const runtimeTenantId = React.useMemo(() => (
    user?.agent_slug || slugifyRuntimeSegment(user?.agent_name || user?.distributor?.name, 'local')
  ), [user?.agent_name, user?.agent_slug, user?.distributor?.name]);
  const runtimeUserId = React.useMemo(() => (
    user?.id == null ? 'web' : String(user.id)
  ), [user?.id]);
  const runtimeSessionId = React.useMemo(() => (
    sessionId || 'web'
  ), [sessionId]);
  const savedJobPanelJobs = React.useMemo<JobPanelJob[]>(() => (
    savedAgentJobs.map((job) => {
      const photos = (job.photos || []).map((photo, index) => ({
        id: String(photo.id || `${job.id}-photo-${index}`),
        url: normalizeAgentArtifactUrl(String(photo.url || '')),
        filename: photo.filename || 'Job photo',
        timestamp: photo.created_at || null,
      })).filter(photo => photo.url);
      const subtitleParts = [
        job.customer_name,
        job.service_address,
        job.work_order_number,
      ].filter(Boolean);
      const workOrders = normalizeSavedWorkOrders(job.work_orders);
      const conversations = normalizeSavedJobConversations(job.sessions);
      return {
        id: `saved-${job.id}`,
        agentJobId: job.id,
        name: job.name || 'Saved job',
        subtitle: subtitleParts.length > 0 ? subtitleParts.join(' - ') : 'Saved job context.',
        updatedLabel: job.updated_at ? `Updated ${new Date(job.updated_at).toLocaleDateString()}` : 'Saved job',
        photos,
        workOrders,
        conversations,
        conversationCount: conversations.length,
        workOrderCount: workOrders.length,
        status: 'saved',
      };
    })
  ), [savedAgentJobs]);
  const jobPanelJobs = React.useMemo<JobPanelJob[]>(() => ([
    ...savedJobPanelJobs,
    {
      id: `session-${sessionId || 'current'}`,
      name: 'Job draft',
      subtitle: 'Current chat, photos, and work orders ready to save together.',
      updatedLabel: 'Current chat',
      photos: jobPanelPhotos,
      workOrders: jobPanelWorkOrders,
      conversations: timeline.length > 0 ? [{
        id: sessionId || 'current',
        sessionId,
        title: 'Current assistant chat',
        subtitle: `${timeline.length} messages`,
      }] : [],
      conversationCount: timeline.length > 0 ? 1 : 0,
      workOrderCount: jobPanelWorkOrders.length || jobPanelWorkOrderCount,
      status: 'unassigned',
    },
  ]), [jobPanelPhotos, jobPanelWorkOrderCount, jobPanelWorkOrders, savedJobPanelJobs, sessionId, timeline.length]);
  const selectedJobPanelJob = React.useMemo(() => (
    jobPanelJobs.find(job => job.id === selectedJobPanelId) || jobPanelJobs[0] || null
  ), [jobPanelJobs, selectedJobPanelId]);
  const filteredJobPanelJobs = React.useMemo(() => {
    const query = jobSearchQuery.trim().toLowerCase();
    return jobPanelJobs.filter((job) => {
      if (!query) {
        return true;
      }
      return `${job.name} ${job.subtitle} ${job.updatedLabel}`.toLowerCase().includes(query);
    });
  }, [jobPanelJobs, jobSearchQuery]);

  const refreshAgentJobs = useCallback(async (force = false) => {
    if (!sessionId) {
      setSavedAgentJobs([]);
      return;
    }
    setIsLoadingAgentJobs(true);
    try {
      const query = new URLSearchParams({
        tenant_id: runtimeTenantId,
        user_id: runtimeUserId,
        session_id: runtimeSessionId,
      });
      const jobsCacheKey = queryKeys.agentJobs(runtimeTenantId, runtimeUserId, runtimeSessionId);
      const payload = await queryCache.fetchQuery<any>(
        jobsCacheKey,
        async () => {
          const response = await fetch(`${getAgentArtifactBackendBaseUrl()}/api/jobs?${query.toString()}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          });
          if (!response.ok) {
            throw new Error(`Could not load jobs (${response.status})`);
          }
          return response.json();
        },
        { staleTimeMs: 60_000, force },
      );
      const jobs: SavedAgentJob[] = Array.isArray(payload?.jobs) ? payload.jobs : [];
      setSavedAgentJobs(jobs);

      const activeJob = jobs.find(job => job.is_active) || jobs[0] || null;
      if (activeJob) {
        const activePanelId = `saved-${activeJob.id}`;
        setSelectedJobPanelId(previous => {
          const previousStillExists = jobs.some(job => `saved-${job.id}` === previous);
          if (!previous || previous.startsWith('session-') || !previousStillExists) {
            return activePanelId;
          }
          return previous;
        });
      }
    } catch (error) {
      console.warn('[ChatSession] Could not load saved jobs:', error);
      setSavedAgentJobs([]);
    } finally {
      setIsLoadingAgentJobs(false);
    }
  }, [runtimeSessionId, runtimeTenantId, runtimeUserId, sessionId, token]);

  const uploadJobPhoto = useCallback(async (jobId: string, image: SelectedImage) => {
    if (!sessionId) {
      window.alert('Open a saved chat session before adding photos to a job.');
      return;
    }

    setIsSavingJobPhoto(true);
    try {
      const query = new URLSearchParams({
        tenant_id: runtimeTenantId,
        user_id: runtimeUserId,
        session_id: runtimeSessionId,
      });
      const formData = new FormData();
      formData.append('image', await fileFromSelectedImage(image));

      const response = await fetch(
        `${getAgentArtifactBackendBaseUrl()}/api/jobs/${encodeURIComponent(jobId)}/photos?${query.toString()}`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error(`Could not add photo (${response.status})`);
      }

      await refreshAgentJobs(true);
    } catch (error) {
      console.warn('[ChatSession] Could not add job photo:', error);
      window.alert('Could not add that photo to the job. Please try again.');
    } finally {
      setIsSavingJobPhoto(false);
      setJobPhotoTargetId(null);
    }
  }, [refreshAgentJobs, runtimeSessionId, runtimeTenantId, runtimeUserId, sessionId, token]);

  const deleteJobPhoto = useCallback(async (jobId: string, photoId: string) => {
    if (!sessionId) {
      return;
    }

    setDeletingJobPhotoId(photoId);
    try {
      const query = new URLSearchParams({
        tenant_id: runtimeTenantId,
        user_id: runtimeUserId,
        session_id: runtimeSessionId,
      });
      const response = await fetch(
        `${getAgentArtifactBackendBaseUrl()}/api/jobs/${encodeURIComponent(jobId)}/photos/${encodeURIComponent(photoId)}?${query.toString()}`,
        {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        },
      );

      if (!response.ok) {
        throw new Error(`Could not remove photo (${response.status})`);
      }

      await refreshAgentJobs(true);
    } catch (error) {
      console.warn('[ChatSession] Could not remove job photo:', error);
      window.alert('Could not remove that photo. Please try again.');
    } finally {
      setDeletingJobPhotoId(null);
    }
  }, [refreshAgentJobs, runtimeSessionId, runtimeTenantId, runtimeUserId, sessionId, token]);

  useEffect(() => {
    if (!showJobsModal) {
      return;
    }
    void refreshAgentJobs();
  }, [refreshAgentJobs, showJobsModal]);

  useEffect(() => {
    if (!showJobsModal || isLoadingAgentJobs || selectedJobPanelId) {
      return;
    }
    setSelectedJobPanelId(jobPanelJobs[0]?.id || null);
  }, [isLoadingAgentJobs, jobPanelJobs, selectedJobPanelId, showJobsModal]);
  // Ref to always have the latest escalation id for event handlers
  const currentEscalationIdRef = useRef<string | null>(null);

  // Helper to set both state and ref
  const setCurrentEscalationIdAndRef = useCallback((id: string | null) => {
    currentEscalationIdRef.current = id;
    setCurrentEscalationId(id);
  }, []);

  // Custom setter for escalation status that also updates the ref
  const setEscalationStatusWithRef = useCallback((status: 'none' | 'pending' | 'connected' | 'resolved') => {
    currentEscalationStatus.current = status;
    setEscalationStatus(status);
  }, []);
  const [agentName, setAgentName] = useState<string>('');
  const [activeEscalationId, setActiveEscalationId] = useState<string | null>(null);
  const [escalationResolvedAt, setEscalationResolvedAt] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(() => !initialTimelineSnapshot);
  const [showQuotaLimitAlert, setShowQuotaLimitAlert] = useState(false);
  const [quotaLimitMessage, setQuotaLimitMessage] = useState('Trial request limit reached. Please subscribe to continue.');
  const [hasActiveEscalation, setHasActiveEscalation] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [showDownvoteModal, setShowDownvoteModal] = useState(false);
  const [feedbackMessageId, setFeedbackMessageId] = useState<string | null>(null);
  const [selectedDownvoteReasons, setSelectedDownvoteReasons] = useState<string[]>([]);
  const [downvoteComment, setDownvoteComment] = useState('');
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [showCopyToast, setShowCopyToast] = useState(false);
  const [showFeedbackToast, setShowFeedbackToast] = useState(false);
  const [showSessionTitleToast, setShowSessionTitleToast] = useState(false);
  const [sessionTitleToastMessage, setSessionTitleToastMessage] = useState('Chat title updated');
  const [currentSessionTitle, setCurrentSessionTitle] = useState<string | null>(
    () => initialTimelineSnapshot?.data?.session?.title ?? null,
  );
  const [showSessionTitleModal, setShowSessionTitleModal] = useState(false);
  const [sessionTitleDraft, setSessionTitleDraft] = useState('');
  const [isSavingSessionTitle, setIsSavingSessionTitle] = useState(false);
  const [sessionTitleError, setSessionTitleError] = useState<string | null>(null);
  const [expandedSourceGroups, setExpandedSourceGroups] = useState<Record<string, boolean>>({});
  const [activeSourceTabs, setActiveSourceTabs] = useState<Record<string, SourceTabKey>>({});
  // Compute a tight initial breakpoint so the sheet height matches content rather
  // than leaving a large empty gap.  Content is ~520px; add 8px handle + 5% buffer.
  const downvoteBreakpoint = Math.min(1, Math.max(0.65, (528 / window.innerHeight) + 0.05));
  const abortControllerRef = useRef<AbortController | null>(null);
  const stopRequestedRef = useRef(false);
  const currentEscalationStatus = useRef<'none' | 'pending' | 'connected' | 'resolved'>('none');
  const [thinkingMessages, setThinkingMessages] = useState<string[]>(
    () => (initialLiveSnapshot?.thinkingMessages || []) as string[],
  );
  const [isThinking, setIsThinking] = useState(() => initialLiveSnapshot?.isThinking ?? false);
  const [isEscalating, setIsEscalating] = useState(() => initialLiveSnapshot?.isEscalating ?? false);
  const [isStreamingResponse, setIsStreamingResponse] = useState(
    () => initialLiveSnapshot?.isStreamingResponse ?? false,
  );
  const [isStoppingRun, setIsStoppingRun] = useState(
    () => initialLiveSnapshot?.isStoppingRun ?? false,
  );
  const [workStartedAt, setWorkStartedAt] = useState<number | null>(
    () => initialLiveSnapshot?.workStartedAt ?? null,
  );
  const [workClockTick, setWorkClockTick] = useState(0);
  const [messageEndSpacerHeight, setMessageEndSpacerHeight] = useState(DEFAULT_MESSAGE_END_SPACER_PX);
  const isAiRunActive = !hasActiveEscalation && (isSending || isThinking || isStreamingResponse);
  const isAiRunActiveRef = useRef(false);
  const activeAiRunStartedAtRef = useRef<number>(initialLiveSnapshot?.activeRunStartedAt ?? 0);
  const isAutoDispatchingQueuedDraftRef = useRef(false);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const skipNextTimelineAutoScrollRef = useRef(false);
  const pinnedSentMessageIdRef = useRef<string | null>(null);
  const pinnedSentMessageTimeoutsRef = useRef<number[]>([]);
  const shouldMaintainPinnedSentMessageRef = useRef(false);
  const wasAiRunActiveRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isInitializing = useRef(false);
  const initializationInProgressRef = useRef(false);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | number | null>(null);
  const refreshTimelineRef = useRef<((options?: {
    showLoading?: boolean;
    preserveScroll?: boolean;
    force?: boolean;
  }) => Promise<boolean>) | null>(null);
  const activeRequestStartedAtRef = useRef<number | null>(null);
  const artifactPanDragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(null);

  // Initialize markdown link handlers when timeline updates
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      initializeMarkdownLinkHandlers();
    }, 100);

    return () => clearTimeout(timer);
  }, [timeline]);

  useEffect(() => {
    const handleWorkOrderLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a') as HTMLAnchorElement | null;
      const href = link?.getAttribute('href') || '';
      if (!href || !isWorkOrderPdfUrl(href)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const absoluteUrl = normalizeWorkOrderPdfUrl(href);
      if (!absoluteUrl) {
        setSelectedWorkOrder({
          url: 'about:blank',
          title: 'Work order link unavailable',
        });
        setWorkOrderSignError('This work order link is missing its artifact ID. Ask the assistant to regenerate it.');
        return;
      }
      const signedState = readWorkOrderSignedState(absoluteUrl);
      setSelectedWorkOrder({
        url: absoluteUrl,
        title: (link?.textContent || 'Work order PDF').trim() || 'Work order PDF',
      });
      setCustomerSignature(null);
      setTechnicianSignature(null);
      setSignedWorkOrderUrl(signedState?.signedUrl || null);
      setWorkOrderSignStatus(signedState ? 'Already signed. You can open or email this signed PDF.' : null);
      setWorkOrderSignError(null);
      setIsEmailingWorkOrder(false);
      setIsSelectedWorkOrderSigned(Boolean(signedState));
    };
    document.addEventListener('click', handleWorkOrderLinkClick, true);
    return () => document.removeEventListener('click', handleWorkOrderLinkClick, true);
  }, []);

  useEffect(() => {
    const handleOemPageImageLinkClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest?.('a') as HTMLAnchorElement | null;
      const href = link?.getAttribute('href') || '';
      const imageUrl = href ? getOemPdfPageImageUrl(href) : null;
      if (!imageUrl) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      setSelectedArtifactImage({
        id: imageUrl,
        label: (link?.textContent || 'Manual page').trim() || 'Manual page',
        originalUrl: imageUrl,
        displayUrl: imageUrl,
      });
    };

    document.addEventListener('click', handleOemPageImageLinkClick, true);
    return () => document.removeEventListener('click', handleOemPageImageLinkClick, true);
  }, []);

  useEffect(() => {
    if (!selectedWorkOrderUrl || selectedWorkOrderUrl === 'about:blank') {
      setIsSelectedWorkOrderSigned(false);
      return;
    }

    let cancelled = false;
    const titleSuggestsSigned = /(^|[-_\s])signed(\.pdf)?$/i.test(selectedWorkOrder.title || '');
    const signedState = readWorkOrderSignedState(selectedWorkOrderUrl);
    if (signedState) {
      setSignedWorkOrderUrl(signedState.signedUrl);
      setIsSelectedWorkOrderSigned(true);
      setWorkOrderSignStatus('Already signed. You can open or email this signed PDF.');
    } else {
      setIsSelectedWorkOrderSigned(titleSuggestsSigned);
    }

    api.head(selectedWorkOrderUrl)
      .then((response) => {
        if (cancelled) return;
        const signedHeader = response.headers?.['x-work-order-signed'];
        const signedUrlHeader = response.headers?.['x-work-order-signed-url'];
        const signedUrl = signedUrlHeader ? normalizeWorkOrderPdfUrl(signedUrlHeader) : null;
        const disposition = response.headers?.['content-disposition'] || '';
        const headerSuggestsSigned = signedHeader === 'true' || /-signed\.pdf/i.test(disposition);
        if (signedUrl) {
          setSignedWorkOrderUrl(signedUrl);
          writeWorkOrderSignedState(selectedWorkOrderUrl, signedUrl);
        }
        const isSigned = headerSuggestsSigned || titleSuggestsSigned || Boolean(signedState) || Boolean(signedUrl);
        setIsSelectedWorkOrderSigned(isSigned);
        if (isSigned) {
          setWorkOrderSignStatus('Already signed. You can open or email this signed PDF.');
        }
      })
      .catch(() => {
        if (!cancelled && titleSuggestsSigned) {
          setWorkOrderSignStatus('Already signed. You can open or email this signed PDF.');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedWorkOrder?.title, selectedWorkOrderUrl]);

  const signSelectedWorkOrder = useCallback(async () => {
    if (!selectedWorkOrder) return;
    if (isSelectedWorkOrderSigned) {
      setWorkOrderSignStatus('Already signed. You can open or email this signed PDF.');
      return;
    }
    if (!customerSignature) {
      setWorkOrderSignError('Customer signature is required before finalizing.');
      return;
    }
    setIsSigningWorkOrder(true);
    setWorkOrderSignStatus(null);
    setWorkOrderSignError(null);
    try {
      const sourceUrl = new URL(selectedWorkOrderUrl || selectedWorkOrder.url, window.location.href);
      const workOrderId = workOrderIdFromUrl(sourceUrl.href);
      const payload = {
        ...(workOrderId ? { work_order_id: workOrderId } : {}),
        tenant_id: sourceUrl.searchParams.get('tenant_id') || 'local',
        user_id: sourceUrl.searchParams.get('user_id') || 'web',
        session_id: sourceUrl.searchParams.get('session_id') || 'web',
        artifact_id: sourceUrl.searchParams.get('artifact_id') || '',
        customer_signature: customerSignature,
        technician_signature: technicianSignature || '',
        signed_at: new Date().toLocaleString(),
      };
      const backendBaseUrl = getAgentArtifactBackendBaseUrl();
      const signUrl = new URL('/api/work-orders/sign', backendBaseUrl).href;
      const response = await api.post(signUrl, payload);
      const body = response.data;
      if (!body?.ok || !body?.render_url) {
        throw new Error(body?.error || 'Could not finalize signed work order.');
      }
      const nextUrl = normalizeWorkOrderPdfUrl(body.render_url) || new URL(body.render_url, backendBaseUrl).href;
      const cacheBustedUrl = addCacheBustToUrl(nextUrl);
      setSignedWorkOrderUrl(cacheBustedUrl);
      setIsSelectedWorkOrderSigned(true);
      if (selectedWorkOrderUrl) {
        writeWorkOrderSignedState(selectedWorkOrderUrl, cacheBustedUrl, { signedAt: String(payload.signed_at || '') });
      }
      setWorkOrderSignStatus('Signed PDF is ready.');
    } catch (error) {
      setWorkOrderSignError(error instanceof Error ? error.message : 'Could not finalize signed work order.');
    } finally {
      setIsSigningWorkOrder(false);
    }
  }, [customerSignature, isSelectedWorkOrderSigned, selectedWorkOrder, selectedWorkOrderUrl, technicianSignature]);

  const emailSignedWorkOrder = useCallback(async () => {
    const email = workOrderEmail.trim();
    const signedUrl = effectiveSignedWorkOrderUrl;
    if (!signedUrl) {
      setWorkOrderSignError('Finalize the signed PDF before emailing it.');
      return;
    }
    if (!email) {
      setWorkOrderSignError('Enter the customer email address first.');
      return;
    }

    setIsEmailingWorkOrder(true);
    setWorkOrderSignStatus(null);
    setWorkOrderSignError(null);
    try {
      await api.post('/chat/work-orders/email-signed', {
        recipient_email: email,
        work_order_url: signedUrl,
        work_order_title: selectedWorkOrder?.title || 'Signed work order PDF',
      });
      if (selectedWorkOrderUrl) {
        writeWorkOrderSignedState(selectedWorkOrderUrl, signedUrl, { emailedAt: new Date().toISOString() });
      }
      setWorkOrderSignStatus(`Signed work order sent to ${email}.`);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Could not send the signed work order email.';
      setWorkOrderSignError(message);
    } finally {
      setIsEmailingWorkOrder(false);
    }
  }, [effectiveSignedWorkOrderUrl, selectedWorkOrder?.title, selectedWorkOrderUrl, workOrderEmail]);

  // Streaming state
  const streamingMessageIdRef = useRef<string | null>(
    initialLiveSnapshot?.streamingMessageId ?? null,
  );
  const streamingAccumulatedRef = useRef<string>(
    initialLiveSnapshot?.streamingAccumulated ?? '',
  );
  const pendingAdaptiveTraceRef = useRef<AdaptiveTraceStep[]>(
    initialLiveSnapshot?.pendingAdaptiveTrace || [],
  );
  const awaitingServerMessageIdRef = useRef<string | null>(
    initialLiveSnapshot?.awaitingServerMessageId ?? null,
  );
  const clearAiLoadingState = useCallback((removeEmptyStreamingMessage: boolean = false) => {
    const activeStreamingId = streamingMessageIdRef.current;
    const activeStreamingContent = streamingAccumulatedRef.current;

    setIsSending(false);
    setIsEscalating(false);
    setIsThinking(false);
    setThinkingMessages([]);
    setWorkStartedAt(null);
    setWorkClockTick(0);
    setIsStreamingResponse(false);
    setIsStoppingRun(false);

    if (removeEmptyStreamingMessage && activeStreamingId && (!activeStreamingContent || !activeStreamingContent.trim())) {
      setTimeline(prev => prev.filter(msg => msg.id !== activeStreamingId));
    }

    streamingMessageIdRef.current = null;
    streamingAccumulatedRef.current = '';
    pendingAdaptiveTraceRef.current = [];
    activeAiRunStartedAtRef.current = 0;
    activeRequestStartedAtRef.current = null;
    if ((window as any).__chatReconcileIntervalId) {
      clearInterval((window as any).__chatReconcileIntervalId);
      delete (window as any).__chatReconcileIntervalId;
    }
    if ((window as any).__chatTimeoutId) {
      clearTimeout((window as any).__chatTimeoutId);
      delete (window as any).__chatTimeoutId;
    }
  }, []);

  // Decode HTML entities (e.g., &nbsp;) to their character equivalents
  const decodeHtmlEntities = useCallback((input: string): string => {
    if (!input) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = input;
    return textarea.value;
  }, []);

  const pushLiveEvidenceStep = useCallback((label: string, eventName?: string) => {
    const normalized = isRawRuntimeNarrationEvent(eventName)
      ? String(label || '').trim()
      : sanitizeLiveWorkLabel(label);
    if (!normalized || hasActiveEscalation) {
      return;
    }
    const encodedEntry = encodeRuntimeStreamEntry(eventName, normalized);
    setWorkStartedAt(prev => prev ?? Date.now());
    setIsThinking(true);
    setIsSending(false);
    setThinkingMessages(prev => {
      if (prev.includes(encodedEntry)) {
        return prev;
      }
      return [...prev, encodedEntry].slice(-RUNTIME_WORK_ENTRY_BUFFER_LIMIT);
    });
  }, [hasActiveEscalation]);

  useEffect(() => {
    const isWorking = !hasActiveEscalation && (isThinking || isSending || isEscalating) && !isStreamingResponse;
    if (!isWorking) {
      if (workStartedAt !== null) {
        setWorkStartedAt(null);
        setWorkClockTick(0);
      }
      return;
    }
    if (workStartedAt === null) {
      setWorkStartedAt(Date.now());
    }
    const intervalId = window.setInterval(() => {
      setWorkClockTick(tick => tick + 1);
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [hasActiveEscalation, isEscalating, isSending, isStreamingResponse, isThinking, workStartedAt]);

  const parseAdaptiveTraceSteps = useCallback((message: any): AdaptiveTraceStep[] => {
    const rawSteps = Array.isArray(message?.steps) ? message.steps : (() => {
      try {
        const parsed = message?.content ? JSON.parse(message.content) : {};
        return Array.isArray(parsed?.steps) ? parsed.steps : [];
      } catch (_error) {
        return [];
      }
    })();
    return rawSteps
      .map((step: any) => ({
        label: String(step?.label || '').trim(),
        action: String(step?.action || '').trim(),
        status: String(step?.status || '').trim(),
        quality: String(step?.quality || '').trim(),
        quality_label: String(step?.quality_label || '').trim(),
        scope_label: String(step?.scope_label || '').trim(),
        expectation: String(step?.expectation || '').trim(),
        observation: String(step?.observation || '').trim(),
        evaluation: String(step?.evaluation || '').trim(),
        prediction_error: String(step?.prediction_error || '').trim(),
        next_action: String(step?.next_action || '').trim(),
        next_step: String(step?.next_step || '').trim(),
      }))
      .filter((step: AdaptiveTraceStep) => step.label);
  }, []);

  const getLiveEvidenceStepLabel = useCallback((message: any): string => {
    const directStep: Partial<AdaptiveTraceStep> = {
      label: String(message?.label || '').trim(),
      action: String(message?.action || '').trim(),
      status: String(message?.status || '').trim(),
      prediction_error: String(message?.prediction_error || '').trim(),
    };
    const directLabel = publicAdaptiveStepLabel(directStep);
    if (directLabel) {
      return directLabel;
    }
    try {
      const parsed = message?.content ? JSON.parse(message.content) : {};
      return publicAdaptiveStepLabel({
        label: String(parsed?.label || '').trim(),
        action: String(parsed?.action || '').trim(),
        status: String(parsed?.status || '').trim(),
        prediction_error: String(parsed?.prediction_error || '').trim(),
      });
    } catch (_error) {
      return sanitizeLiveWorkLabel(message?.content);
    }
  }, []);

  const getToolStatusLabel = useCallback((event: string, message: any): string => {
    const toolName = String(message?.tool_name || '').trim();
    const agentName = String(message?.agent_name || '').trim();
    const eventContent = sanitizeLiveWorkLabel(message?.content);
    if ((event === 'tool_start' || event === 'tool_end') && eventContent && !isRawRuntimeTelemetry(eventContent)) {
      return eventContent;
    }
    const toolLabels: Record<string, string> = {
      inspect_oem_graph: 'Checking matching product families.',
      read_oem_graph_node: 'Reading product-family details.',
      search_oem_graph_node_documents: 'Searching scoped OEM manuals.',
      search_oem_artifacts: 'Checking extracted tables and diagrams.',
      ensure_oem_artifacts: 'Preparing missing wiring diagrams from the source manual.',
      read_oem_artifact_context: 'Reading the selected table or diagram context.',
      search_oem_exact_tokens: 'Checking exact model, code, connector, or value tokens.',
      verify_oem_visual_artifact: 'Verifying the selected diagram visually.',
      search_oem_graph_manuals: 'Searching OEM manuals for supporting evidence.',
      start_parts_lookup: 'Checking the parts catalog.',
      continue_parts_lookup: 'Continuing the parts lookup.',
      carrier_parts_lookup: 'Checking the parts catalog.',
      technical_support_request: 'Preparing technical support handoff.',
      profile_memory: 'Checking saved contact details.',
      work_order: 'Preparing work order.',
      smartdocs_oem_evidence: 'Searching manual evidence.',
      oem_spaces: 'Searching and downloading manuals.',
      pdf_text: 'Reading the manual text.',
      oem_wiring_lookup: 'Looking for wiring diagrams.',
      image_to_text: 'Reading text from a rendered page.',
      image_generation: 'Creating the room mockup.',
      skill: 'Running a SmartDocs workflow.',
      tool_search: 'Checking available SmartDocs tools.',
    };
    if (event === 'agent_handoff' && agentName) {
      return `Switched to ${agentName}.`;
    }
    if (!toolName) {
      return '';
    }
    const description = toolLabels[toolName] || 'Checking another source.';
    const isCompleteEvent = event === 'tool_complete' || event === 'tool_end';
    const isError = Boolean(message?.is_error);
    if (isCompleteEvent && isError) {
      return `${description.replace(/\.$/, '')} finished with an error.`;
    }
    return isCompleteEvent
      ? description.replace(/^Checking /, 'Checked ').replace(/^Searching /, 'Searched ').replace(/^Reading /, 'Read ').replace(/^Looking /, 'Looked ')
      : description;
  }, []);

  const liveWorkEntries = React.useMemo<LiveWorkEntry[]>(
    () => thinkingMessages.map(entry => {
      const decodedEntry = decodeRuntimeStreamEntry(entry);
      if (decodedEntry.eventName.startsWith('codex_event:')) {
        const display = codexEventDisplay({
          eventName: decodedEntry.eventName,
          label: decodedEntry.label,
          kind: 'codex-muted',
        });
        if (display) {
          return {
            eventName: decodedEntry.eventName,
            label: display.label,
            kind: display.kind,
            detail: display.detail,
            hidden: display.hidden,
          };
        }
      }
      const sanitizedLabel = sanitizeLiveWorkLabel(decodedEntry.label);
      return {
        eventName: decodedEntry.eventName,
        label: sanitizedLabel,
        kind: classifyLiveWorkEntry(sanitizedLabel),
      };
    }).filter(entry => entry.label && !entry.hidden),
    [thinkingMessages],
  );
  const visibleWorkEntries = React.useMemo(
    () => liveWorkEntries.filter(isCustomerRuntimeNarration),
    [liveWorkEntries],
  );
  const interleavedRuntimeEntries = React.useMemo(
    () => liveWorkEntries.filter(entry => (
      !isBoilerplateRuntimeEntry(entry) &&
      (isCustomerRuntimeNarration(entry) || isInternalRuntimeEntry(entry))
    )),
    [liveWorkEntries],
  );
  const liveWorkElapsed = React.useMemo(() => {
    void workClockTick;
    return formatWorkElapsed(workStartedAt);
  }, [workClockTick, workStartedAt]);
  const liveWorkElapsedSeconds = React.useMemo(() => {
    void workClockTick;
    if (!workStartedAt) {
      return 0;
    }
    return Math.max(0, Math.floor((Date.now() - workStartedAt) / 1000));
  }, [workClockTick, workStartedAt]);
  const isRuntimeWorking = !hasActiveEscalation && (isThinking || isSending || isEscalating) && !isStreamingResponse;
  const shouldShowDeadAirFallback =
    isRuntimeWorking &&
    visibleWorkEntries.length === 0 &&
    liveWorkElapsedSeconds >= RUNTIME_DEAD_AIR_FALLBACK_SECONDS;
  const runtimeDisplayItems = React.useMemo(
    () => groupRuntimeCommandEntries(
      shouldShowDeadAirFallback
        ? [{ eventName: 'runtime_dead_air', label: 'Thinking...', kind: 'thinking' as const }]
        : interleavedRuntimeEntries,
    ).slice(-12),
    [interleavedRuntimeEntries, shouldShowDeadAirFallback],
  );

  const formatBytes = useCallback((bytes: number): string => {
    if (!bytes || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / (1024 ** i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }, []);

  const shouldHideTimelineText = useCallback((item: any) => {
    return Boolean(item?.has_image) && (!item?.content || item.content.trim() === '' || item.content.trim() === IMAGE_ONLY_PLACEHOLDER);
  }, []);

  useEffect(() => {
    if (!queuedDraft) {
      return;
    }

    if (timeline.some(item => doesMessageMatchQueuedDraft(item, queuedDraft))) {
      setQueuedDraft(null);
      setIsQueueSubmitting(false);
    }
  }, [queuedDraft, timeline]);

  useEffect(() => {
    if (selectedArtifactImage) {
      setArtifactImageZoom(1);
      setArtifactImageRotation(0);
      setArtifactImagePan({ x: 0, y: 0 });
      artifactPanDragRef.current = null;
    }
  }, [selectedArtifactImage]);

  const zoomArtifactImage = useCallback((delta: number) => {
    setArtifactImageZoom(value => Math.min(4, Math.max(0.5, Number((value + delta).toFixed(2)))));
  }, []);

  const resetArtifactImageView = useCallback(() => {
    setArtifactImageZoom(1);
    setArtifactImageRotation(0);
    setArtifactImagePan({ x: 0, y: 0 });
    artifactPanDragRef.current = null;
  }, []);

  const handleArtifactViewerPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!selectedArtifactImage) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    artifactPanDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: artifactImagePan.x,
      originY: artifactImagePan.y,
    };
  }, [artifactImagePan.x, artifactImagePan.y, selectedArtifactImage]);

  const handleArtifactViewerPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = artifactPanDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) {
      return;
    }
    setArtifactImagePan({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    });
  }, []);

  const handleArtifactViewerPointerEnd = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = artifactPanDragRef.current;
    if (drag?.pointerId === event.pointerId) {
      artifactPanDragRef.current = null;
    }
  }, []);

  const handleArtifactViewerWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    zoomArtifactImage(event.deltaY < 0 ? 0.15 : -0.15);
  }, [zoomArtifactImage]);

  const handleOpenJobPhoto = useCallback((photo: JobPanelPhoto) => {
    const imageItem: ArtifactImagePreviewItem = {
      id: photo.id,
      label: photo.filename || 'Job photo',
      originalUrl: photo.url,
      displayUrl: normalizeAgentArtifactUrl(photo.url),
    };

    setShowJobsModal(false);
    setSelectedArtifactImage(null);
    window.setTimeout(() => {
      setSelectedArtifactImage(imageItem);
    }, 80);
  }, []);

  const handleImageSelection = useCallback(async (file: File) => {
    try {
      const selected = await readImageFile(file);
      if (jobPhotoTargetId) {
        await uploadJobPhoto(jobPhotoTargetId, selected);
        return;
      }
      setSelectedImage(selected);
    } catch (error) {
      const imageError = getImageSelectionErrorMessage(error);
      setIsSending(false);
      setIsThinking(false);
      setThinkingMessages([]);
      setTimeout(() => window.alert(imageError), 0);
    }
  }, [jobPhotoTargetId, uploadJobPhoto]);

  const handleNativeImageSelection = useCallback(async (source: CameraSource) => {
    try {
      const photo = await Camera.getPhoto({
        allowEditing: false,
        correctOrientation: true,
        quality: 80,
        resultType: CameraResultType.DataUrl,
        source,
      });

      const selected = buildSelectedImageFromCameraPhoto(photo);
      if (jobPhotoTargetId) {
        await uploadJobPhoto(jobPhotoTargetId, selected);
        return;
      }
      setSelectedImage(selected);
    } catch (error) {
      if (isCameraCancellationError(error)) {
        setJobPhotoTargetId(null);
        return;
      }

      const imageError = getImageSelectionErrorMessage(error);
      setIsSending(false);
      setIsThinking(false);
      setThinkingMessages([]);
      setTimeout(() => window.alert(imageError), 0);
    }
  }, [jobPhotoTargetId, uploadJobPhoto]);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await handleImageSelection(file);
    e.target.value = '';
  }, [handleImageSelection]);

  const handlePasteImage = useCallback(async (e: React.ClipboardEvent<HTMLIonTextareaElement>) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imageItem = items.find((item) => item.type.startsWith('image/'));
    if (!imageItem) return;
    const file = imageItem.getAsFile();
    if (!file) return;
    e.preventDefault();
    await handleImageSelection(file);
  }, [handleImageSelection]);

  const handleAttachClick = useCallback(() => {
    setJobPhotoTargetId(null);
    setShowAttachActionSheet(true);
  }, []);

  const handleJobsPanelOpen = useCallback(() => {
    setSelectedJobPanelId(null);
    setJobsViewMode('list');
    setShowJobsModal(true);
    void refreshAgentJobs();
  }, [refreshAgentJobs]);

  const handleSelectJobFromList = useCallback((jobId: string) => {
    setSelectedJobPanelId(jobId);
    setJobsViewMode('detail');
  }, []);

  const handleOpenJobConversation = useCallback((conversation: JobPanelConversation) => {
    const targetSessionId = String(conversation.sessionId || '').trim();
    if (!targetSessionId) {
      return;
    }
    setShowJobsModal(false);
    history.push(`/chat/${targetSessionId}`);
  }, [history]);

  const handleCreateJobFromChat = useCallback(() => {
    setShowJobsModal(false);
    setMessage('/job create a job from this conversation ');
    setTimeout(() => {
      void messageInputRef.current?.setFocus?.();
    }, 0);
  }, []);

  const handleAddJobPhotoFromPanel = useCallback(() => {
    if (selectedJobPanelJob?.agentJobId) {
      setJobPhotoTargetId(selectedJobPanelJob.agentJobId);
    } else {
      setJobPhotoTargetId(null);
    }
    setShowAttachActionSheet(true);
  }, [selectedJobPanelJob?.agentJobId]);

  const handleRemoveJobPhotoFromPanel = useCallback((event: React.MouseEvent<HTMLButtonElement>, photo: JobPanelPhoto) => {
    event.stopPropagation();
    if (!selectedJobPanelJob?.agentJobId) {
      return;
    }
    const confirmed = window.confirm('Remove this photo from the job?');
    if (!confirmed) {
      return;
    }
    void deleteJobPhoto(selectedJobPanelJob.agentJobId, photo.id);
  }, [deleteJobPhoto, selectedJobPanelJob?.agentJobId]);

  const handleOpenWorkOrderResult = useCallback((item: WorkOrderResultItem) => {
    setSelectedWorkOrder({
      title: item.number,
      url: item.url,
    });
    setCustomerSignature(null);
    setTechnicianSignature(null);
    setSignedWorkOrderUrl(null);
    setWorkOrderSignStatus(null);
    setWorkOrderSignError(null);
    setIsEmailingWorkOrder(false);
    setIsSelectedWorkOrderSigned(item.signed);
  }, []);

  const handleFindWorkOrdersFromPanel = useCallback(() => {
    setShowJobsModal(false);
    setMessage('/get-work-order ');
    setTimeout(() => {
      void messageInputRef.current?.setFocus?.();
    }, 0);
  }, []);

  // Manual keyboard height calculation - commented out since Ionic now handles it properly
  // const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  // const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);

  // Socket-related state
  const socketRef = useRef<any>(null);
  const socketInitialized = useRef(false);
  const sessionUnavailableRef = useRef(false);

  const redirectToChatList = useCallback(() => {
    if (sessionUnavailableRef.current) {
      return;
    }
    sessionUnavailableRef.current = true;
    refreshTimelineRef.current = null;
    if (socketRef.current) {
      socketRef.current.removeAllListeners?.();
      socketRef.current.disconnect?.();
      socketRef.current = null;
    }
    socketInitialized.current = false;
    clearAiLoadingState(true);
    history.replace('/');
  }, [clearAiLoadingState, history]);

  useEffect(() => {
    sessionUnavailableRef.current = false;
  }, [sessionId]);

  // Track if this is the initial load for instant scroll
  const isInitialLoadRef = useRef(true);
  const shouldAutoScrollRef = useRef(true);

  // WebSocket URL is imported from api service

  // Initialize Capacitor Keyboard API
  useEffect(() => {
    const initializeKeyboard = async () => {
        if (Capacitor.isNativePlatform()) {
          try {
            // Hide accessory bar on iOS to prevent extra row with arrows and "Done" button
            if (Capacitor.getPlatform() === 'ios') {
              await Keyboard.setAccessoryBarVisible({ isVisible: false });
            }
          } catch (error) {
            console.error('[ChatSession] Error initializing Keyboard API:', error);
          }
        }
    };

    initializeKeyboard();
  }, []);

  // Keyboard event listeners for iOS
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
      return;
    }

    // Manual keyboard height calculation - commented out since Ionic now handles it properly
    const handleKeyboardWillShow = (_info: any) => {
      setIsKeyboardOpen(true);
      // setKeyboardHeight(height);

      // Set CSS variable for keyboard height - commented out since Ionic handles it
      // document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
    };

        // Manual keyboard height calculation - commented out since Ionic now handles it properly
        const handleKeyboardWillHide = () => {
      setIsKeyboardOpen(false);
      // setKeyboardHeight(0);

      // Add a small delay before removing CSS variable to prevent layout shift - commented out since Ionic handles it
      // setTimeout(() => {
      //   document.documentElement.style.removeProperty('--keyboard-height');
      // }, 100);
    };

    // Add event listeners
    Keyboard.addListener('keyboardWillShow', handleKeyboardWillShow);
    Keyboard.addListener('keyboardWillHide', handleKeyboardWillHide);

    return () => {
      // Remove event listeners
      Keyboard.removeAllListeners();
    };
  }, []);

  // App lifecycle management for socket reconnection
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleAppResume = async () => {
      if (socketRef.current && !socketRef.current.connected) {
        try {
          socketRef.current.connect();
        } catch (error) {
          console.error('[ChatSession] Error reconnecting socket:', error);
        }
      }
    };

    const removeListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        handleAppResume();
      }
    });

    return () => {
      removeListener.remove();
    };
  }, []);

  // Scroll to bottom function
  const scrollToBottom = useCallback((smooth: boolean = true) => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTo({
        top: messageContainerRef.current.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
      return;
    }
    if (ionContentRef.current) {
      void ionContentRef.current.scrollToBottom(smooth ? 220 : 0);
      return;
    }
    if (messagesEndRef.current) {
      // Scroll to bottom with a small delay to ensure DOM is updated
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({
          behavior: smooth ? 'smooth' : 'auto',
          block: 'end'
        });
      });
    } else if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  const scheduleScrollToBottom = useCallback((smooth: boolean = true) => {
    const delays = [0, 80, 220];
    delays.forEach((delay, index) => {
      window.setTimeout(() => {
        scrollToBottom(smooth && index === 0);
      }, delay);
    });
  }, [scrollToBottom]);

  const clearPinnedSentMessageTimeouts = useCallback(() => {
    pinnedSentMessageTimeoutsRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    pinnedSentMessageTimeoutsRef.current = [];
  }, []);

  const resetMessageEndSpacerHeight = useCallback(() => {
    setMessageEndSpacerHeight(DEFAULT_MESSAGE_END_SPACER_PX);
  }, []);

  useEffect(() => {
    isAiRunActiveRef.current = isAiRunActive;
  }, [isAiRunActive]);

  // Server history remains in the query cache. Only locally-created bubbles and
  // partial streams are kept in this separate, memory-only snapshot.
  useEffect(() => {
    if (!sessionId || hydratedSessionId !== sessionId) return;
    setChatSessionLiveSnapshot(sessionId, {
      isSending,
      isThinking,
      isEscalating,
      isStreamingResponse,
      isStoppingRun,
      workStartedAt,
      activeRunStartedAt: activeAiRunStartedAtRef.current,
      streamingMessageId: streamingMessageIdRef.current,
      streamingAccumulated: streamingAccumulatedRef.current,
      thinkingMessages,
      pendingAdaptiveTrace: pendingAdaptiveTraceRef.current,
      queuedDraft,
      pendingTimelineItems: getTransientTimelineItems(timeline),
      awaitingServerMessageId: awaitingServerMessageIdRef.current,
    });
  }, [
    isEscalating,
    isSending,
    isStoppingRun,
    isStreamingResponse,
    isThinking,
    hydratedSessionId,
    queuedDraft,
    sessionId,
    thinkingMessages,
    timeline,
    workStartedAt,
  ]);

  const restoreScrollAfterTimelineRefresh = useCallback((previousScrollTop: number, previousScrollHeight: number) => {
    requestAnimationFrame(() => {
      const container = messageContainerRef.current || chatContainerRef.current;
      if (!container) {
        return;
      }
      const heightDelta = container.scrollHeight - previousScrollHeight;
      container.scrollTop = Math.max(0, previousScrollTop + heightDelta);
    });
  }, []);
  const scrollMessageToTop = useCallback(async (messageId: string, smooth: boolean = false) => {
    const target = document.querySelector<HTMLElement>(`[data-chat-message-id="${messageId}"]`);
    if (!target) {
      return false;
    }

    const topOffset = 16;
    if (messageContainerRef.current) {
      const containerRect = messageContainerRef.current.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = targetRect.top - containerRect.top + messageContainerRef.current.scrollTop - topOffset;
      messageContainerRef.current.scrollTo({
        top: Math.max(0, targetTop),
        behavior: smooth ? 'smooth' : 'auto',
      });
      return true;
    }

    if (ionContentRef.current) {
      const scrollElement = await ionContentRef.current.getScrollElement();
      const targetRect = target.getBoundingClientRect();
      const scrollRect = scrollElement.getBoundingClientRect();
      const targetTop = targetRect.top - scrollRect.top + scrollElement.scrollTop - topOffset;
      await ionContentRef.current.scrollToPoint(0, Math.max(0, targetTop), smooth ? 220 : 0);
      return true;
    }

    return false;
  }, []);

  const keepPinnedSentMessageVisible = useCallback((smooth: boolean = false) => {
    const messageId = pinnedSentMessageIdRef.current;
    if (!messageId || !shouldMaintainPinnedSentMessageRef.current) {
      return false;
    }

    clearPinnedSentMessageTimeouts();
    setMessageEndSpacerHeight(PINNED_MESSAGE_END_SPACER_PX);
    const attempts = [0, 80, 220];
    attempts.forEach(delay => {
      const timeoutId = window.setTimeout(() => {
        void scrollMessageToTop(messageId, smooth && delay === 0);
      }, delay);
      pinnedSentMessageTimeoutsRef.current.push(timeoutId);
    });
    return true;
  }, [clearPinnedSentMessageTimeouts, scrollMessageToTop]);

  const isNearBottom = useCallback(() => {
    const container = messageContainerRef.current || chatContainerRef.current;
    if (!container) {
      return true;
    }
    return container.scrollHeight - container.scrollTop - container.clientHeight < 180;
  }, []);

  const handleChatScroll = useCallback((event: CustomEvent) => {
    const detail = event.detail as {
      scrollTop?: number;
      scrollHeight?: number;
      clientHeight?: number;
    };
    if (
      typeof detail?.scrollTop === 'number'
      && typeof detail?.scrollHeight === 'number'
      && typeof detail?.clientHeight === 'number'
    ) {
      shouldAutoScrollRef.current = detail.scrollHeight - detail.scrollTop - detail.clientHeight < 180;
      if (!shouldAutoScrollRef.current && pinnedSentMessageIdRef.current) {
        shouldMaintainPinnedSentMessageRef.current = false;
        clearPinnedSentMessageTimeouts();
      }
    }
  }, [clearPinnedSentMessageTimeouts]);

  useEffect(() => {
    const container = messageContainerRef.current || chatContainerRef.current;
    if (!container) {
      return;
    }
    const updateAutoScroll = () => {
      shouldAutoScrollRef.current = isNearBottom();
      if (!shouldAutoScrollRef.current && pinnedSentMessageIdRef.current) {
        shouldMaintainPinnedSentMessageRef.current = false;
        clearPinnedSentMessageTimeouts();
      }
    };
    container.addEventListener('scroll', updateAutoScroll, { passive: true });
    updateAutoScroll();
    return () => {
      container.removeEventListener('scroll', updateAutoScroll);
    };
  }, [clearPinnedSentMessageTimeouts, isNearBottom]);


  // Auto-scroll when component mounts or messages change
  useEffect(() => {
    if (pinnedSentMessageIdRef.current && isAiRunActive) {
      skipNextTimelineAutoScrollRef.current = true;
      keepPinnedSentMessageVisible(false);
      return;
    }

    if (skipNextTimelineAutoScrollRef.current) {
      skipNextTimelineAutoScrollRef.current = false;
      return;
    }

    // Use instant scroll on initial load, smooth scroll for updates
    if (isInitialLoadRef.current && timeline.length > 0) {
      scheduleScrollToBottom(false); // Instant scroll on first load with layout retries
      isInitialLoadRef.current = false;
      shouldAutoScrollRef.current = true;
    } else if (!isInitialLoadRef.current && shouldAutoScrollRef.current) {
      scheduleScrollToBottom(true); // Smooth scroll for subsequent updates
    }
  }, [isAiRunActive, keepPinnedSentMessageVisible, scheduleScrollToBottom, timeline]);


  // Note: thinkingContainerRef removed - no longer using ThinkingContainer component

  // Add a new effect to handle scroll after loading history
  useEffect(() => {
    if (pinnedSentMessageIdRef.current && isAiRunActive) {
      keepPinnedSentMessageVisible(false);
      return;
    }

    if (!isLoadingHistory && timeline.length > 0) {
      // Use instant scroll when history finishes loading on initial load
      if (isInitialLoadRef.current || shouldAutoScrollRef.current) {
        scheduleScrollToBottom(!isInitialLoadRef.current);
      }
    }
  }, [isAiRunActive, isLoadingHistory, keepPinnedSentMessageVisible, scheduleScrollToBottom, timeline.length]);

  useEffect(() => {
    if (!isAiRunActive) {
      if (wasAiRunActiveRef.current) {
        clearPinnedSentMessageTimeouts();
        pinnedSentMessageIdRef.current = null;
        shouldMaintainPinnedSentMessageRef.current = false;
        resetMessageEndSpacerHeight();
      }
      wasAiRunActiveRef.current = false;
      return;
    }

    wasAiRunActiveRef.current = true;
  }, [clearPinnedSentMessageTimeouts, isAiRunActive, resetMessageEndSpacerHeight]);

  useEffect(() => {
    if (!pinnedSentMessageIdRef.current || !isAiRunActive || !shouldMaintainPinnedSentMessageRef.current) {
      return;
    }
    keepPinnedSentMessageVisible(false);
  }, [
    isAiRunActive,
    keepPinnedSentMessageVisible,
    liveWorkElapsedSeconds,
    messageEndSpacerHeight,
    runtimeDisplayItems.length,
  ]);

  useEffect(() => () => {
    clearPinnedSentMessageTimeouts();
    resetMessageEndSpacerHeight();
  }, [clearPinnedSentMessageTimeouts, resetMessageEndSpacerHeight]);


  // Get contractor name from user
  const getContractorName = useCallback(() => {
    // First try to use user.name if available
    if (user?.name && user.name.trim()) {
      return user.name.trim();
    }

    // Fallback to email splitting if name is not available
    if (user?.email) {
      // Get the part before @ in the email or just use the email
      return user.email.split('@')[0] || user.email;
    }

    return "Contractor";
  }, [user]);

  // Get distributor name
  const getDistributorName = useCallback(() => {
    return user?.distributor?.name || user?.distributor_name || DEFAULT_DISTRIBUTOR;
  }, [user?.distributor?.name, user?.distributor_name]);

  const appendAssistantMessage = useCallback((content: string) => {
    setTimeline(prev => [...prev, {
      id: Date.now().toString(),
      type: 'message',
      content,
      sender: 'assistant',
      timestamp: new Date().toISOString(),
      isTransient: true,
      escalation_context: {
        escalation_id: currentEscalationIdRef.current,
        is_during_escalation: hasActiveEscalation,
        escalation_phase: hasActiveEscalation ? 'during' : 'before'
      }
    }]);
  }, [hasActiveEscalation]);

  const stripLegacyReferenceSections = useCallback((content: string) => {
    return (content || '')
      .replace(/<h3[^>]*>.*?(?:Reference Documents?|Sources I checked):.*?<\/h3>\s*<ul>.*?<\/ul>/gis, '')
      .trim();
  }, []);

  const getDisplayContent = useCallback((content: string, references?: ReferenceDocumentItem[]) => {
    if (references && references.length > 0) {
      return stripLegacyReferenceSections(content);
    }
    return content;
  }, [stripLegacyReferenceSections]);

  const openReferenceDocument = useCallback(async (reference: ReferenceDocumentItem) => {
    try {
      if (reference.url) {
        await openUrl(reference.url);
        return;
      }
      const response = reference.managed_document_id
        ? await api.get(`/chat/reference-documents/${reference.managed_document_id}/presigned-url`)
        : await api.post('/s3/presigned-url', {
            filename: reference.storage_object_key,
            bucket: reference.storage_bucket,
          });
      const presignedUrl = response?.data?.data?.presigned_url;
      const legacyPresignedUrl = response?.data?.presigned_url;
      if (!presignedUrl && !legacyPresignedUrl) {
        throw new Error('Missing presigned URL');
      }
      await openUrl(presignedUrl || legacyPresignedUrl);
    } catch (error) {
      console.error('[ChatSession] Failed to open reference document:', error);
      appendAssistantMessage('I could not open that reference document right now.');
    }
  }, [appendAssistantMessage]);

  const formatSourceTitle = useCallback((reference: ReferenceDocumentItem, fallback: string) => {
    const rawTitle = reference.filename || reference.storage_object_key || fallback;
    const filename = rawTitle.split(/[\\/]/).pop() || rawTitle;
    return filename.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_-]+/g, ' ').trim() || fallback;
  }, []);

  const renderDocumentThumbnail = useCallback((title: string) => (
    <ReferenceDocumentThumbnail aria-hidden="true">
      <ReferenceDocumentThumbnailHeader>{title.slice(0, 10) || 'Doc'}</ReferenceDocumentThumbnailHeader>
      <ReferenceDocumentThumbnailLine />
      <ReferenceDocumentThumbnailLine />
      <ReferenceDocumentThumbnailLine $short />
      <ReferenceDocumentThumbnailTable>
        <span />
        <span />
        <span />
        <span />
      </ReferenceDocumentThumbnailTable>
    </ReferenceDocumentThumbnail>
  ), []);

  const getReferenceSearchText = useCallback((reference: ReferenceDocumentItem) => [
    reference.manual_type,
    reference.category,
    reference.subcategory,
    reference.family,
    reference.filename,
    reference.storage_object_key,
  ].filter(Boolean).join(' ').toLowerCase(), []);

  const getManualTypeLabel = useCallback((reference: ReferenceDocumentItem) => {
    if (reference.manual_type) {
      return reference.manual_type;
    }
    const text = getReferenceSearchText(reference);
    if (/\b(service|diagnostic|troubleshoot|repair)\b/.test(text)) return 'Service Manual';
    if (/\b(install|installation|startup|start up)\b/.test(text)) return 'Installation Guide';
    if (/\b(engineering|submittal|spec|data|performance)\b/.test(text)) return 'Engineering Guide';
    if (/\b(wiring|diagram)\b/.test(text)) return 'Wiring Diagram';
    return 'Document';
  }, [getReferenceSearchText]);

  const getSourceTabKey = useCallback((reference: ReferenceDocumentItem): SourceTabKey | 'other' => {
    const text = getReferenceSearchText(reference);
    if (/\b(service|diagnostic|troubleshoot|repair)\b/.test(text)) return 'service';
    if (/\b(install|installation|startup|start up)\b/.test(text)) return 'install';
    if (/\b(engineering|submittal|spec|data|performance)\b/.test(text)) return 'engineering';
    return 'other';
  }, [getReferenceSearchText]);

  const getReferenceScore = useCallback((reference: ReferenceDocumentItem): number | null => {
    const rawScore = reference.relevance_score;
    const score = typeof rawScore === 'number' ? rawScore : Number.parseFloat(String(rawScore ?? ''));
    if (!Number.isFinite(score)) {
      return null;
    }
    return score > 1 ? score / 100 : score;
  }, []);

  const getReferenceRank = useCallback((reference: ReferenceDocumentItem, fallback: number) => {
    const rawRank = reference.source_rank;
    const rank = typeof rawRank === 'number' ? rawRank : Number.parseInt(String(rawRank ?? ''), 10);
    return Number.isFinite(rank) && rank > 0 ? rank : fallback + 1;
  }, []);

  const sortReferencesByRelevance = useCallback((items: ReferenceDocumentItem[]) => {
    return [...items].sort((left, right) => {
      const leftScore = getReferenceScore(left);
      const rightScore = getReferenceScore(right);
      if (leftScore !== null || rightScore !== null) {
        return (rightScore ?? -1) - (leftScore ?? -1);
      }
      return getReferenceRank(left, items.indexOf(left)) - getReferenceRank(right, items.indexOf(right));
    });
  }, [getReferenceRank, getReferenceScore]);

  const getMatchLabel = useCallback((reference: ReferenceDocumentItem) => {
    const explicit = String(reference.match_type || '').toLowerCase();
    if (explicit === 'inspected' || explicit === 'inspected_pdf' || explicit === 'verified_page') return 'Inspected manual';
    if (explicit === 'web') return 'Web source';
    if (explicit === 'downloaded') return 'Opened manual';
    if (explicit === 'exact') return 'Exact match';
    if (explicit === 'partial') return 'Partial match';
    if (explicit === 'scanned') return 'Scanned';
    const score = getReferenceScore(reference);
    if (score !== null) {
      if (score >= 0.85) return 'Exact match';
      if (score >= 0.65) return 'Partial match';
      return 'Scanned';
    }
    return 'Scanned';
  }, [getReferenceScore]);

  const formatRelevanceValue = useCallback((reference: ReferenceDocumentItem, fallbackIndex: number) => {
    const score = getReferenceScore(reference);
    if (score !== null) {
      return `${Math.round(score * 100)}%`;
    }
    return `#${getReferenceRank(reference, fallbackIndex)}`;
  }, [getReferenceRank, getReferenceScore]);

  const renderReferenceDocuments = useCallback((references?: ReferenceDocumentItem[], groupKey = 'latest') => {
    if (!references || references.length === 0) {
      return null;
    }

    const orderedReferences = sortReferencesByRelevance(references);
    const fullManualReference = orderedReferences.find(
      reference => reference.delivery_type === 'full_document',
    );
    const isExpanded = Boolean(expandedSourceGroups[groupKey]);
    const activeTab = activeSourceTabs[groupKey] || 'all';
    const exactCount = orderedReferences.filter(reference => getMatchLabel(reference) === 'Exact match').length;
    const tabCounts: Record<SourceTabKey, number> = {
      all: orderedReferences.length,
      exact: exactCount,
      service: orderedReferences.filter(reference => getSourceTabKey(reference) === 'service').length,
      install: orderedReferences.filter(reference => getSourceTabKey(reference) === 'install').length,
      engineering: orderedReferences.filter(reference => getSourceTabKey(reference) === 'engineering').length,
    };
    const filteredReferences = orderedReferences.filter((reference) => {
      if (activeTab === 'all') return true;
      if (activeTab === 'exact') return getMatchLabel(reference) === 'Exact match';
      return getSourceTabKey(reference) === activeTab;
    });
    const tabLabels: Record<SourceTabKey, string> = {
      all: 'All',
      exact: 'Exact matches',
      service: 'Service',
      install: 'Install',
      engineering: 'Engineering',
    };

    return (
      <ReferenceDocuments>
        <ReferenceDocumentsSummary>
          <ReferenceDocumentsToggle
            type="button"
            fill="clear"
            onClick={fullManualReference
              ? () => { void openReferenceDocument(fullManualReference); }
              : () => setExpandedSourceGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }))}
          >
            {fullManualReference
              ? `Open full manual · ${formatSourceTitle(fullManualReference, 'Manual')}`
              : `Answers backed by ${orderedReferences.length} source${orderedReferences.length === 1 ? '' : 's'}`}
            <ReferenceDocumentsToggleIcon
              icon={fullManualReference ? openOutline : chevronForwardOutline}
              $open={!fullManualReference && isExpanded}
            />
          </ReferenceDocumentsToggle>
        </ReferenceDocumentsSummary>
        {!fullManualReference && isExpanded && (
          <ReferenceSourceDetails>
            <ReferenceSourceTabs>
              {SOURCE_TAB_ORDER.map((tabKey) => (
                <ReferenceSourceTab
                  key={tabKey}
                  type="button"
                  fill="clear"
                  $active={activeTab === tabKey}
                  onClick={() => setActiveSourceTabs(prev => ({ ...prev, [groupKey]: tabKey }))}
                >
                  {tabLabels[tabKey]} ({tabCounts[tabKey]})
                </ReferenceSourceTab>
              ))}
            </ReferenceSourceTabs>
            <ReferenceSourceTableHeader>
              <span>Document</span>
              <span>
                Relevance
              </span>
            </ReferenceSourceTableHeader>
            <ReferenceSourceRows>
              {filteredReferences.map((reference) => {
                const originalIndex = orderedReferences.indexOf(reference);
                const title = formatSourceTitle(reference, `Source ${originalIndex + 1}`);
                const isClickable = Boolean(reference.url || reference.managed_document_id || (reference.storage_bucket && reference.storage_object_key));
                const matchLabel = getMatchLabel(reference);
                const score = getReferenceScore(reference);
                const relevance = formatRelevanceValue(reference, originalIndex);
                const matchTag = matchLabel === 'Exact match'
                  ? <ReferenceSourceMatchExact>{matchLabel}</ReferenceSourceMatchExact>
                  : matchLabel === 'Partial match' || matchLabel === 'Inspected manual'
                    ? <ReferenceSourceMatchPartial>{matchLabel}</ReferenceSourceMatchPartial>
                    : <ReferenceSourceMatchScanned>{matchLabel}</ReferenceSourceMatchScanned>;
                const relevanceLevel = (score ?? 0) >= 0.9 ? 'high' : (score ?? 0) >= 0.65 ? 'medium' : 'low';

                return (
                  <ReferenceSourceRow
                    key={`${reference.managed_document_id || 'unresolved'}-${title}-${originalIndex}-detail`}
                    type="button"
                    onClick={isClickable ? () => { void openReferenceDocument(reference); } : undefined}
                    disabled={!isClickable}
                  >
                    {renderDocumentThumbnail(title)}
                    <ReferenceSourceRowMain>
                      <ReferenceSourceRowTitle>{title}</ReferenceSourceRowTitle>
                      <ReferenceSourceTags>
                        <span>{getManualTypeLabel(reference)}</span>
                        {reference.manufacturer && <span>{reference.manufacturer}</span>}
                        {matchTag}
                      </ReferenceSourceTags>
                    </ReferenceSourceRowMain>
                    <ReferenceSourceRelevance>
                      <ReferenceSourceRelevanceDot $level={relevanceLevel} />
                      {relevance}
                    </ReferenceSourceRelevance>
                    <ReferenceSourceOpenIcon icon={openOutline} />
                  </ReferenceSourceRow>
                );
              })}
            </ReferenceSourceRows>
          </ReferenceSourceDetails>
        )}
      </ReferenceDocuments>
    );
  }, [
    activeSourceTabs,
    expandedSourceGroups,
    formatRelevanceValue,
    formatSourceTitle,
    getManualTypeLabel,
    getMatchLabel,
    getReferenceScore,
    getSourceTabKey,
    openReferenceDocument,
    renderDocumentThumbnail,
    sortReferencesByRelevance,
  ]);

  const getApiErrorMessage = useCallback((error: unknown, fallback: string) => {
    const chatError = error as ChatApiError;
    return chatError?.messageFromApi ?? (error as Error)?.message ?? fallback;
  }, []);

  const trimmedSessionTitleDraft = sessionTitleDraft.trim();
  const normalizedCurrentSessionTitle = (currentSessionTitle || '').trim();
  const sessionTitleValidationError = useMemo(() => {
    return getSessionTitleValidationError(sessionTitleDraft);
  }, [sessionTitleDraft]);
  const isSessionTitleUnchanged = trimmedSessionTitleDraft === normalizedCurrentSessionTitle;
  const canSaveSessionTitle =
    !isSavingSessionTitle &&
    !sessionTitleValidationError &&
    !isSessionTitleUnchanged;

  const openSessionTitleModal = useCallback(() => {
    setSessionTitleDraft(currentSessionTitle || '');
    setSessionTitleError(null);
    setShowSessionTitleModal(true);
  }, [currentSessionTitle]);

  const closeSessionTitleModal = useCallback(() => {
    if (isSavingSessionTitle) {
      return;
    }
    setShowSessionTitleModal(false);
    setSessionTitleError(null);
  }, [isSavingSessionTitle]);

  const handleSaveSessionTitle = useCallback(async () => {
    if (!sessionId || !canSaveSessionTitle) {
      return;
    }

    setIsSavingSessionTitle(true);
    setSessionTitleError(null);
    try {
      const response = await chatApi.updateSessionTitle(
        sessionId,
        trimmedSessionTitleDraft || null,
      );
      const nextTitle = response?.data?.session?.title ?? null;
      setCurrentSessionTitle(nextTitle);
      setSessionTitleDraft(nextTitle || '');
      setShowSessionTitleModal(false);
      setSessionTitleToastMessage(nextTitle ? 'Chat title updated' : 'Chat title cleared');
      setShowSessionTitleToast(true);
    } catch (error) {
      if (isSessionUnavailableError(error)) {
        redirectToChatList();
        return;
      }
      setSessionTitleError(getApiErrorMessage(error, 'Unable to update this chat title right now.'));
    } finally {
      setIsSavingSessionTitle(false);
    }
  }, [canSaveSessionTitle, getApiErrorMessage, redirectToChatList, sessionId, trimmedSessionTitleDraft]);

  const applyFeedbackToTimeline = useCallback((messageId: string, feedback: MessageFeedback) => {
    skipNextTimelineAutoScrollRef.current = true;
    setTimeline(prev => prev.map((item) => {
      if (item?.id !== messageId) {
        return item;
      }
      return {
        ...item,
        feedback,
      };
    }));
  }, []);

  const submitMessageFeedback = useCallback(async (
    messageId: string,
    rating: 'up' | 'down',
    reasons: string[] = [],
    comment: string = '',
  ) => {
    if (!sessionId) {
      return;
    }

    setIsSubmittingFeedback(true);
    setFeedbackError(null);
    try {
      const response = await chatApi.submitMessageFeedback(sessionId, messageId, {
        rating,
        reasons,
        comment,
      });
      const feedback = response?.data?.feedback as MessageFeedback | undefined;
      if (feedback) {
        applyFeedbackToTimeline(messageId, feedback);
      } else {
        applyFeedbackToTimeline(messageId, {
          rating,
          reasons,
          comment: comment || null,
        });
      }
      setShowFeedbackToast(true);
    } catch (error) {
      if (isSessionUnavailableError(error)) {
        redirectToChatList();
        return;
      }
      const message = getApiErrorMessage(error, 'Unable to save feedback right now.');
      setFeedbackError(message);
      throw error;
    } finally {
      setIsSubmittingFeedback(false);
    }
  }, [applyFeedbackToTimeline, getApiErrorMessage, redirectToChatList, sessionId]);

  const openDownvoteModal = useCallback((messageId: string) => {
    setFeedbackMessageId(messageId);
    setSelectedDownvoteReasons([]);
    setDownvoteComment('');
    setFeedbackError(null);
    setShowDownvoteModal(true);
  }, []);

  const toggleDownvoteReason = useCallback((reason: string, checked: boolean) => {
    setSelectedDownvoteReasons((prev) => {
      if (checked) {
        if (prev.includes(reason)) {
          return prev;
        }
        return [...prev, reason];
      }
      return prev.filter((item) => item !== reason);
    });
  }, []);

  const handleThumbsUp = useCallback(async (messageId: string) => {
    try {
      await submitMessageFeedback(messageId, 'up');
    } catch (_error) {
      // Error state is managed by feedbackError
    }
  }, [submitMessageFeedback]);

  const handleDownvoteSubmit = useCallback(async () => {
    if (!feedbackMessageId || selectedDownvoteReasons.length === 0) {
      setFeedbackError('Please select at least one reason.');
      return;
    }
    try {
      await submitMessageFeedback(
        feedbackMessageId,
        'down',
        selectedDownvoteReasons,
        downvoteComment.trim(),
      );
      setShowDownvoteModal(false);
      setFeedbackMessageId(null);
      setSelectedDownvoteReasons([]);
      setDownvoteComment('');
      setFeedbackError(null);
    } catch (_error) {
      // Error state is managed by feedbackError
    }
  }, [downvoteComment, feedbackMessageId, selectedDownvoteReasons, submitMessageFeedback]);

  /**
   * Pretty plain text for clipboard: Unicode bullets, numbered lists, section breaks
   * for headings, links as `Label (url)` — no Markdown. See `htmlToPrettyPlainText`.
   */
  const extractPlainText = useCallback((rawContent: string): string => {
    const formatted = formatAllLinks(stripLegacyReferenceSections(rawContent));
    return htmlToPrettyPlainText(formatted);
  }, [stripLegacyReferenceSections]);

  const copyTextToClipboard = useCallback(async (text: string) => {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    // Legacy fallback (older Android WebViews)
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
  }, []);

  const sharedMessageText = useCallback((rawContent: string): string => {
    const messageText = extractPlainText(rawContent).trim();
    return [messageText, SMARTDOCS_SHARE_FOOTER].filter(Boolean).join('\n\n');
  }, [extractPlainText]);

  /**
   * Copy a message's readable text to clipboard.
   * Uses the async Clipboard API (iOS Safari ≥ 13, Android Chrome ≥ 66, all modern
   * Capacitor WebViews). Falls back to the legacy execCommand path for older contexts.
   */
  const handleCopyMessage = useCallback(async (rawContent: string) => {
    try {
      const text = extractPlainText(rawContent);
      await copyTextToClipboard(text);
      setShowCopyToast(true);
    } catch (_err) {
      // Silently fail — clipboard access denied or unavailable
    }
  }, [copyTextToClipboard, extractPlainText]);

  const handleShareMessage = useCallback(async (rawContent: string) => {
    const text = sharedMessageText(rawContent);
    try {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Share')) {
        await Share.share({
          title: 'SmartDocs',
          text,
          dialogTitle: 'Share message',
        });
        return;
      }

      if (navigator?.share) {
        await navigator.share({
          title: 'SmartDocs',
          text,
        });
        return;
      }

      await copyTextToClipboard(text);
      setShowCopyToast(true);
    } catch (err) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'AbortError') {
        return;
      }
      try {
        await copyTextToClipboard(text);
        setShowCopyToast(true);
      } catch (_copyErr) {
        // Silently fail if both share and clipboard are unavailable.
      }
    }
  }, [copyTextToClipboard, sharedMessageText]);

  // Format time to "X minutes/hours/days ago"
  const formatTime = useCallback((dateString: string) => {
    try {
      // Parse the UTC timestamp correctly by ensuring it's treated as UTC
      // If the string doesn't end with 'Z', add it to ensure UTC parsing
      const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
      const utcDate = new Date(utcDateString);
      return formatDistanceToNow(utcDate, { addSuffix: true });
    } catch (error) {
      return 'moments ago';
    }
  }, []);

  // Update the last update time whenever a new message is added
  useEffect(() => {
    if (messages.length > 0) {
      setLastUpdateTime(formatTime(new Date().toISOString()));
    }
  }, [messages.length, formatTime]);

  // Effect for socket initialization
  useEffect(() => {
    let isMounted = true;
    let socketInstance: any | null = null;

    const setupSocket = async () => {
      if (!sessionId || socketInitialized.current || !isMounted) {
        return;
      }

      if (escalationStatus === 'connected' || escalationStatus === 'resolved') {
        return;
      }

      // If we already have a socket connection, disconnect it first
      if (socketRef.current?.connected) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      const socketUrl = WEBSOCKET_URL;
      const newSocket = io(socketUrl, {
        auth: {
          token: localStorage.getItem('token'),
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 30000, // Increased from 10000 to 30000 (30 seconds) to handle longer AI processing times
        autoConnect: true,
        forceNew: true,
        // withCredentials: true,
        path: '/socket.io',
        // Add additional options to help with connection stability
        upgrade: true,
        rememberUpgrade: false
      });

      let isJoiningRoom = false;
      let hasJoinedRoom = false;
      const MAX_RETRIES = 5;
      let retryCount = 0;

      const joinRoom = () => {
        if (!isMounted || isJoiningRoom || hasJoinedRoom || retryCount >= MAX_RETRIES) {
          if (retryCount >= MAX_RETRIES) {
            console.error('[ChatSession] Maximum retry attempts reached. Failed to join chat room.');
          }
          return;
        }

        // Add timeout to prevent infinite retries
        setTimeout(() => {
          if (isJoiningRoom && !hasJoinedRoom) {
            console.error('[ChatSession] Join room timeout');
            isJoiningRoom = false;
            retryCount++;
            if (retryCount < MAX_RETRIES) {
              setTimeout(joinRoom, 1000);
            }
          }
        }, 5000); // 5 second timeout

        isJoiningRoom = true;

        const joinData = {
          session_id: sessionId,
          role: 'user'
        };

        // Emit join_chat without expecting acknowledgment
        newSocket.emit('join_chat', joinData);

        // The server will emit 'room_joined' event which we handle below
        // This is more reliable than callback acknowledgments
      };

      newSocket.on('connect', () => {
        if (!isMounted) return;
        hasJoinedRoom = false; // Reset join status on new connection
        joinRoom();
      });

      // Handle room joined event
      newSocket.on('room_joined', (data: any) => {
        if (!isMounted) return;
        if (data.success) {
          hasJoinedRoom = true;
          isJoiningRoom = false;
          retryCount = 0;
          newSocket.emit('ready_to_receive', { session_id: sessionId });
          if (activeRequestStartedAtRef.current) {
            void refreshTimelineRef.current?.();
          }
        } else {
          console.error('[ChatSession] Failed to join chat room:', data);
          isJoiningRoom = false;
          retryCount++;
          if (retryCount < MAX_RETRIES) {
            setTimeout(joinRoom, 1000);
          }
        }
      });

      newSocket.on('disconnect', (_reason: any) => {
        if (!isMounted) return;
        hasJoinedRoom = false;
        isJoiningRoom = false;
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      });

      // Handle new messages from the SmartDocs workflow.
      newSocket.on('new_message', async (message: any) => {
        if (!isMounted) return;

        // Allow empty content for stream_start and stream_complete events
        const event = message.event;
        const isStreamEvent = event === 'stream_start' || event === 'stream_complete';
        const isStatusEvent = [
          'request',
          'status',
          'codex_event',
          'runtime_event',
          'runtime_narration',
          'adaptive_step',
          'adaptive_trace',
          'tool_start',
          'tool_complete',
          'tool_end',
          'agent_handoff',
          'complete',
          'error',
        ].includes(event);

        if (!message || (!message.content && !isStreamEvent && !isStatusEvent)) {
          console.error('[ChatSession] Invalid message format:', message);
          return;
        }

        // Check if we're properly connected to the room
        if (!hasJoinedRoom) {
          console.warn('[ChatSession] Received message but not joined to room, attempting to join');
          joinRoom();
          const messageSessionId = String(message.session_id || '').trim();
          if (messageSessionId && messageSessionId !== sessionId) {
            return;
          }
        }

        const sender = message.sender;
        const role = message.role;

        if (event === 'queue_updated') {
          try {
            const payload = message.content ? JSON.parse(message.content) : {};
            setQueuedDraft(prev => prev ? {
              ...prev,
              queuedAt: payload.queued_at || prev.queuedAt,
              serverQueued: true,
            } : prev);
          } catch (_error) {
            setQueuedDraft(prev => prev ? {
              ...prev,
              serverQueued: true,
            } : prev);
          }
          setIsQueueSubmitting(false);
          return;
        }

        if (event === 'queue_cleared') {
          setQueuedDraft(null);
          setIsQueueSubmitting(false);
          return;
        }

        if (event === 'run_interrupted') {
          setIsThinking(false);
          setThinkingMessages([]);
          setIsSending(false);
          setIsStreamingResponse(false);
          setIsStoppingRun(false);
          streamingMessageIdRef.current = null;
          streamingAccumulatedRef.current = '';
          pendingAdaptiveTraceRef.current = [];
          return;
        }

        if (event === 'queued_message_dispatched') {
          try {
            const payload = message.content ? JSON.parse(message.content) : {};
            const dispatchedText = payload.message || '';
            if (dispatchedText || payload.image_data_url) {
              setTimeline(prev => [...prev, {
                id: `queued-${payload.timestamp || Date.now().toString()}`,
                type: 'message',
                content: dispatchedText,
                timestamp: payload.timestamp || new Date().toISOString(),
                sender: 'user',
                isTransient: true,
                has_image: Boolean(payload.image_data_url),
                image_url: payload.image_data_url || null,
                image_filename: payload.image_filename || null,
                escalation_context: {
                  escalation_id: currentEscalationId,
                  is_during_escalation: false,
                  escalation_phase: 'before'
                }
              }]);
            }
          } catch (_error) {
            // Ignore malformed queued dispatch payload.
          }
          setQueuedDraft(null);
          setIsQueueSubmitting(false);
          setIsSending(true);
          return;
        }

        // Handle escalation resolved events from SmartDocs.
        if (event === 'escalation_resolved') {
          try {
            const content = JSON.parse(message.content);
            const resolvedAt = new Date().toISOString();
            const escalationId = message.escalation_id || currentEscalationIdRef.current;

            clearAiLoadingState(true);
            setEscalationStatusWithRef('resolved');
            setHasActiveEscalation(false);
            setActiveEscalationId(null);
            setAgentName('');
            setEscalationResolvedAt(resolvedAt);

            addEscalationEvent({
              type: 'resolve',
              timestamp: resolvedAt,
              escalationId
            });
          } catch (error) {
            console.error('[ChatSession] Error parsing escalation resolved content:', error);
          }
          return;
        }

        // Handle connect to distributor event (data collection complete)
        if (event === 'connect_to_distributor') {
          try {
            const content = JSON.parse(message.content);
            await createEscalationFromCollectedData(content);
          } catch (error) {
            console.error('[ChatSession] Error creating escalation after data collection:', error);
            clearAiLoadingState(true);
            appendAssistantMessage(getApiErrorMessage(error, 'Failed to connect to live agent. Please try again.'));
            // Reset escalation state on error
            setEscalationStatusWithRef('none');
            setHasActiveEscalation(false);
          }
          return; // Don't display this message to user
        }

        if (event === 'adaptive_step') {
          pushLiveEvidenceStep(getLiveEvidenceStepLabel(message), event);
          return;
        }

        if (event === 'adaptive_trace') {
          pendingAdaptiveTraceRef.current = parseAdaptiveTraceSteps(message);
          return;
        }

        if (event === 'request' || event === 'status' || event === 'codex_event' || event === 'runtime_event' || event === 'runtime_narration' || event === 'complete' || event === 'error') {
          const runtimeEventKind = event === 'runtime_event' ? String((message as any)?.kind || 'status') : '';
          const codexEventKind = event === 'codex_event'
            ? String(((message as any)?.codex_event || (message as any)?.extra?.codex_event || {})?.type || 'event')
            : '';
          pushLiveEvidenceStep(
            runtimeStreamEventLabel(event, message),
            runtimeEventKind ? `${event}:${runtimeEventKind}` : (codexEventKind ? `${event}:${codexEventKind}` : event),
          );
          return;
        }

        if (event === 'tool_start' || event === 'tool_complete' || event === 'tool_end' || event === 'agent_handoff') {
          const toolName = String((message as any)?.tool_name || (message as any)?.agent_name || 'tool')
            .trim()
            .replace(/[^a-zA-Z0-9_-]/g, '_');
          pushLiveEvidenceStep(getToolStatusLabel(event, message), toolName ? `${event}:${toolName}` : event);
          return;
        }

        // Handle thinking events
        if (event === 'thinking') {
          // Don't show thinking indicator when escalation is active
          if (hasActiveEscalation) {
            return;
          }

          // Show thinking indicator immediately when we receive any thinking event
          setWorkStartedAt(prev => prev ?? Date.now());
          setIsThinking(true);
          // Set isSending to false since we're now in thinking mode (prevents duplicate indicators)
          setIsSending(false);

          // Add only user-safe progress labels if the backend includes status text.
          if (message.content && message.content.trim()) {
            const boldRegex = /\*\*([^*]+?)\*\*/;
            const match = message.content.match(boldRegex);
            const statusLabel = sanitizeLiveWorkLabel(match ? match[1] : message.content);

            if (statusLabel) {
              setThinkingMessages(prev => {
                if (prev.some(existing => existing === statusLabel)) {
                  return prev;
                }
                return [...prev, statusLabel].slice(-6);
              });
            }
          }

          return; // Don't add thinking messages to chat, just update thinking state
        }

        // Handle streaming start
        if (event === 'stream_start') {
          // Clear error timeout since we got a response
          if ((window as any).__errorTimeoutId) {
            clearTimeout((window as any).__errorTimeoutId);
            delete (window as any).__errorTimeoutId;
          }

          const streamId = message.id || `stream-${Date.now().toString()}`;
          streamingMessageIdRef.current = streamId;
          streamingAccumulatedRef.current = message.content || '';
          const formattedContent = decodeHtmlEntities(streamingAccumulatedRef.current);

          if (formattedContent && formattedContent.trim()) {
            setIsThinking(false);
            setThinkingMessages([]);
            setIsStreamingResponse(true);
            setIsSending(false);
            setTimeline(prev => [...prev, {
              id: streamId,
              type: 'message',
              content: formattedContent,
              timestamp: message.timestamp || new Date().toISOString(),
              sender: 'assistant',
              isTransient: true,
              feedback_eligible: false,
              escalation_context: {
                escalation_id: currentEscalationId,
                is_during_escalation: hasActiveEscalation,
                escalation_phase: hasActiveEscalation ? 'during' : 'before'
              }
            }]);
          }

          return;
        }

        // Handle streaming chunk
        if (event === 'stream_chunk') {
          const currentId = streamingMessageIdRef.current;
          if (!currentId) {
            // Initialize if we missed start - this is our stream_start equivalent
            const fallbackId = message.id || `stream-${Date.now().toString()}`;
            streamingMessageIdRef.current = fallbackId;
            streamingAccumulatedRef.current = '';
          }


          const chunk = message.content || '';
          streamingAccumulatedRef.current = (streamingAccumulatedRef.current || '') + chunk;

          const formattedContent = decodeHtmlEntities(streamingAccumulatedRef.current);
          const targetId = streamingMessageIdRef.current as string | null;
          if (!targetId || !formattedContent || !formattedContent.trim()) {
            return;
          }

          setIsThinking(false);
          setThinkingMessages([]);
          setIsStreamingResponse(true);
          setIsSending(false);

          setTimeline(prev => {
            const hasStreamingMessage = prev.some(m => m.id === targetId);
            if (!hasStreamingMessage) {
              return [...prev, {
                id: targetId,
                type: 'message',
                content: formattedContent,
                timestamp: message.timestamp || new Date().toISOString(),
                sender: 'assistant',
                isTransient: true,
                feedback_eligible: false,
                reference_documents: Array.isArray(message.reference_documents) ? message.reference_documents : [],
                artifacts: Array.isArray((message as any).artifacts) ? (message as any).artifacts : [],
                escalation_context: {
                  escalation_id: currentEscalationId,
                  is_during_escalation: hasActiveEscalation,
                  escalation_phase: hasActiveEscalation ? 'during' : 'before'
                }
              }];
            }

            return prev.map(m => m.id === targetId
              ? { ...m, content: formattedContent }
              : m);
          });
          return;
        }

        // Handle streaming complete
        if (event === 'stream_complete') {
          const currentId = streamingMessageIdRef.current;
          const finalAccumulated = message.content || streamingAccumulatedRef.current || '';
          // The agent forwards the real DB message_id in stream_complete.
          // The relay preserves it as both message.message_id and message.id.
          // Prefer the explicit field; fall back to message.id when it looks like a UUID
          // (rules out relay-generated ids like "msg_1234567890").
          const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          const explicitId = (message as any).message_id;
          const fallbackId = typeof message.id === 'string' && uuidPattern.test(message.id) ? message.id : undefined;
          const realMessageId: string | undefined = (typeof explicitId === 'string' && explicitId.trim() ? explicitId : undefined) || fallbackId;
          const references = Array.isArray(message.reference_documents) ? message.reference_documents : [];
          const artifacts = Array.isArray((message as any).artifacts) ? (message as any).artifacts : [];
          const adaptiveTrace = pendingAdaptiveTraceRef.current;
          const rawFinalContent = decodeHtmlEntities(finalAccumulated);
          const finalContent = getDisplayContent(rawFinalContent, references);
          const formattedFinalContent = finalContent;

          if (currentId && formattedFinalContent && formattedFinalContent.trim()) {
            setTimeline(prev => {
              const hasVisibleMessage = prev.some(m => m.id === currentId);
              if (!hasVisibleMessage) {
                return [...prev, {
                  id: realMessageId || currentId,
                  type: 'message',
                  content: formattedFinalContent,
                  timestamp: message.timestamp || new Date().toISOString(),
                  sender: 'assistant',
                  isTransient: true,
                  feedback_eligible: Boolean(realMessageId),
                  reference_documents: references,
                  artifacts,
                  adaptive_trace: adaptiveTrace,
                  escalation_context: {
                    escalation_id: currentEscalationId,
                    is_during_escalation: hasActiveEscalation,
                    escalation_phase: hasActiveEscalation ? 'during' : 'before'
                  }
                }];
              }

              return prev.map(m =>
                m.id === currentId
                  ? { ...m, content: formattedFinalContent, reference_documents: references, artifacts }
                  : m,
              ).map(m =>
                m.id === currentId
                  ? { ...m, adaptive_trace: adaptiveTrace }
                  : m,
              );
            });
          }

          // Reconcile the visible streaming message if one exists.
          if (currentId && finalAccumulated.trim()) {
            // Reconcile ephemeral id with real DB id if the agent provided one.
            // Also mark feedback_eligible:true so the feedback buttons become active.
            if (realMessageId) {
              setTimeline(prev => prev.map(m =>
                m.id === currentId
                  ? {
                    ...m,
                    id: realMessageId,
                    feedback_eligible: true,
                    reference_documents: references,
                    artifacts,
                    adaptive_trace: adaptiveTrace,
                  }
                  : m,
              ));
            } else {
              // No id swap needed — retain existing content unchanged.
              setTimeline(prev => prev);
            }
          } else if (finalAccumulated.trim()) {
            // Only create a message entry if we have actual content
            // This prevents empty message bubbles from appearing
            const finalContent = getDisplayContent(
              decodeHtmlEntities(finalAccumulated),
              references,
            );
            const formattedFinalContent = finalContent;

            if (formattedFinalContent && formattedFinalContent.trim()) {
              const addId = realMessageId || message.id || `stream-${Date.now().toString()}`;
              setTimeline(prev => [...prev, {
                id: addId,
                type: 'message',
                content: formattedFinalContent,
                timestamp: message.timestamp || new Date().toISOString(),
                sender: 'assistant',
                isTransient: true,
                feedback_eligible: Boolean(realMessageId),
                reference_documents: references,
                artifacts,
                adaptive_trace: adaptiveTrace,
                escalation_context: {
                  escalation_id: currentEscalationId,
                  is_during_escalation: hasActiveEscalation,
                  escalation_phase: hasActiveEscalation ? 'during' : 'before'
                }
              }]);
            }
          }

          streamingMessageIdRef.current = null;
          streamingAccumulatedRef.current = '';
          pendingAdaptiveTraceRef.current = [];
          awaitingServerMessageIdRef.current = realMessageId ?? null;
          activeAiRunStartedAtRef.current = 0;
          setIsThinking(false);
          setThinkingMessages([]);
          setIsStreamingResponse(false); // Reset streaming flag when complete
          setIsSending(false);
          setIsEscalating(false);

          // The server persists the final response before emitting this event.
          // Refresh immediately, but keep the completed live bubble until its
          // matching server row is present in the response.
          void refreshTimelineRef.current?.({ showLoading: false, force: true });

          // Clear timeout if it exists
          if ((window as any).__chatTimeoutId) {
            clearTimeout((window as any).__chatTimeoutId);
            delete (window as any).__chatTimeoutId;
          }
          if ((window as any).__chatReconcileIntervalId) {
            clearInterval((window as any).__chatReconcileIntervalId);
            delete (window as any).__chatReconcileIntervalId;
          }
          if (!finalAccumulated.trim()) {
            // Some relay paths send completion as a state-only event after the
            // backend has persisted the assistant message. Reconcile once while
            // the request timestamp is still available; otherwise clearing it
            // here can hide the completed answer until a manual page reload.
            await refreshTimelineRef.current?.();
          }
          activeRequestStartedAtRef.current = null;

          return;
        }

        // Handle agent_response events
        if (event === 'agent_response') {
          // Hide thinking steps immediately when response is received
          clearAiLoadingState();

          // Clear timeout if it exists
          if ((window as any).__chatTimeoutId) {
            clearTimeout((window as any).__chatTimeoutId);
            delete (window as any).__chatTimeoutId;
          }

          // Use the actual sender from the message, fallback to 'assistant' if not provided
          const messageSender = sender || 'assistant';
          const adaptiveTrace = pendingAdaptiveTraceRef.current;

          // Create the final message
          const newMessage = {
            id: message.id || Date.now().toString(),
            type: 'message',
            content: getDisplayContent(message.content, message.reference_documents),
            timestamp: message.timestamp || new Date().toISOString(),
            sender: messageSender,
            reference_documents: Array.isArray(message.reference_documents) ? message.reference_documents : [],
            artifacts: Array.isArray((message as any).artifacts) ? (message as any).artifacts : [],
            adaptive_trace: adaptiveTrace,
            escalation_context: {
              escalation_id: currentEscalationId,
              is_during_escalation: hasActiveEscalation,
              escalation_phase: hasActiveEscalation ? 'during' : 'before'
            }
          };

          setTimeline(prev => [...prev, newMessage]);
          pendingAdaptiveTraceRef.current = [];

          return; // Exit early to prevent fallback processing
        }

        // Handle human_agent_response events (from distributor)
        if (event === 'human_agent_response') {
          // Hide thinking steps immediately when response is received
          clearAiLoadingState(true);

          // Human agent messages always have sender 'agent'
          const messageSender = 'agent';

          // Create the final message
          const newMessage = {
            id: message.id || Date.now().toString(),
            type: 'message',
            content: message.content,
            timestamp: message.timestamp || new Date().toISOString(),
            sender: messageSender,
            escalation_context: {
              escalation_id: currentEscalationId,
              is_during_escalation: hasActiveEscalation,
              escalation_phase: hasActiveEscalation ? 'during' : 'before'
            }
          };

          setTimeline(prev => [...prev, newMessage]);

          return; // Exit early to prevent fallback processing
        }

        // Handle other events (fallback for backward compatibility)
        // Skip escalation_resolved and connect_to_distributor events in fallback
        if (!event || ![
          'thinking',
          'agent_response',
          'human_agent_response',
          'escalation_resolved',
          'connect_to_distributor',
          'adaptive_step',
          'adaptive_trace',
          'tool_start',
          'tool_complete',
          'tool_end',
          'agent_handoff',
          'request',
          'status',
          'complete',
          'error',
        ].includes(event)) {
          // Use the sender field from the message, with improved fallback logic
          // The WebSocket server sets sender based on role, so we should trust the sender field
          const messageSender = sender || (role === 'user' ? 'user' : role === 'agent' ? 'agent' : 'assistant');
          const isUserMessage = messageSender === 'user';

          const newMessage = {
            id: message.id || Date.now().toString(),
            type: 'message',
            content: message.content,
            timestamp: message.timestamp || new Date().toISOString(),
            sender: messageSender,
            escalation_context: {
              escalation_id: currentEscalationId,
              is_during_escalation: hasActiveEscalation,
              escalation_phase: hasActiveEscalation ? 'during' : 'before'
            }
          };

          setTimeline(prev => [...prev, newMessage]);
          if (isUserMessage) {
            setQueuedDraft(prev => {
              if (!prev || !doesMessageMatchQueuedDraft(newMessage, prev)) {
                return prev;
              }
              return null;
            });
            setIsQueueSubmitting(false);
          }

          // Update escalation status if needed
          if (messageSender === 'agent' && !hasActiveEscalation) {
            setHasActiveEscalation(true);
            setEscalationStatusWithRef('connected');
            setAgentName('Live Agent');
          }

          clearAiLoadingState(messageSender === 'agent');
        }

        // Acknowledge message receipt if needed
        if (message.message_id) {
          newSocket.emit('message_received', {
            message_id: message.message_id,
            session_id: sessionId
          });
        }
      });

      // Handle agent joined event (for AI responses)
      newSocket.on('agent_joined', (data: any) => {
        if (!isMounted) return;
        if (data.agent_name) {
          clearAiLoadingState(true);
          setAgentName(data.agent_name);
          setEscalationStatusWithRef('connected');
        }
      });

      // Handle human agent joined event (for escalation)
      newSocket.on('human_agent_joined', (data: any) => {
        if (!isMounted) return;

        // Use escalation id from event, fallback to currentEscalationIdRef (distributor now sends escalation_id so contractor can always match)
        const escalationId = data.escalation_id || currentEscalationIdRef.current;

        if (data.agent_name) {
          clearAiLoadingState(true);
          setAgentName(data.agent_name);
          setEscalationStatusWithRef('connected');
          setHasActiveEscalation(true);
          if (escalationId) setCurrentEscalationIdAndRef(escalationId);

          const connectionContent = `You are now connected to ${data.agent_name || 'Live Agent'}`;

          // Replace the "Connecting you to a live agent..." message with the connection message if it exists
          setTimeline(prev => {
            const next = prev.map(msg => {
              if (msg.id === `connecting-${escalationId}` && msg.type === 'escalation_event') {
                return {
                  ...msg,
                  content: connectionContent,
                  timestamp: new Date().toISOString()
                };
              }
              return msg;
            });
            // If no message was replaced (race or missing "Connecting..." message), add "User connected" separator so it always shows
            const hasConnectionMessage = next.some(m => m.type === 'escalation_event' && m.content === connectionContent);
            if (!hasConnectionMessage && escalationId) {
              return [...next, {
                id: `connect-${escalationId}`,
                type: 'escalation_event',
                content: connectionContent,
                timestamp: new Date().toISOString(),
                sender: 'assistant',
                escalation_context: {
                  escalation_id: escalationId,
                  is_during_escalation: true,
                  escalation_phase: 'during'
                }
              }];
            }
            return next;
          });
        }
      });

      // Handle escalation resolution
      newSocket.on('escalation_resolved', (data: any) => {
        if (!isMounted) return;

        const resolvedAt = data.resolved_at || data.updated_at || new Date().toISOString();
        const escalationId = data.escalation_id || currentEscalationIdRef.current;

        clearAiLoadingState(true);
        setEscalationStatusWithRef('resolved');
        setHasActiveEscalation(false);
        setActiveEscalationId(null);
        setAgentName('');
        setEscalationResolvedAt(resolvedAt);

        addEscalationEvent({
          type: 'resolve',
          timestamp: resolvedAt,
          escalationId
        });
      });

      // Handle agent disconnected
      newSocket.on('agent_disconnected', (data: any) => {
        if (!isMounted) return;

        const resolvedAt = data.resolved_at || data.updated_at || new Date().toISOString();
        const escalationId = data.escalation_id || currentEscalationIdRef.current;

        clearAiLoadingState(true);
        setEscalationStatusWithRef('resolved');
        setHasActiveEscalation(false);
        setActiveEscalationId(null);
        setAgentName('');
        setEscalationResolvedAt(resolvedAt);

        addEscalationEvent({
          type: 'resolve',
          timestamp: resolvedAt,
          escalationId
        });
      });

      // Handle distributor status response
      newSocket.on('distributor_status', (data: any) => {
        if (!isMounted) return;
        if (data.connected && data.agent_name) {
          clearAiLoadingState(true);
          setAgentName(data.agent_name);
          setEscalationStatusWithRef('connected');
          setHasActiveEscalation(true);
        }
      });

      newSocket.on('error', (error: { message: string }) => {
        if (!isMounted) return;
        console.error('[ChatSession] Socket error:', error);
      });

      // Add specific event listeners for debugging
      newSocket.on('connect_error', (error: any) => {
        if (!isMounted) return;
        console.error('[ChatSession] Socket connection error:', error);
        // Don't show error to user if it's just a timeout during initial connection
        // The socket.io client will automatically retry with reconnection enabled
      });

      newSocket.on('reconnect', () => {
        if (!isMounted) return;
        hasJoinedRoom = false; // Reset join status on reconnection
        joinRoom(); // Try to rejoin room after reconnection
      });

      newSocket.on('reconnect_error', (error: any) => {
        if (!isMounted) return;
        console.error('[ChatSession] Socket reconnection error:', error);
      });

      newSocket.on('reconnect_failed', () => {
        if (!isMounted) return;
        console.error('[ChatSession] Socket reconnection failed');
      });

      socketInstance = newSocket;
      socketRef.current = newSocket;
      socketInitialized.current = true;
    };

    // Add a small delay to prevent multiple rapid initializations
    const initializationTimeout = setTimeout(() => {
      setupSocket();
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(initializationTimeout);
      if ((window as any).__chatReconcileIntervalId) {
        clearInterval((window as any).__chatReconcileIntervalId);
        delete (window as any).__chatReconcileIntervalId;
      }
      activeRequestStartedAtRef.current = null;

      // Clean up socket connection and event listeners
      if (socketInstance) {
        socketInstance.removeAllListeners();
        socketInstance.disconnect();
        socketInstance = null;
      }

      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }

      socketInitialized.current = false;
    };
  // This effect owns the socket lifetime for a session. Adding mutable render
  // callbacks here would reconnect the socket while a response is streaming.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]); // Removed escalationStatus from dependencies

  // Note: Chat history is now loaded via the timeline API in the fetchTimeline effect above

    // Initialize chat for existing session
  const initializeChat = useCallback(async () => {
    if (isInitializing.current || initializationInProgressRef.current) return;

    initializationInProgressRef.current = true;
    isInitializing.current = true;

    try {

      // Since session creation happens in ChatList, we should always have a sessionId here
      if (!sessionId) {
        console.error('[ChatSession] No sessionId provided - this should not happen with new flow');
        return;
      }

      // For existing sessions, we can optionally send an initial message
      // or just rely on the welcome message that was already shown

      // The welcome message should already be displayed from the ChatList flow
      // If we need to send an initial message to the AI, we can do it here
      // For now, we'll just ensure the session is ready for communication

    } catch (error) {
      console.error('Error initializing chat:', error);
    } finally {
      isInitializing.current = false;
      initializationInProgressRef.current = false;
    }
  }, [sessionId]);

    // Note: Welcome message is now handled by the backend timeline API

  // Initialize chat for existing sessions
  useEffect(() => {
    if (sessionId) {
      // Clear any existing timeout
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }

      // Add a small delay to prevent duplicate initialization
      initializationTimeoutRef.current = setTimeout(() => {
        initializeChat();
      }, 100);
    }

    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [sessionId, initializeChat]);


  // Note: Escalation status is now handled by the timeline API

  // Hydrate session-scoped state when navigating between chats. Keeping this in
  // memory makes returning to a chat instant and preserves an in-progress run.
  useEffect(() => {
    const cachedTimeline = sessionId ? getCachedTimeline(sessionId) : undefined;
    const liveSnapshot = sessionId ? getChatSessionLiveSnapshot(sessionId) : undefined;

    setTimeline(mergeServerTimeline(
      cachedTimeline?.data?.timeline || [],
      liveSnapshot?.pendingTimelineItems || [],
    ));
    setEscalationEvents(cachedTimeline?.data?.escalation_events || []);
    setIsLoadingHistory(!cachedTimeline);
    setIsSending(liveSnapshot?.isSending ?? false);
    setIsThinking(liveSnapshot?.isThinking ?? false);
    setThinkingMessages(liveSnapshot?.thinkingMessages || []);
    setIsEscalating(liveSnapshot?.isEscalating ?? false);
    setIsStreamingResponse(liveSnapshot?.isStreamingResponse ?? false);
    setIsStoppingRun(liveSnapshot?.isStoppingRun ?? false);
    setWorkStartedAt(liveSnapshot?.workStartedAt ?? null);
    streamingMessageIdRef.current = liveSnapshot?.streamingMessageId ?? null;
    streamingAccumulatedRef.current = liveSnapshot?.streamingAccumulated ?? '';
    pendingAdaptiveTraceRef.current = liveSnapshot?.pendingAdaptiveTrace || [];
    awaitingServerMessageIdRef.current = liveSnapshot?.awaitingServerMessageId ?? null;
    activeAiRunStartedAtRef.current = liveSnapshot?.activeRunStartedAt ?? 0;
    stopRequestedRef.current = false;
    setQueuedDraft(liveSnapshot?.queuedDraft || null);
    setIsQueueSubmitting(false);
    setCurrentSessionTitle(cachedTimeline?.data?.session?.title ?? null);
    setHydratedSessionId(sessionId);
    setShowSessionTitleModal(false);
    setSessionTitleDraft('');
    setSessionTitleError(null);
    setIsSavingSessionTitle(false);
    isInitialLoadRef.current = true; // Reset initial load flag for new session
    pinnedSentMessageIdRef.current = null;
    shouldMaintainPinnedSentMessageRef.current = false;
    resetMessageEndSpacerHeight();
    clearPinnedSentMessageTimeouts();
  }, [clearPinnedSentMessageTimeouts, resetMessageEndSpacerHeight, sessionId]);

  useEffect(() => {
    if (hasActiveEscalation) {
      setQueuedDraft(null);
      setIsQueueSubmitting(false);
    }
  }, [hasActiveEscalation]);

  const queueCurrentDraft = useCallback((messageText: string) => {
    const trimmedMessage = (messageText || '').trim();
    const imagePayload = selectedImage;
    if ((!trimmedMessage && !imagePayload) || hasActiveEscalation || queuedDraft) {
      return;
    }
    setQueuedDraft({
      text: trimmedMessage,
      image: imagePayload,
      queuedAt: new Date().toISOString(),
      serverQueued: false,
    });
    setMessage('');
    setSelectedImage(null);
  }, [hasActiveEscalation, queuedDraft, selectedImage]);

  const handleDeleteQueuedDraft = useCallback(() => {
    setQueuedDraft(null);
    setIsQueueSubmitting(false);
  }, []);

  const handleEditQueuedDraft = useCallback(() => {
    if (!queuedDraft || isQueueSubmitting) {
      return;
    }
    setMessage(queuedDraft.text);
    setSelectedImage(queuedDraft.image);
    setQueuedDraft(null);
    setIsQueueSubmitting(false);
    const nextCursor = queuedDraft.text.length;
    requestAnimationFrame(() => {
      void messageInputRef.current?.setFocus();
      void messageInputRef.current?.getInputElement().then((input) => {
        input.setSelectionRange(nextCursor, nextCursor);
      });
    });
  }, [isQueueSubmitting, queuedDraft]);

  const handleStopAgentRun = useCallback(async () => {
    if (!sessionId || hasActiveEscalation || isStoppingRun) {
      return;
    }
    stopRequestedRef.current = true;
    setIsStoppingRun(true);
    setQueuedDraft(null);
    setIsQueueSubmitting(false);
    try {
      await chatApi.stopMessageToAgent(getContractorName(), sessionId);
    } catch (error) {
      console.error('[ChatSession] Error stopping agent run:', error);
      if (isSessionUnavailableError(error)) {
        redirectToChatList();
        return;
      }
    } finally {
      clearAiLoadingState(true);
      void refreshTimelineRef.current?.();
    }
  }, [clearAiLoadingState, getContractorName, hasActiveEscalation, isStoppingRun, redirectToChatList, sessionId]);

  const clearComposerInput = useCallback((sentText?: string) => {
    if (sentText?.trim()) {
      ignoredComposerEchoRef.current = {
        value: sentText,
        until: Date.now() + 2000,
      };
    }
    setMessage('');
    requestAnimationFrame(() => {
      void messageInputRef.current?.getInputElement().then((input) => {
        input.value = '';
      });
    });
  }, []);

  const dispatchAiMessage = useCallback(async (
    messageText: string,
    image: SelectedImage | null,
    targetSessionId?: string,
    serviceTitanSelection?: ServiceTitanSelection,
    submissionOptions: AssistantMessageSubmissionOptions = {},
  ) => {
    const activeSessionId = targetSessionId || sessionId;
    const trimmedMessage = (messageText || '').trim();
    const hasImage = Boolean(image);
    const choiceSubmission = Boolean(
      serviceTitanSelection || submissionOptions.choiceSubmission,
    );
    if ((!trimmedMessage && !hasImage) || !activeSessionId) {
      return choiceSubmission ? false : undefined;
    }

    if (hasActiveEscalation && !socketRef.current?.connected) {
      return choiceSubmission ? false : undefined;
    }

    try {
      stopRequestedRef.current = false;
      activeAiRunStartedAtRef.current = Date.now();
      if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
        try {
          await Keyboard.hide();
        } catch (_error) {
          // Keyboard hide is best-effort
        }
      }

      setIsStreamingResponse(false); // Reset streaming flag for new message
      setIsSending(true);
      activeRequestStartedAtRef.current = Date.now();
      setWorkStartedAt(prev => prev ?? Date.now());
      setQueuedDraft(prev => {
        if (!prev || !doesMessageMatchQueuedDraft({
          sender: 'user',
          content: trimmedMessage || IMAGE_ONLY_PLACEHOLDER,
          timestamp: new Date().toISOString(),
          has_image: hasImage,
          image_url: image?.dataUrl || null,
        }, prev)) {
          return prev;
        }
        return null;
      });

      // Reset streaming state for new message to prevent streaming to old bubble
      streamingMessageIdRef.current = null;
      streamingAccumulatedRef.current = '';

      const imagePayload = image?.dataUrl;
      if (!serviceTitanSelection && !submissionOptions.preserveComposer) {
        clearComposerInput(messageText); // Clear input immediately, including stale voice echoes.
        setSelectedImage(null);
      }

      // Add message to local state immediately for better UX
      const tempMessageId = Date.now().toString();
      const tempMessage = {
        id: tempMessageId,
        type: 'message',
        content: trimmedMessage || IMAGE_ONLY_PLACEHOLDER,
        timestamp: new Date().toISOString(),
        sender: 'user',
        isTransient: true,
        has_image: Boolean(imagePayload),
        image_url: imagePayload || null,
        image_filename: image?.name || null,
        escalation_context: {
          escalation_id: currentEscalationId,
          is_during_escalation: hasActiveEscalation,
          escalation_phase: hasActiveEscalation ? 'during' : 'before'
        }
      };

      // Add message to timeline
      pinnedSentMessageIdRef.current = tempMessageId;
      shouldMaintainPinnedSentMessageRef.current = true;
      setMessageEndSpacerHeight(PINNED_MESSAGE_END_SPACER_PX);
      setTimeline(prev => [...prev, tempMessage]);
      window.setTimeout(() => {
        void keepPinnedSentMessageVisible(false);
      }, 0);

      const rollbackChoiceSubmission = () => {
        if (!choiceSubmission) return;
        setTimeline(prev => prev.filter(item => item.id !== tempMessageId));
        if (pinnedSentMessageIdRef.current === tempMessageId) {
          pinnedSentMessageIdRef.current = null;
          shouldMaintainPinnedSentMessageRef.current = false;
          setMessageEndSpacerHeight(0);
        }
        setIsSending(false);
        setIsThinking(false);
        setThinkingMessages([]);
        setWorkStartedAt(null);
        setWorkClockTick(0);
        activeRequestStartedAtRef.current = null;
      };

      // Save user message to database
      // TODO: Commented out - messages are now saved via backend API when they flow through socket
      // try {
      //   await api.post('/chat/save-message', {
      //     session_id: sessionId,
      //     sender: 'user',
      //     content: trimmedMessage,
      //     timestamp: new Date().toISOString()
      //   });
      //   console.log('[ChatSession] User message saved to database');
      // } catch (error) {
      //   console.error('[ChatSession] Error saving user message to database:', error);
      //   // Continue even if DB save fails
      // }

      try {
        const contractorName = getContractorName();
        const response = await chatApi.sendMessageToAgent(
          contractorName,
          activeSessionId,
          trimmedMessage,
          assistantMode === 'servicetitan' ? '' : selectedManufacturerSlug,
          assistantMode,
          imagePayload,
          image?.name,
          undefined,
          serviceTitanSelection,
        );

        if (stopRequestedRef.current) {
          rollbackChoiceSubmission();
          return choiceSubmission ? false : undefined;
        }

        if (response.success) {
          const effectiveManufacturerSlug = response.data?.effective_manufacturer_slug;
          if (
            typeof effectiveManufacturerSlug === 'string' &&
            effectiveManufacturerSlug !== selectedManufacturerSlug &&
            assistantManufacturers.some((manufacturer) => manufacturer.slug === effectiveManufacturerSlug)
          ) {
            setSelectedManufacturerSlug(effectiveManufacturerSlug);
          }
          // Check if escalation is active
          if (response.data?.escalation_active) {
            if (socketRef.current?.connected) {
              socketRef.current.emit('new_message', {
                session_id: activeSessionId,
                content: trimmedMessage,
                role: 'user',
                timestamp: new Date().toISOString()
              });
            } else {
              console.error('[ChatSession] WebSocket not connected for human agent communication');
            }

            // Stop loading indicator immediately for human agent communication
            setIsSending(false);
            setIsThinking(false);
            setThinkingMessages([]);
          } else {
            // Message sent successfully to SmartDocs - response will come via WebSocket.
            // Keep isSending true until we receive response from WebSocket
            // The response will come through WebSocket from SmartDocs.
            // isSending will be set to false when we receive stream_complete or agent_response event

            // Socket delivery is best-effort. Reconcile against the durable timeline while
            // this request is active so a missed completion event cannot leave the UI stuck.
            if ((window as any).__chatReconcileIntervalId) {
              clearInterval((window as any).__chatReconcileIntervalId);
            }
            (window as any).__chatReconcileIntervalId = window.setInterval(() => {
              if (!activeRequestStartedAtRef.current) {
                clearInterval((window as any).__chatReconcileIntervalId);
                delete (window as any).__chatReconcileIntervalId;
                return;
              }
              void refreshTimelineRef.current?.();
            }, 10000);

            // Set a timeout to prevent infinite loading (60 seconds - longer for AI responses)
            const timeoutId = setTimeout(() => {
              const hasStreamingContent = Boolean(streamingAccumulatedRef.current && streamingAccumulatedRef.current.trim());
              setIsSending(currentIsSending => {
                if (!currentIsSending || hasStreamingContent) {
                  return currentIsSending;
                }
                setIsThinking(false);
                setThinkingMessages([]);
                setWorkStartedAt(null);
                setWorkClockTick(0);
                void refreshTimelineRef.current?.();
                activeRequestStartedAtRef.current = null;
                if ((window as any).__chatReconcileIntervalId) {
                  clearInterval((window as any).__chatReconcileIntervalId);
                  delete (window as any).__chatReconcileIntervalId;
                }
                return false;
              });
            }, 60000);

            // Store timeout ID so we can clear it when stream completes
            (window as any).__chatTimeoutId = timeoutId;
          }
          if (choiceSubmission) return true;
        } else {
          console.error('[ChatSession] Failed to send message:', response);
          const responseMessage = response?.message;
          const responseStatusCode = response?.status_code;
          setIsSending(false);
          setIsThinking(false);
          setThinkingMessages([]);
          activeRequestStartedAtRef.current = null;
          if (isSessionUnavailableError(response)) {
            rollbackChoiceSubmission();
            redirectToChatList();
            return choiceSubmission ? false : undefined;
          }
          if (choiceSubmission) {
            rollbackChoiceSubmission();
            return false;
          }
          if (
            responseStatusCode === 403 ||
            (typeof responseMessage === 'string' &&
              (responseMessage.toLowerCase().includes('limit') ||
                responseMessage.toLowerCase().includes('trial') ||
                responseMessage.toLowerCase().includes('subscribe') ||
                responseMessage.toLowerCase().includes('upgrade')))
          ) {
            setQuotaLimitMessage(responseMessage || 'Trial request limit reached. Please subscribe to continue.');
            setShowQuotaLimitAlert(true);
            return;
          }
        }
      } catch (error) {
        if (stopRequestedRef.current) {
          rollbackChoiceSubmission();
          return choiceSubmission ? false : undefined;
        }
        console.error('[ChatSession] Error sending message to SmartDocs:', error);
        console.error('[ChatSession] Error details:', {
          message: error instanceof Error ? error.message : String(error),
          response: (error as any)?.response?.data,
          status: (error as any)?.response?.status
        });

        if (isSessionUnavailableError(error)) {
          setIsSending(false);
          setIsThinking(false);
          setThinkingMessages([]);
          redirectToChatList();
          return;
        }

        const chatError = error as ChatApiError;
        const errorMessage = chatError.messageFromApi || chatError.message || '';
        const errorStatus = chatError.status || chatError.statusCode;
        const isQuotaError =
          errorStatus === 403 &&
          (errorMessage.toLowerCase().includes('limit') ||
            errorMessage.toLowerCase().includes('trial') ||
            errorMessage.toLowerCase().includes('subscribe') ||
            errorMessage.toLowerCase().includes('upgrade'));

        if (choiceSubmission) {
          rollbackChoiceSubmission();
          throw new Error(
            errorMessage || (
              serviceTitanSelection
                ? 'Unable to select that ServiceTitan option.'
                : 'Unable to send that model.'
            ),
          );
        }

        if (isQuotaError) {
          setIsSending(false);
          setIsThinking(false);
          setThinkingMessages([]);
          activeRequestStartedAtRef.current = null;
          setQuotaLimitMessage(errorMessage || 'Trial request limit reached. Please subscribe to continue.');
          setShowQuotaLimitAlert(true);
          return;
        }

        // Wait longer to see if WebSocket response comes through
        // Reasoning models can take 20-30 seconds before streaming starts
        const errorTimeoutId = setTimeout(() => {
          // Check if we've started receiving streamed content via WebSocket
          const hasStreamingContent = streamingAccumulatedRef.current && streamingAccumulatedRef.current.trim().length > 0;
          let shouldClearSending = false;

          // Check current sending and thinking state (use functional update to get latest state)
          setIsSending(currentIsSending => {
            setIsThinking(currentIsThinking => {
              // Check thinking messages using functional update to get latest state
              setThinkingMessages(currentThinkingMessages => {
                const hasThinkingActivity = currentIsThinking || currentThinkingMessages.length > 0;

                // Only show error if:
                // 1. No streaming content has arrived
                // 2. We're still in sending state (meaning no response came)
                // 3. No thinking activity (reasoning might still be processing)
                if (!hasStreamingContent && currentIsSending && !hasThinkingActivity) {
                  // Add error message to chat only if no streaming has started
                  setTimeline(prev => [...prev, {
                    id: Date.now().toString(),
                    type: 'message',
                    content: errorMessage || 'Failed to send message. Please try again.',
                    sender: 'assistant',
                    timestamp: new Date().toISOString(),
                    isTransient: true,
                    escalation_context: {
                      escalation_id: currentEscalationId,
                      is_during_escalation: hasActiveEscalation,
                      escalation_phase: hasActiveEscalation ? 'during' : 'before'
                    }
                  }]);
                  shouldClearSending = true;
                  return []; // Clear thinking messages
                } else {
                  return currentThinkingMessages; // Keep current thinking messages
                }
              });

              return shouldClearSending ? false : currentIsThinking;
            });
            if (shouldClearSending) {
              setWorkStartedAt(null);
              setWorkClockTick(0);
              return false;
            }
            return currentIsSending; // Keep current state
          });
        }, 60000); // Wait 60 seconds - reasoning models can take 30-40 seconds

        // Store timeout ID so we can clear it if response arrives
        (window as any).__errorTimeoutId = errorTimeoutId;
      }
    } catch (error) {
      if (stopRequestedRef.current) {
        return choiceSubmission ? false : undefined;
      }
      console.error('[ChatSession] Error sending message:', error);
      if (isSessionUnavailableError(error)) {
        rollbackChoiceSubmission();
        setIsSending(false);
        setIsThinking(false);
        setThinkingMessages([]);
        activeRequestStartedAtRef.current = null;
        redirectToChatList();
        return choiceSubmission ? false : undefined;
      }
      if (choiceSubmission) {
        setIsSending(false);
        setIsThinking(false);
        setThinkingMessages([]);
        throw error;
      }
      const displayMessage = (error as ChatApiError).messageFromApi ?? (error as Error).message ?? 'Failed to send message. Please try again.';
      setTimeline(prev => [...prev, {
        id: Date.now().toString(),
        type: 'message',
        content: displayMessage,
        sender: 'assistant',
        timestamp: new Date().toISOString(),
        escalation_context: {
          escalation_id: currentEscalationId,
          is_during_escalation: hasActiveEscalation,
          escalation_phase: hasActiveEscalation ? 'during' : 'before'
        }
      }]);
      setIsSending(false); // Set to false on error
      setIsThinking(false);
      setThinkingMessages([]);
      activeRequestStartedAtRef.current = null;
    }
  }, [assistantManufacturers, assistantMode, clearComposerInput, sessionId, hasActiveEscalation, currentEscalationId, getContractorName, keepPinnedSentMessageVisible, redirectToChatList, selectedManufacturerSlug]);

  const ensureSessionForDraft = useCallback(async () => {
    if (sessionId) {
      return sessionId;
    }

    const isSubscriptionUser = user?.role === 'SubscriptionUser';
    const response = await chatApi.createSession(
      isSubscriptionUser ? undefined : getDistributorName(),
      isSubscriptionUser ? undefined : getContractorName(),
      isSubscriptionUser ? undefined : DEFAULT_REGION,
    );

    if (!response.success || !response.data?.session_id) {
      throw new Error(response.message || 'Failed to create chat session');
    }

    const newSessionId = response.data.session_id;
    history.replace(`/chat/${newSessionId}`, {
      welcomeMessage: response.data.welcome_message,
      isNewSession: true,
    });
    return newSessionId;
  }, [getContractorName, getDistributorName, history, sessionId, user?.role]);

  const handleSendMessage = useCallback(async (
    messageText: string,
    submissionOptions: AssistantMessageSubmissionOptions = {},
  ) => {
    const choiceSubmission = Boolean(
      submissionOptions.serviceTitanSelection || submissionOptions.choiceSubmission,
    );
    if (isAiRunActive) {
      await handleStopAgentRun();
      return choiceSubmission ? false : undefined;
    }

    if (assistantMode === 'servicetitan' && !serviceTitan.connected) {
      setAssistantOptionsError('Connect ServiceTitan before asking a ServiceTitan question.');
      return choiceSubmission ? false : undefined;
    }

    if (assistantMode !== 'servicetitan' && !selectedManufacturerSlug) {
      setAssistantOptionsError('Choose a manufacturer before asking SmartDocs.');
      return choiceSubmission ? false : undefined;
    }

    if (isListeningToVoice) {
      voiceAcceptingTranscriptRef.current = false;
      if (Capacitor.isNativePlatform()) {
        await SpeechRecognition.stop().catch(() => undefined);
        await SpeechRecognition.removeAllListeners().catch(() => undefined);
      } else {
        try {
          speechRecognitionRef.current?.stop();
        } catch (_error) {
          // Some WebViews throw if recognition already ended.
        }
      }
      setIsListeningToVoice(false);
      setVoiceStatus(null);
    }
    const activeSessionId = await ensureSessionForDraft();
    return dispatchAiMessage(
      messageText,
      choiceSubmission ? null : selectedImage,
      activeSessionId,
      submissionOptions.serviceTitanSelection,
      submissionOptions,
    );
  }, [assistantMode, dispatchAiMessage, ensureSessionForDraft, handleStopAgentRun, isAiRunActive, isListeningToVoice, selectedImage, selectedManufacturerSlug, serviceTitan.connected]);

  const handleServiceTitanChoice = useCallback(async (
    artifact: ServiceTitanChoicePromptArtifact,
    choice: ServiceTitanChoice,
  ) => {
    const sent = await handleSendMessage(choice.display_label, {
      serviceTitanSelection: {
        selection_token: artifact.selection_token,
        choice_number: choice.choice_number,
      },
      preserveComposer: true,
      choiceSubmission: true,
    });
    if (sent === false) {
      throw new Error('ServiceTitan selection was not accepted.');
    }
  }, [handleSendMessage]);

  const handlePartsModelChoice = useCallback(async (choice: PartsModelChoice) => {
    const sent = await handleSendMessage(choice.label.trim(), {
      preserveComposer: true,
      choiceSubmission: true,
    });
    if (sent === false) {
      throw new Error('That model selection was not accepted.');
    }
  }, [handleSendMessage]);

  const handleComposerInput = useCallback((value: string) => {
    const ignoredEcho = ignoredComposerEchoRef.current;
    if (ignoredEcho && Date.now() < ignoredEcho.until && value === ignoredEcho.value) {
      return;
    }
    if (ignoredEcho && Date.now() >= ignoredEcho.until) {
      ignoredComposerEchoRef.current = null;
    }
    setMessage(value);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const detectVoiceSupport = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const { available } = await SpeechRecognition.available();
          if (isMounted) {
            setIsVoiceSupported(Boolean(available));
          }
        } catch (_error) {
          if (isMounted) {
            setIsVoiceSupported(false);
          }
        }
        return;
      }

      if (isMounted) {
        setIsVoiceSupported(Boolean(getSpeechRecognitionConstructor()));
      }
    };

    void detectVoiceSupport();

    return () => {
      isMounted = false;
      try {
        speechRecognitionRef.current?.abort();
      } catch (_error) {
        // Browser recognition cleanup can throw if it is already stopped.
      }
      if (Capacitor.isNativePlatform()) {
        void SpeechRecognition.stop().catch(() => undefined);
        void SpeechRecognition.removeAllListeners().catch(() => undefined);
      }
    };
  }, []);

  const stopVoiceInput = useCallback(async () => {
    voiceAcceptingTranscriptRef.current = false;
    if (Capacitor.isNativePlatform()) {
      await SpeechRecognition.stop().catch(() => undefined);
      await SpeechRecognition.removeAllListeners().catch(() => undefined);
      setIsListeningToVoice(false);
      setVoiceStatus(null);
      return;
    }

    try {
      speechRecognitionRef.current?.stop();
    } catch (_error) {
      // Some WebViews throw if stop is called after recognition already ended.
    }
    setIsListeningToVoice(false);
    setVoiceStatus(null);
  }, []);

  const openVoicePermissionSettings = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      await NativeSettings.open({
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App,
      });
    } catch (error) {
      console.error('[ChatSession] Failed to open native settings:', error);
    }
  }, []);

  const startVoiceInput = useCallback(() => {
    if (Capacitor.isNativePlatform()) {
      const startNativeVoiceInput = async () => {
        try {
          const { available } = await SpeechRecognition.available();
          if (!available) {
            setVoiceError('Voice input is not available on this device.');
            setVoiceStatus(null);
            return;
          }

          const currentPermission = await SpeechRecognition.checkPermissions();
          let permissionState = currentPermission.speechRecognition;

          if (permissionState !== 'granted') {
            const requestedPermission = await SpeechRecognition.requestPermissions();
            permissionState = requestedPermission.speechRecognition;
          }

          if (permissionState !== 'granted') {
            setVoiceError(null);
            setVoiceStatus(null);
            setIsListeningToVoice(false);
            setShowVoicePermissionAlert(true);
            return;
          }

          await SpeechRecognition.stop().catch(() => undefined);
          await SpeechRecognition.removeAllListeners().catch(() => undefined);

          voiceBaseMessageRef.current = message;
          voiceFinalTranscriptRef.current = '';
          voiceAcceptingTranscriptRef.current = true;
          setVoiceError(null);
          setVoiceStatus('Listening...');

          await SpeechRecognition.addListener('partialResults', ({ matches }) => {
            if (!voiceAcceptingTranscriptRef.current) {
              return;
            }
            const transcript = Array.isArray(matches) ? (matches[0] || '').trim() : '';
            const nextMessage = mergeVoiceTranscript(voiceBaseMessageRef.current, transcript);
            handleComposerInput(nextMessage);
            setVoiceStatus(transcript ? 'Listening...' : 'Voice captured.');
          });

          await SpeechRecognition.addListener('listeningState', ({ status }) => {
            const isListening = status === 'started';
            setIsListeningToVoice(isListening);
            setVoiceStatus(isListening ? 'Listening...' : null);
          });

          setIsListeningToVoice(true);
          await SpeechRecognition.start({
            language: 'en-US',
            maxResults: 1,
            partialResults: true,
            popup: false,
          });
        } catch (error) {
          console.error('[ChatSession] Native speech recognition failed to start:', error);
          voiceAcceptingTranscriptRef.current = false;
          setVoiceError('Voice input could not start. Please try again.');
          setVoiceStatus(null);
          setIsListeningToVoice(false);
        }
      };

      void startNativeVoiceInput();
      return;
    }

    const Recognition = getSpeechRecognitionConstructor();
    if (!Recognition) {
      setVoiceError('Speech-to-text is not available in this browser.');
      setVoiceStatus(null);
      return;
    }

    try {
      speechRecognitionRef.current?.abort();
    } catch (_error) {
      // Ignore stale recognizer cleanup failures.
    }

    const recognition = new Recognition();
    speechRecognitionRef.current = recognition;
    voiceBaseMessageRef.current = message;
    voiceFinalTranscriptRef.current = '';
    voiceAcceptingTranscriptRef.current = true;
    setVoiceError(null);
    setVoiceStatus('Listening...');

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onstart = () => {
      setIsListeningToVoice(true);
      setVoiceStatus('Listening...');
    };
    recognition.onend = () => {
      voiceAcceptingTranscriptRef.current = false;
      setIsListeningToVoice(false);
      setVoiceStatus(null);
    };
    recognition.onerror = (event) => {
      voiceAcceptingTranscriptRef.current = false;
      const error = event.error || 'unknown';
      const isPermissionBlocked = error === 'not-allowed' || error === 'service-not-allowed';
      const friendly = isPermissionBlocked
        ? (Capacitor.isNativePlatform() ? null : 'Microphone access is blocked in this browser.')
        : error === 'no-speech'
          ? 'I did not hear anything.'
          : 'Speech-to-text stopped. Please try again.';
      setVoiceError(friendly);
      setVoiceStatus(null);
      setIsListeningToVoice(false);
      if (isPermissionBlocked) {
        setShowVoicePermissionAlert(true);
      }
    };
    recognition.onresult = (event) => {
      if (!voiceAcceptingTranscriptRef.current) {
        return;
      }
      let interimTranscript = '';
      let finalTranscript = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript || '';
        if (result?.isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      if (finalTranscript.trim()) {
        voiceFinalTranscriptRef.current = `${voiceFinalTranscriptRef.current} ${finalTranscript}`.trim();
      }
      const combinedTranscript = `${voiceFinalTranscriptRef.current} ${interimTranscript}`.trim();
      const nextMessage = mergeVoiceTranscript(voiceBaseMessageRef.current, combinedTranscript);
      handleComposerInput(nextMessage);
      setVoiceStatus(interimTranscript.trim() ? 'Listening...' : 'Voice captured.');
    };

    try {
      recognition.start();
    } catch (_error) {
      voiceAcceptingTranscriptRef.current = false;
      setVoiceError('Speech-to-text could not start. Please try again.');
      setVoiceStatus(null);
      setIsListeningToVoice(false);
    }
  }, [handleComposerInput, message]);

  const toggleVoiceInput = useCallback(() => {
    if (isListeningToVoice) {
      void stopVoiceInput();
      return;
    }
    startVoiceInput();
  }, [isListeningToVoice, startVoiceInput, stopVoiceInput]);

  const handleQueuedSend = useCallback(async () => {
    if (!queuedDraft || !sessionId || hasActiveEscalation) {
      return;
    }
    const draft = queuedDraft;
    try {
      setIsQueueSubmitting(true);

      if (!isAiRunActive) {
        await dispatchAiMessage(draft.text, draft.image);
        setQueuedDraft(null);
        setIsQueueSubmitting(false);
        return;
      }

      stopRequestedRef.current = true;
      await chatApi.stopMessageToAgent(getContractorName(), sessionId);
      clearAiLoadingState(true);
      setQueuedDraft(null);
      await dispatchAiMessage(draft.text, draft.image);
    } catch (error) {
      console.error('[ChatSession] Error queueing follow-up message:', error);
    } finally {
      setIsQueueSubmitting(false);
    }
  }, [clearAiLoadingState, dispatchAiMessage, getContractorName, hasActiveEscalation, isAiRunActive, queuedDraft, sessionId]);

  const handleQueuedEnter = useCallback((event: React.KeyboardEvent) => {
    if (!isAiRunActive || queuedDraft || hasActiveEscalation) {
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      queueCurrentDraft(message);
    }
  }, [hasActiveEscalation, isAiRunActive, message, queueCurrentDraft, queuedDraft]);

  useEffect(() => {
    if (!queuedDraft || queuedDraft.serverQueued || isAiRunActive || isQueueSubmitting || hasActiveEscalation || !sessionId) {
      return;
    }
    if (isAutoDispatchingQueuedDraftRef.current) {
      return;
    }

    isAutoDispatchingQueuedDraftRef.current = true;
    setIsQueueSubmitting(true);

    const autoDispatchQueuedDraft = async () => {
      try {
        await dispatchAiMessage(queuedDraft.text, queuedDraft.image);
        setQueuedDraft(null);
      } catch (error) {
        console.error('[ChatSession] Error auto-sending queued follow-up:', error);
      } finally {
        setIsQueueSubmitting(false);
        isAutoDispatchingQueuedDraftRef.current = false;
      }
    };

    autoDispatchQueuedDraft();
  }, [dispatchAiMessage, hasActiveEscalation, isAiRunActive, isQueueSubmitting, queuedDraft, sessionId]);

  // Format assistant message text as markdown; rich cards/previews stay in structured UI paths.
  const formatMessageText = (text: string) => {
    const simplifiedText = normalizeBareBracketedAppLinks(
      normalizeBareAppUrls(
        unwrapBacktickedMarkdownLinks(simplifyWorkOrderResponse(text)),
      ),
    );
    const textWithLinks = formatAllLinks(simplifiedText);
    const parsedWorkOrderItems = parseWorkOrderResults(textWithLinks);
    const workOrderItems = parsedWorkOrderItems.length > 0
      ? parsedWorkOrderItems
      : extractWorkOrderLinks(textWithLinks);
    if (workOrderItems.length > 0) {
      return (
        <WorkOrderResultList
          intro={parsedWorkOrderItems.length > 0 ? extractWorkOrderIntro(textWithLinks) : undefined}
          items={workOrderItems}
          onOpen={handleOpenWorkOrderResult}
          onOpenExternal={(url) => void openUrl(url)}
          refreshKey={timeline.length}
        />
      );
    }

    const markdownTextForDisplay = hidePreviewedArtifactImageLinks(simplifiedText);
    const content = decodeHtmlEntities(markdownTextForDisplay);

    return (
      <>
        <HtmlContent>{renderMarkdownBlocks(content)}</HtmlContent>
        <OemArtifactImagePreviews
          html={textWithLinks}
          onOpen={setSelectedArtifactImage}
        />
      </>
    );
  };

  // Helper function to add escalation event
  const addEscalationEvent = useCallback((event: Omit<EscalationEvent, 'id'>) => {
    const newEvent: EscalationEvent = {
      ...event,
      id: Date.now().toString(),
    };

    // Add escalation event to timeline
    let content = '';
    if (event.type === 'start') {
      // Parse UTC timestamp correctly for display
      const utcDateString = event.timestamp.endsWith('Z') ? event.timestamp : event.timestamp + 'Z';
      const localDate = new Date(utcDateString);
      content = `You escalated at ${localDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else if (event.type === 'connect') {
      content = `You are now connected to ${event.agentName || 'Live Agent'}`;
    } else if (event.type === 'resolve') {
      // Parse UTC timestamp correctly for display
      const utcDateString = event.timestamp.endsWith('Z') ? event.timestamp : event.timestamp + 'Z';
      const localDate = new Date(utcDateString);
      content = `Escalation resolved at ${localDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    }

    setTimeline(prev => {
      const hasEvent = prev.some(msg =>
        msg.type === 'escalation_event' &&
        msg.escalation_context?.escalation_id === newEvent.escalationId &&
        msg.escalation_context?.escalation_phase === (event.type === 'resolve' ? 'after' : 'during') &&
        msg.content === content
      );

      if (hasEvent) {
        return prev;
      }

      return [...prev, {
        id: newEvent.id,
        type: 'escalation_event',
        content,
        timestamp: newEvent.timestamp,
        sender: 'assistant',
        escalation_context: {
          escalation_id: newEvent.escalationId,
          is_during_escalation: event.type === 'start' || event.type === 'connect',
          escalation_phase: event.type === 'resolve' ? 'after' : 'during'
        }
      }];
    });

  // Also keep track of escalation events for state management
    setEscalationEvents((prev: any[]) => {
      const hasEvent = prev.some(existing =>
        existing.type === newEvent.type &&
        existing.escalationId === newEvent.escalationId
      );
      return hasEvent ? prev : [...prev, newEvent];
    });
  }, []);

  const createEscalationFromCollectedData = useCallback(async (collectedData: Record<string, unknown>) => {
    if (!sessionId) {
      throw new Error('Session ID is required');
    }

    setIsEscalating(true);

    try {
      const response = await api.post('/escalations/create', {
        session_id: sessionId,
        reason: 'User requested human assistance',
        collected_data: collectedData
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to create escalation');
      }

      const escalationId = response.data.data.event_id;
      setCurrentEscalationIdAndRef(escalationId);
      setActiveEscalationId(escalationId);
      setEscalationResolvedAt(null);
      setAgentName('');
      setHasActiveEscalation(true);

      addEscalationEvent({
        type: 'start',
        timestamp: new Date().toISOString(),
        escalationId
      });

      setTimeline(prev => [...prev, {
        id: `connecting-${escalationId}`,
        type: 'escalation_event',
        content: 'Connecting you to a live agent...',
        timestamp: new Date().toISOString(),
        sender: 'assistant',
        escalation_context: {
          escalation_id: escalationId,
          is_during_escalation: true,
          escalation_phase: 'during'
        }
      }]);
    } finally {
      setIsEscalating(false);
    }
  }, [addEscalationEvent, sessionId, setCurrentEscalationIdAndRef]);

  // Effect to fetch timeline from API when session changes
  useEffect(() => {
    let cancelled = false;
    const fetchTimeline = async (options: {
      showLoading?: boolean;
      preserveScroll?: boolean;
      force?: boolean;
    } = {}) => {
      if (!sessionId || sessionUnavailableRef.current) return false;
      const showLoading = options.showLoading !== false;
      const shouldShowLoading = showLoading && !getCachedTimeline(sessionId);
      const container = messageContainerRef.current || chatContainerRef.current;
      const previousScrollTop = container?.scrollTop ?? 0;
      const previousScrollHeight = container?.scrollHeight ?? 0;

      try {
        if (shouldShowLoading) {
          setIsLoadingHistory(true);
        }

        const runStartedWhenRequested = activeAiRunStartedAtRef.current;
        const awaitingServerMessageIdWhenRequested = awaitingServerMessageIdRef.current;
        const isReconcilingActiveRun = runStartedWhenRequested > 0 || Boolean(awaitingServerMessageIdWhenRequested);
        const response = await chatApi.getContractorTimeline(sessionId, {
          force: options.force ?? isReconcilingActiveRun,
          fallbackToStale: !isReconcilingActiveRun,
        });
        if (cancelled) return false;

        if (response.success) {
          setCurrentSessionTitle(response.data.session?.title ?? null);
          // Process timeline to fix escalation timestamps
          const processedTimeline = response.data.timeline.map((item: any) => {
            if (item.type === 'escalation_event') {
              // Fix escalation timestamps that come from backend
              if (item.content.includes('You escalated at') || item.content.includes('Escalation resolved at')) {
                // Extract the timestamp from the content and convert to local time
                const timeMatch = item.content.match(/(\d{2}:\d{2})/);
                if (timeMatch) {
                  // Create a proper UTC timestamp and convert to local time
                  const utcDateString = item.timestamp.endsWith('Z') ? item.timestamp : item.timestamp + 'Z';
                  const localDate = new Date(utcDateString);
                  const localTime = localDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                  // Replace the UTC time in content with local time
                  if (item.content.includes('You escalated at')) {
                    item.content = `You escalated at ${localTime}`;
                  } else if (item.content.includes('Escalation resolved at')) {
                    item.content = `Escalation resolved at ${localTime}`;
                  }
                }
              }
            }
            return item;
          });

          // The backend returns database rows in authoritative creation order.
          // Preserve that order instead of trying to reconstruct causality from
          // timestamps that may have identical precision.
          const serverTimeline = processedTimeline;

          // Keep the request's run marker as well as the current one. A stream can
          // complete between awaiting this request and applying its response; that
          // response may still be a pre-completion snapshot.
          const activeRunStartedAt = activeAiRunStartedAtRef.current || runStartedWhenRequested;
          const awaitingServerMessageId = awaitingServerMessageIdRef.current || awaitingServerMessageIdWhenRequested;
          const serverHasAwaitedMessage = Boolean(
            awaitingServerMessageId && serverTimeline.some(item => item?.id === awaitingServerMessageId),
          );
          const hasCompletedActiveRun =
            serverHasAwaitedMessage || (
              activeRunStartedAt > 0 &&
              serverTimeline.some((item: any) => {
              const itemTime = timelineTimestampValue(item?.timestamp);
              return (
                item?.type === 'message' &&
                item?.sender === 'assistant' &&
                item?.id !== 'welcome_message' &&
                item?.feedback_eligible !== false &&
                itemTime >= activeRunStartedAt - 2000
              );
              })
            );

          setTimeline(currentTimeline => {
            const shouldPreserveLiveItems =
              (activeRunStartedAt > 0 && !hasCompletedActiveRun) ||
              Boolean(awaitingServerMessageId && !serverHasAwaitedMessage);
            if (!shouldPreserveLiveItems) {
              return serverTimeline;
            }
            return mergeServerTimeline(serverTimeline, currentTimeline);
          });
          if (serverHasAwaitedMessage) {
            awaitingServerMessageIdRef.current = null;
          }
          const requestStartedAt = activeRequestStartedAtRef.current;
          const persistedAssistantResponse = requestStartedAt
            ? [...serverTimeline].reverse().find((item: any) => {
                if (item.type !== 'message' || item.sender !== 'assistant' || !item.timestamp) {
                  return false;
                }
                const timestamp = timelineTimestampValue(item.timestamp);
                return timestamp >= requestStartedAt - 2000;
              })
            : null;
          if (persistedAssistantResponse) {
            clearAiLoadingState();
          }
          if (options.preserveScroll) {
            skipNextTimelineAutoScrollRef.current = true;
            restoreScrollAfterTimelineRefresh(previousScrollTop, previousScrollHeight);
          }
          setEscalationEvents(response.data.escalation_events);

          // Group escalation events by escalation_id
          const escalationGroups: { [key: string]: any[] } = {};
          response.data.escalation_events.forEach((event: any) => {
            if (!escalationGroups[event.escalation_id]) {
              escalationGroups[event.escalation_id] = [];
            }
            escalationGroups[event.escalation_id].push(event);
          });

          // Find active escalations (start events without corresponding resolve events)
          const activeEscalations = Object.entries(escalationGroups).filter(([, events]) => {
            const hasStart = events.some((event: any) => event.type === 'start');
            const hasResolve = events.some((event: any) => event.type === 'resolve');
            return hasStart && !hasResolve;
          });

          const hasActiveEscalation = activeEscalations.length > 0;

          setHasActiveEscalation(hasActiveEscalation);

          if (hasActiveEscalation) {
            clearAiLoadingState(true);
            const [activeEscalationId, activeEvents] = activeEscalations[0];
            setCurrentEscalationIdAndRef(activeEscalationId);

            // Check if agent has joined
            const connectEvent = activeEvents.find((event: any) => event.type === 'connect');
            if (connectEvent) {
              setEscalationStatusWithRef('connected');
              setAgentName(connectEvent.agent_name || 'Live Agent');
            } else {
              setEscalationStatusWithRef('pending');
              setAgentName('');
            }
          } else {
            // Check if there are any resolved escalations
            const resolvedEscalations = Object.entries(escalationGroups).filter(([, events]) => {
              return events.some((event: any) => event.type === 'resolve');
            });

            const hasResolvedEscalations = resolvedEscalations.length > 0;

            if (hasResolvedEscalations) {
              setEscalationStatusWithRef('resolved');
              // Find the most recent resolved escalation
              const mostRecentResolved = resolvedEscalations
                .map(([escalationId, events]) => {
                  const resolveEvent = events.find((event: any) => event.type === 'resolve');
                  return { escalationId, timestamp: resolveEvent.timestamp };
                })
                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0];

              if (mostRecentResolved) {
                setEscalationResolvedAt(mostRecentResolved.timestamp);
              }
            } else {
              setEscalationStatusWithRef('none');
            }

            // Clear escalation-related state when no active escalation
            setCurrentEscalationIdAndRef(null);
            setAgentName('');
            setActiveEscalationId(null);
          }
        }
      } catch (error) {
        if (cancelled) {
          return false;
        }
        if (isSessionUnavailableError(error)) {
          redirectToChatList();
          return false;
        }
        console.error('[ChatSession] Error fetching timeline:', error);
      } finally {
        if (shouldShowLoading) {
          setIsLoadingHistory(false);
        }
      }
    };

    refreshTimelineRef.current = fetchTimeline;
    fetchTimeline();
    return () => {
      cancelled = true;
      refreshTimelineRef.current = null;
    };
  // Timeline refresh is keyed by the session. Callback dependencies are read
  // through refs/current state and must not restart the reconciliation loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectToChatList, sessionId]);

  // If this page is remounted while an agent turn is still running, reconnecting
  // the socket handles new chunks and these bounded checks reconcile anything that
  // was completed while the user was on another screen. The timeline endpoint is
  // authoritative once the final assistant message has been persisted.
  useEffect(() => {
    if (!sessionId) return;
    const restoredRun = getChatSessionLiveSnapshot(sessionId);
    if (!restoredRun?.activeRunStartedAt) return;

    let cancelled = false;
    const timeouts: number[] = [];
    const clearPolls = () => {
      timeouts.forEach(timeoutId => window.clearTimeout(timeoutId));
      timeouts.length = 0;
    };

    [2500, 6000, 12_000, 20_000, 35_000, 55_000, 85_000].forEach(delay => {
      timeouts.push(window.setTimeout(async () => {
        if (cancelled || activeAiRunStartedAtRef.current <= 0) return;
        const reconciled = await refreshTimelineRef.current?.({
          showLoading: false,
          preserveScroll: true,
          force: true,
        });
        if (reconciled) clearPolls();
      }, delay));
    });

    return () => {
      cancelled = true;
      clearPolls();
    };
  }, [sessionId]);

  // Enhanced keyboard handling for web browsers (fallback)
  useEffect(() => {
    // Only use web keyboard detection if not on native platform
    if (Capacitor.isNativePlatform()) {
      return;
    }

    let removeListeners: (() => void) | undefined;

    // Use visualViewport API for better keyboard detection on web
    if (window.visualViewport) {
      const handleViewportChange = () => {
        const viewport = window.visualViewport;
        if (viewport) {
          const windowHeight = window.innerHeight;
          const viewportHeight = viewport.height;
          // Manual keyboard height calculation - commented out since Ionic now handles it properly
          const keyboardHeight = Math.max(windowHeight - viewportHeight, 0);

          // Lower threshold for web to detect keyboard more reliably
          const threshold = 100;
          if (keyboardHeight > threshold) {
            // const height = Math.max(keyboardHeight, 250); // Ensure minimum height
            setIsKeyboardOpen(true);
            // setKeyboardHeight(height);
            // Set CSS variable for web - commented out since Ionic handles it
            // document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
            // Add delay to ensure DOM updates before scrolling
            setTimeout(() => {
              if (!keepPinnedSentMessageVisible(false)) {
                scheduleScrollToBottom(true); // Smooth scroll for keyboard events
              }
            }, 300);
          } else {
            setIsKeyboardOpen(false);
            // setKeyboardHeight(0);
            // Add a small delay before removing CSS variable to prevent layout shift - commented out since Ionic handles it
            // setTimeout(() => {
            //   document.documentElement.style.removeProperty('--keyboard-height');
            // }, 100);
            // Add delay to ensure DOM updates before scrolling
            setTimeout(() => {
              if (!keepPinnedSentMessageVisible(false)) {
                scheduleScrollToBottom(true); // Smooth scroll for keyboard events
              }
            }, 300);
          }
        }
      };

      // Initial check
      handleViewportChange();

      window.visualViewport.addEventListener('resize', handleViewportChange);
      removeListeners = () => {
        window.visualViewport?.removeEventListener('resize', handleViewportChange);
      };
    } else {
      // Fallback for browsers without visualViewport API
      const handleResize = () => {
        const currentHeight = window.innerHeight;
        const previousHeight = window.visualViewport ? window.visualViewport.height : currentHeight;
        const heightDifference = Math.abs(currentHeight - previousHeight);

        if (heightDifference > 150) {
          // const height = Math.max(heightDifference, 250); // Ensure minimum height
          setIsKeyboardOpen(true);
          // setKeyboardHeight(height);
          // document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
          setTimeout(() => {
            if (!keepPinnedSentMessageVisible(false)) {
              scheduleScrollToBottom(true); // Smooth scroll for keyboard events
            }
          }, 300);
        } else {
          setIsKeyboardOpen(false);
          // setKeyboardHeight(0);
          // Add a small delay before removing CSS variable to prevent layout shift - commented out since Ionic handles it
          // setTimeout(() => {
          //   document.documentElement.style.removeProperty('--keyboard-height');
          // }, 100);
          setTimeout(() => {
            if (!keepPinnedSentMessageVisible(false)) {
              scheduleScrollToBottom(true); // Smooth scroll for keyboard events
            }
          }, 300);
        }
      };

      window.addEventListener('resize', handleResize);
      removeListeners = () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    return () => {
      if (removeListeners) removeListeners();
    };
  }, [keepPinnedSentMessageVisible, scheduleScrollToBottom]);

  // App lifecycle management for socket reconnection only
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const handleAppResume = async () => {
      if (socketRef.current && !socketRef.current.connected) {
        try {
          socketRef.current.connect();
        } catch (error) {
          console.error('[ChatSession] Error reconnecting socket:', error);
        }
      }
    };

    const removeListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        handleAppResume();
      }
    });

    return () => {
      removeListener.remove();
    };
  }, []);

  return (
    <IonPage>
      {showBanner && (
        <Banner
          message={campaign?.message ?? ''}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}
      <IonLoading
        isOpen={showGlobalLoading}
        message={'Please wait...'}
        spinner="dots"
      />

      <IonAlert
        isOpen={showQuotaLimitAlert}
        onDidDismiss={() => setShowQuotaLimitAlert(false)}
        header="Quota reached"
        message={quotaLimitMessage}
        buttons={[
          {
            text: 'Not now',
            role: 'cancel',
          },
          {
            text: 'Subscribe',
            handler: () => history.push('/subscribe'),
          },
        ]}
      />

      <AppHeader style={showBanner ? { marginTop: '48px' } : {}}>
          <BackButton onClick={() => history.push('/')} slot="start">
            <IonIcon icon={chevronBackOutline} />
          </BackButton>
          <HeaderContent>
            <HeaderLogo />
            {lastUpdateTime && <TimeIndicator>{lastUpdateTime}</TimeIndicator>}
            <HeaderActions>
              {user?.role === 'SubscriptionUser' && (
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); history.push('/subscribe'); }}
                  style={{ fontSize: '12px', opacity: 0.9, textDecoration: 'underline', whiteSpace: 'nowrap' }}
                >
                  Upgrade plan
                </a>
              )}
              <HeaderActionButton
                fill="clear"
                aria-label="Chat settings"
                title="Chat settings"
                onClick={openSessionTitleModal}
              >
                <IonIcon slot="icon-only" icon={settingsOutline} />
              </HeaderActionButton>
            </HeaderActions>
          </HeaderContent>
      </AppHeader>

      <IonContent
          ref={ionContentRef}
          fullscreen
          forceOverscroll={false}
          scrollY
          scrollEvents
          onIonScroll={handleChatScroll}
          style={{
          }}
        >
        <ChatContainer ref={chatContainerRef}>
          <MessageContainer ref={messageContainerRef}>
            {isLoadingHistory ? (
              <ScreenLoader label="Loading chat session" />
            ) : (
              <>
                {/* Note: Loading more removed - timeline API returns all messages */}

                {/* Unified Message Timeline */}
                {timeline
                  .filter((msg) => {
                    // Filter out empty messages to prevent empty message bubbles
                    if (msg.type === 'message' && (!msg.content || !msg.content.trim()) && !msg.has_image) {
                      return false;
                    }
                    return true;
                  })
                  .map((msg, index) => {
                  // Handle escalation events (system messages)
                  if (msg.type === 'escalation_event') {
                    return (
                      <EscalationSeparator key={`separator-${msg.id || index}`}>
                        <div>{msg.content}</div>
                      </EscalationSeparator>
                    );
                  }

                  // Handle regular messages
                  if (msg.type === 'message') {
                    const feedback = msg.feedback as MessageFeedback | null | undefined;
                    const timelineIndex = timeline.indexOf(msg);
                    const previousUserMessage = timeline
                      .slice(0, timelineIndex >= 0 ? timelineIndex : index)
                      .reverse()
                      .find(item => item.type === 'message' && item.sender === 'user')?.content;
                    const hasLaterUserMessage = timeline
                      .slice((timelineIndex >= 0 ? timelineIndex : index) + 1)
                      .some(item => item.type === 'message' && item.sender === 'user');
                    const assistantReferences = (msg as MessageDisplay).reference_documents;
                    // Timeline API sets feedback_eligible; live WebSocket/stream rows omit it — treat missing as eligible.
                    const canShowFeedback =
                      msg.sender === 'assistant' &&
                      Boolean(msg.id) &&
                      msg.id !== 'welcome_message' &&
                      msg.feedback_eligible !== false;
                    return (
                      <Message
                        key={`msg-${msg.id || index}`}
                        $isUser={msg.sender === 'user'}
                        data-chat-message-id={msg.id || `message-${index}`}
                      >
                        <>
                          {msg.image_url && (
                            <img
                              src={msg.image_url}
                              alt={msg.image_filename || 'Chat upload'}
                              style={{
                                maxWidth: '100%',
                                maxHeight: '280px',
                                objectFit: 'contain',
                                borderRadius: '12px',
                                marginBottom: shouldHideTimelineText(msg) ? 0 : '10px',
                                background: 'rgba(0, 0, 0, 0.04)',
                              }}
                            />
                          )}
                          {!shouldHideTimelineText(msg) && formatMessageText(msg.content)}
                          {msg.sender === 'assistant' && (
                            <OemReferencePagePreviews
                              references={getRenderedPageReferences(assistantReferences)}
                              artifacts={(msg as MessageDisplay).artifacts}
                              onOpen={setSelectedArtifactImage}
                            />
                          )}
                          {msg.sender === 'assistant' && ((msg as MessageDisplay).artifacts || [])
                            .filter(isServiceTitanChoicePromptArtifact)
                            .map(artifact => (
                              <ServiceTitanChoiceCard
                                key={`servicetitan-choice-${artifact.selection_token}`}
                                artifact={artifact}
                                disabled={assistantMode !== 'servicetitan' || !serviceTitan.connected || hasActiveEscalation || isAiRunActive || hasLaterUserMessage}
                                disabledMessage={assistantMode !== 'servicetitan'
                                  ? 'Switch back to ServiceTitan to use these choices.'
                                  : (!serviceTitan.connected
                                    ? 'Connect ServiceTitan to use these choices.'
                                    : (hasActiveEscalation
                                      ? 'ServiceTitan choices are unavailable while a human support chat is active.'
                                      : (hasLaterUserMessage
                                        ? 'This choice has already been answered. Ask SmartDocs to show the choices again if you need to change it.'
                                        : undefined)))}
                                onSelect={choice => handleServiceTitanChoice(artifact, choice)}
                              />
                            ))}
                          {msg.sender === 'assistant' && ((msg as MessageDisplay).artifacts || [])
                            .filter(isPartsModelChoicePromptArtifact)
                            .map(artifact => {
                              const wrongManufacturer = artifact.manufacturer_slug !== selectedManufacturerSlug;
                              const disabled = wrongManufacturer
                                || hasActiveEscalation
                                || isAiRunActive
                                || hasLaterUserMessage;
                              const disabledMessage = wrongManufacturer
                                  ? 'Switch back to the manufacturer used for this parts search.'
                                  : (hasActiveEscalation
                                    ? 'Model choices are unavailable while a human support chat is active.'
                                    : (hasLaterUserMessage
                                      ? 'This choice has already been answered. Ask SmartDocs to list the models again if needed.'
                                      : undefined));
                              return (
                                <PartsModelChoiceCard
                                  key={`parts-model-choice-${msg.id}-${artifact.manufacturer_slug}`}
                                  artifact={artifact}
                                  disabled={disabled}
                                  disabledMessage={disabledMessage}
                                  onSelect={handlePartsModelChoice}
                                />
                              );
                            })}
                          {msg.sender === 'assistant' && ((msg as MessageDisplay).artifacts || [])
                            .filter(artifact => artifact.kind === 'servicetitan_equipment_review')
                            .map(artifact => (
                              <ServiceTitanEquipmentReviewCard
                                key={`servicetitan-equipment-${artifact.draft_id}`}
                                sessionId={id}
                                artifact={artifact as ServiceTitanEquipmentReviewArtifact}
                                onUpdated={(updatedArtifact) => {
                                  setTimeline(previous => previous.map(item => {
                                    if (item.id !== msg.id) return item;
                                    return {
                                      ...item,
                                      artifacts: (item.artifacts || []).map((candidate: AgentArtifactItem) => (
                                        candidate.draft_id === updatedArtifact.draft_id
                                          ? updatedArtifact
                                          : candidate
                                      )),
                                    };
                                  }));
                                }}
                              />
                            ))}
                          {msg.sender === 'assistant' && shouldShowReferenceDocumentsTray(assistantReferences, msg.content, previousUserMessage)
                            ? renderReferenceDocuments(assistantReferences, msg.id || `message-${index}`)
                            : null}
                          {canShowFeedback && (
                            <FeedbackActions>
                              <FeedbackButton
                                size="small"
                                fill="clear"
                                onClick={() => handleCopyMessage(msg.content ?? '')}
                                aria-label="Copy to clipboard"
                              >
                                <IonIcon icon={copyOutline} style={{ fontSize: FEEDBACK_ICON_PX }} />
                              </FeedbackButton>
                              <FeedbackButton
                                size="small"
                                fill="clear"
                                onClick={() => handleShareMessage(msg.content ?? '')}
                                aria-label="Share message"
                              >
                                <IonIcon icon={shareOutline} style={{ fontSize: FEEDBACK_ICON_PX }} />
                              </FeedbackButton>
                              {/* Once a rating exists only show the chosen icon (locked, no re-tap). */}
                              {!feedback?.rating || feedback.rating === 'up' ? (
                                <FeedbackButton
                                  size="small"
                                  fill="clear"
                                  $selected={feedback?.rating === 'up'}
                                  disabled={isSubmittingFeedback || !!feedback?.rating}
                                  onClick={feedback?.rating ? undefined : () => handleThumbsUp(msg.id)}
                                  aria-label="Thumbs up"
                                >
                                  <IonIcon
                                    icon={feedback?.rating === 'up' ? thumbsUp : thumbsUpOutline}
                                    style={{ fontSize: FEEDBACK_ICON_PX }}
                                  />
                                </FeedbackButton>
                              ) : null}
                              {!feedback?.rating || feedback.rating === 'down' ? (
                                <FeedbackButton
                                  size="small"
                                  fill="clear"
                                  $selected={feedback?.rating === 'down'}
                                  disabled={isSubmittingFeedback || !!feedback?.rating}
                                  onClick={feedback?.rating ? undefined : () => openDownvoteModal(msg.id)}
                                  aria-label="Thumbs down"
                                >
                                  <IonIcon
                                    icon={feedback?.rating === 'down' ? thumbsDown : thumbsDownOutline}
                                    style={{ fontSize: FEEDBACK_ICON_PX }}
                                  />
                                </FeedbackButton>
                              ) : null}
                            </FeedbackActions>
                          )}
                        </>
                      </Message>
                    );
                  }

                  return null;
                })}

                {/* Show a subtle runtime stream while the assistant is working. */}
                {isRuntimeWorking && (
                  <RuntimeStreamPanel key="runtime-stream">
                    <RuntimeStreamHeader>Working for {liveWorkElapsed}</RuntimeStreamHeader>
                    {runtimeDisplayItems.map((item, stepIndex) => (
                      <RuntimeStreamDisplayRow
                        item={item}
                        index={stepIndex}
                        key={'entries' in item
                          ? `command-group-${stepIndex}-${item.entries.length}`
                          : `${item.eventName}-${item.label}-${stepIndex}`}
                      />
                    ))}
                  </RuntimeStreamPanel>
                )}

              </>
            )}

            {/* Spacer div to ensure last message is visible above footer - height accounts for footer overlay */}
            <div
              ref={messagesEndRef}
              style={{ height: `${messageEndSpacerHeight}px`, flexShrink: 0, width: '100%' }}
            />
          </MessageContainer>
        </ChatContainer>
    </IonContent>

      <IonActionSheet
        isOpen={showAttachActionSheet}
        header={jobPhotoTargetId ? 'Add photo to job' : 'Add to chat'}
        subHeader={jobPhotoTargetId ? 'Choose a jobsite photo to save directly to this job.' : 'Add something now, or open saved job context.'}
        onDidDismiss={() => setShowAttachActionSheet(false)}
        buttons={
          [
            {
              text: 'Take job photo',
              icon: cameraOutline,
              handler: () => {
                void handleNativeImageSelection(CameraSource.Camera);
              },
            },
            {
              text: 'Choose photo',
              icon: imagesOutline,
              handler: () => {
                if (Capacitor.isNativePlatform()) {
                  void handleNativeImageSelection(CameraSource.Photos);
                } else {
                  fileInputRef.current?.click();
                }
              },
            },
            ...(jobPhotoTargetId ? [] : [{
              text: 'Jobs',
              icon: briefcaseOutline,
              handler: handleJobsPanelOpen,
            }]),
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => {
                setJobPhotoTargetId(null);
              },
            },
          ]
        }
      />

      <JobsModal
        isOpen={showJobsModal}
        onDidDismiss={() => setShowJobsModal(false)}
      >
        <IonContent className="jobs-modal-content">
          <JobsModalBody>
            <JobsModalHeader>
              <JobsModalHeaderText>
                <JobsModalTitle>Jobs</JobsModalTitle>
                <JobsModalSubtitle>Photos, chats, and work orders from the jobsite.</JobsModalSubtitle>
              </JobsModalHeaderText>
              <JobsModalCloseActions>
                <IonButton
                  fill="clear"
                  color="medium"
                  aria-label="Close jobs"
                  title="Close"
                  onClick={() => setShowJobsModal(false)}
                >
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </JobsModalCloseActions>
            </JobsModalHeader>

            <JobsModalContent>
              {jobsViewMode === 'list' ? (
                <JobsBrowser>
                  <JobSearchRow>
                    <JobSearchBox>
                      <IonIcon icon={searchOutline} />
                      <input
                        value={jobSearchQuery}
                        onChange={(event) => setJobSearchQuery(event.target.value)}
                        placeholder="Search jobs..."
                        aria-label="Search jobs"
                      />
                    </JobSearchBox>
                    <JobFilterButton type="button">
                      Filter
                    </JobFilterButton>
                  </JobSearchRow>

                  {isLoadingAgentJobs && (
                    <JobsLoadingState>Loading saved jobs...</JobsLoadingState>
                  )}

                  <JobRows>
                    {filteredJobPanelJobs.map((job) => {
                      const isDraft = job.status === 'unassigned';
                      return (
                        <JobRow
                          key={job.id}
                          type="button"
                          onClick={() => handleSelectJobFromList(job.id)}
                        >
                          <JobRowIcon>
                            <IonIcon icon={briefcaseOutline} />
                          </JobRowIcon>
                          <JobRowMain>
                            <JobRowTitle>{job.name}</JobRowTitle>
                            <JobRowMeta>{job.subtitle}</JobRowMeta>
                            <JobRowUpdated>{job.updatedLabel}</JobRowUpdated>
                          </JobRowMain>
                          <JobRowSide>
                            <JobStatusPill $tone={isDraft ? 'gray' : 'blue'}>
                              {isDraft ? 'Draft' : 'Saved'}
                            </JobStatusPill>
                            <IonIcon icon={chevronForwardOutline} />
                          </JobRowSide>
                        </JobRow>
                      );
                    })}
                  </JobRows>

                  {!isLoadingAgentJobs && filteredJobPanelJobs.length === 0 && (
                    <EmptyJobState>
                      No jobs match that search.
                    </EmptyJobState>
                  )}
                </JobsBrowser>
              ) : (
                <JobDetail>
                  {selectedJobPanelJob ? (
                    <>
                      <JobDetailHero>
                        <JobDetailBackButton type="button" onClick={() => setJobsViewMode('list')}>
                          <IonIcon icon={chevronBackOutline} />
                          Jobs
                        </JobDetailBackButton>
                        <div>
                          <JobDetailTitle>{selectedJobPanelJob.name}</JobDetailTitle>
                          <JobDetailMeta>
                            <span>{selectedJobPanelJob.subtitle}</span>
                            <span>{selectedJobPanelJob.updatedLabel}</span>
                            <JobBadge $tone={selectedJobPanelJob.status === 'saved' ? 'green' : 'gray'}>
                              {selectedJobPanelJob.status === 'saved' ? 'Saved' : 'Not saved'}
                            </JobBadge>
                          </JobDetailMeta>
                        </div>
                        <JobStatGrid>
                          <JobStat>
                            <JobStatValue>
                              <IonIcon icon={imagesOutline} />
                              {selectedJobPanelJob.photos.length}
                            </JobStatValue>
                            <JobStatLabel>Photos</JobStatLabel>
                          </JobStat>
                          <JobStat>
                            <JobStatValue>
                              <IonIcon icon={chatbubblesOutline} />
                              {selectedJobPanelJob.conversationCount}
                            </JobStatValue>
                            <JobStatLabel>Chats</JobStatLabel>
                          </JobStat>
                          <JobStat>
                            <JobStatValue>
                              <IonIcon icon={documentTextOutline} />
                              {selectedJobPanelJob.workOrderCount}
                            </JobStatValue>
                            <JobStatLabel>Work orders</JobStatLabel>
                          </JobStat>
                        </JobStatGrid>
                      </JobDetailHero>

                      <JobDetailActions>
                        <JobActionButton $primary onClick={handleCreateJobFromChat}>
                          <IonIcon icon={briefcaseOutline} />
                          Save as job
                        </JobActionButton>
                        <JobActionButton onClick={handleAddJobPhotoFromPanel} disabled={isSavingJobPhoto}>
                          <IonIcon icon={cameraOutline} />
                          {isSavingJobPhoto ? 'Adding...' : 'Add photo'}
                        </JobActionButton>
                        <JobActionButton onClick={handleFindWorkOrdersFromPanel}>
                          <IonIcon icon={documentTextOutline} />
                          Get work orders
                        </JobActionButton>
                      </JobDetailActions>

                      <JobSection>
                        <JobSectionTitle>
                          <IonIcon icon={imagesOutline} />
                          Photos
                        </JobSectionTitle>
                        {selectedJobPanelJob.photos.length > 0 ? (
                          <JobPhotoGrid>
                            {selectedJobPanelJob.photos.map(photo => (
                              <JobPhotoTile key={photo.id}>
                                <JobPhotoThumb
                                  type="button"
                                  onClick={() => handleOpenJobPhoto(photo)}
                                >
                                  <img src={photo.url} alt={photo.filename || 'Job photo'} />
                                </JobPhotoThumb>
                                {selectedJobPanelJob.agentJobId ? (
                                  <JobPhotoRemoveButton
                                    type="button"
                                    title="Remove photo"
                                    aria-label="Remove photo from job"
                                    disabled={deletingJobPhotoId === photo.id}
                                    onClick={(event) => handleRemoveJobPhotoFromPanel(event, photo)}
                                  >
                                    {deletingJobPhotoId === photo.id ? (
                                      <IonSpinner name="crescent" />
                                    ) : (
                                      <IonIcon icon={trashOutline} />
                                    )}
                                  </JobPhotoRemoveButton>
                                ) : null}
                              </JobPhotoTile>
                            ))}
                          </JobPhotoGrid>
                        ) : (
                          <EmptyJobState>
                            No photos yet. Add a jobsite photo when you want it saved with this job.
                          </EmptyJobState>
                        )}
                      </JobSection>

                      <JobSection>
                        <JobSectionTitle>
                          <IonIcon icon={chatbubblesOutline} />
                          Conversations
                        </JobSectionTitle>
                        {selectedJobPanelJob.conversations.length > 0 ? (
                          selectedJobPanelJob.conversations.map(conversation => (
                            <JobTimelineRow
                              key={conversation.id}
                              type="button"
                              $clickable={Boolean(conversation.sessionId)}
                              disabled={!conversation.sessionId}
                              onClick={() => handleOpenJobConversation(conversation)}
                            >
                              <span>{conversation.title}</span>
                              <span>{conversation.subtitle}</span>
                            </JobTimelineRow>
                          ))
                        ) : (
                          <EmptyJobState>
                            No conversations saved to this job yet.
                          </EmptyJobState>
                        )}
                      </JobSection>

                      <JobSection>
                        <JobSectionTitle>
                          <IonIcon icon={documentTextOutline} />
                          Work orders
                        </JobSectionTitle>
                        {selectedJobPanelJob.workOrderCount > 0 ? (
                          selectedJobPanelJob.workOrders.length > 0 ? (
                            <WorkOrderResultList
                              items={selectedJobPanelJob.workOrders}
                              onOpen={handleOpenWorkOrderResult}
                              onOpenExternal={(url) => void openUrl(url)}
                              refreshKey={`${selectedJobPanelJob.id}-${selectedJobPanelJob.workOrderCount}-${timeline.length}`}
                              compact
                            />
                          ) : (
                            <JobTimelineRow>
                              <span>{selectedJobPanelJob.workOrderCount} work order link{selectedJobPanelJob.workOrderCount === 1 ? '' : 's'} found in this chat</span>
                              <JobActionButton onClick={handleFindWorkOrdersFromPanel}>
                                Find
                              </JobActionButton>
                            </JobTimelineRow>
                          )
                        ) : (
                          <EmptyJobState>
                            No saved work orders yet.
                          </EmptyJobState>
                        )}
                      </JobSection>
                    </>
                  ) : (
                    <EmptyJobState>
                      No job selected.
                    </EmptyJobState>
                  )}
                </JobDetail>
              )}
            </JobsModalContent>
          </JobsModalBody>
        </IonContent>
      </JobsModal>

      <ArtifactImageModal
        isOpen={Boolean(selectedArtifactImage)}
        onDidDismiss={() => setSelectedArtifactImage(null)}
      >
        <IonContent className="artifact-image-modal-content" scrollY={false}>
          <ArtifactImageModalBody>
            <ArtifactImageModalHeader>
              <ArtifactImageModalTitle>
                {selectedArtifactImage?.label || 'Extracted image'}
              </ArtifactImageModalTitle>
              <ArtifactImageModalActions>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Rotate left"
                  title="Rotate left"
                  onClick={() => setArtifactImageRotation(value => value - 90)}
                >
                  <IonIcon slot="icon-only" icon={arrowUndoOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Rotate right"
                  title="Rotate right"
                  onClick={() => setArtifactImageRotation(value => value + 90)}
                >
                  <IonIcon slot="icon-only" icon={arrowRedoOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Zoom out"
                  title="Zoom out"
                  disabled={artifactImageZoom <= 0.5}
                  onClick={() => zoomArtifactImage(-0.25)}
                >
                  <IonIcon slot="icon-only" icon={removeOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Zoom in"
                  title="Zoom in"
                  disabled={artifactImageZoom >= 4}
                  onClick={() => zoomArtifactImage(0.25)}
                >
                  <IonIcon slot="icon-only" icon={addOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Reset image view"
                  title="Reset"
                  onClick={resetArtifactImageView}
                >
                  <IonIcon slot="icon-only" icon={refreshOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="light"
                  aria-label="Close image preview"
                  title="Close"
                  onClick={() => setSelectedArtifactImage(null)}
                >
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </ArtifactImageModalActions>
            </ArtifactImageModalHeader>
            <ArtifactImageModalCanvas
              onPointerDown={handleArtifactViewerPointerDown}
              onPointerMove={handleArtifactViewerPointerMove}
              onPointerUp={handleArtifactViewerPointerEnd}
              onPointerCancel={handleArtifactViewerPointerEnd}
              onWheel={handleArtifactViewerWheel}
              onDoubleClick={resetArtifactImageView}
            >
              {selectedArtifactImage?.displayUrl && (
                <ArtifactImageModalImg
                  src={selectedArtifactImage.displayUrl}
                  alt={selectedArtifactImage.label}
                  style={{
                    transform: `translate(${artifactImagePan.x}px, ${artifactImagePan.y}px) rotate(${artifactImageRotation}deg) scale(${artifactImageZoom})`,
                  }}
                />
              )}
            </ArtifactImageModalCanvas>
          </ArtifactImageModalBody>
        </IonContent>
      </ArtifactImageModal>

      <WorkOrderModal
        isOpen={Boolean(selectedWorkOrder)}
        onDidDismiss={() => {
          setSelectedWorkOrder(null);
          setCustomerSignature(null);
          setTechnicianSignature(null);
          setSignedWorkOrderUrl(null);
          setIsSelectedWorkOrderSigned(false);
          setWorkOrderSignStatus(null);
          setWorkOrderSignError(null);
          setWorkOrderEmail('');
        }}
      >
        <IonContent className="work-order-modal-content" scrollY={false}>
          <WorkOrderModalBody>
            <WorkOrderModalHeader>
              <WorkOrderModalTitle>
                <WorkOrderModalTitleMain>{selectedWorkOrder?.title || 'Work order'}</WorkOrderModalTitleMain>
                <WorkOrderModalSubtitle>
                  {effectiveSignedWorkOrderUrl ? 'Signed PDF ready' : 'Review, sign, and email'}
                </WorkOrderModalSubtitle>
              </WorkOrderModalTitle>
              <ArtifactImageModalActions>
                <IonButton
                  fill="clear"
                  color="dark"
                  aria-label="Open PDF in a new tab"
                  title="Open PDF"
                  onClick={() => void openUrl(effectiveSignedWorkOrderUrl || selectedWorkOrderUrl || '')}
                >
                  <IonIcon slot="icon-only" icon={openOutline} />
                </IonButton>
                <IonButton
                  fill="clear"
                  color="dark"
                  aria-label="Close work order"
                  title="Close"
                  onClick={() => {
                    setSelectedWorkOrder(null);
                    setIsEmailingWorkOrder(false);
                    setIsSelectedWorkOrderSigned(false);
                    setWorkOrderSignStatus(null);
                    setWorkOrderSignError(null);
                  }}
                >
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              </ArtifactImageModalActions>
            </WorkOrderModalHeader>
            <WorkOrderModalContent>
              {selectedWorkOrderUrl && (
                <WorkOrderPdfFrame
                  src={signedWorkOrderUrl || selectedWorkOrderUrl}
                  title="Work order PDF preview"
                />
              )}
              <WorkOrderSignPanel>
                <WorkOrderSignHeading>{effectiveSignedWorkOrderUrl ? 'Work order signed' : 'Sign work order'}</WorkOrderSignHeading>
                <WorkOrderSignHelp>
                  {effectiveSignedWorkOrderUrl
                    ? 'This PDF already includes signatures. You can open it or email a copy.'
                    : 'Have the customer sign below. Add the technician signature if needed, then finalize the PDF before emailing it.'}
                </WorkOrderSignHelp>
                {!effectiveSignedWorkOrderUrl && (
                  <>
                    <SignaturePad label="Customer signature" onChange={setCustomerSignature} />
                    <SignaturePad label="Technician signature (optional)" onChange={setTechnicianSignature} />
                    <WorkOrderActionStack>
                      <IonButton
                        expand="block"
                        onClick={signSelectedWorkOrder}
                        disabled={isSigningWorkOrder || !customerSignature}
                      >
                        {isSigningWorkOrder ? 'Finalizing...' : 'Finalize signed PDF'}
                      </IonButton>
                    </WorkOrderActionStack>
                  </>
                )}
                <WorkOrderActionStack>
                  <IonButton
                    expand="block"
                    fill="outline"
                    disabled={!effectiveSignedWorkOrderUrl}
                    onClick={() => void openUrl(effectiveSignedWorkOrderUrl || '')}
                  >
                    Open signed PDF
                  </IonButton>
                </WorkOrderActionStack>
                {workOrderSignStatus && <WorkOrderStatus>{workOrderSignStatus}</WorkOrderStatus>}
                {workOrderSignError && <WorkOrderStatus $error>{workOrderSignError}</WorkOrderStatus>}
                <WorkOrderFieldLabel htmlFor="work-order-email">Customer email</WorkOrderFieldLabel>
                <WorkOrderInput
                  id="work-order-email"
                  value={workOrderEmail}
                  type="email"
                  placeholder="customer@example.com"
                  onChange={(event) => setWorkOrderEmail(event.target.value)}
                />
                <WorkOrderActionStack>
                  <IonButton
                    expand="block"
                    fill="outline"
                    disabled={!effectiveSignedWorkOrderUrl || !workOrderEmail.trim() || isEmailingWorkOrder}
                    onClick={emailSignedWorkOrder}
                  >
                    {isEmailingWorkOrder ? 'Sending...' : 'Email signed PDF'}
                  </IonButton>
                </WorkOrderActionStack>
                <WorkOrderSignHelp style={{ marginTop: 10 }}>
                  Sends through the configured SmartDocs email service. The PDF includes the captured signature.
                </WorkOrderSignHelp>
              </WorkOrderSignPanel>
            </WorkOrderModalContent>
          </WorkOrderModalBody>
        </IonContent>
      </WorkOrderModal>

      <SessionTitleModal
        isOpen={showSessionTitleModal}
        onDidDismiss={() => {
          setShowSessionTitleModal(false);
          setSessionTitleError(null);
        }}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton
                fill="clear"
                disabled={isSavingSessionTitle}
                onClick={closeSessionTitleModal}
              >
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle>Chat title</IonTitle>
            <IonButtons slot="end">
              <IonButton
                fill="clear"
                strong
                disabled={!canSaveSessionTitle}
                onClick={() => void handleSaveSessionTitle()}
              >
                {isSavingSessionTitle ? 'Saving...' : 'Save'}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText>
            <p style={{ marginTop: 20, marginBottom: 0, fontSize: 16, lineHeight: 1.45 }}>
              Enter chat title
            </p>
          </IonText>
          <SessionTitleInputItem lines="full">
            <SessionTitleInput
              value={sessionTitleDraft}
              placeholder="Add title"
              autocomplete="off"
              autocapitalize="sentences"
              clearInput
              onIonInput={(event) => {
                setSessionTitleDraft(String(event.detail.value ?? ''));
                setSessionTitleError(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleSaveSessionTitle();
                }
              }}
            />
          </SessionTitleInputItem>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 8,
            fontSize: 13,
          }}>
            <IonText color={sessionTitleValidationError ? 'danger' : 'medium'}>
              <span>{sessionTitleValidationError || 'Leave blank to use the default chat title.'}</span>
            </IonText>
            <IonText color={trimmedSessionTitleDraft.length > CHAT_SESSION_TITLE_MAX_LENGTH ? 'danger' : 'medium'}>
              <span>{trimmedSessionTitleDraft.length}/{CHAT_SESSION_TITLE_MAX_LENGTH}</span>
            </IonText>
          </div>
          {sessionTitleError && (
            <IonText color="danger">
              <p style={{ marginTop: 10, marginBottom: 0, fontSize: 14 }}>{sessionTitleError}</p>
            </IonText>
          )}
        </IonContent>
      </SessionTitleModal>

      <IonAlert
        isOpen={showVoicePermissionAlert}
        onDidDismiss={() => setShowVoicePermissionAlert(false)}
        header="Allow Microphone Access"
        message={
          Capacitor.getPlatform() === 'ios'
            ? 'To use voice input, allow SmartDocs to access your microphone and speech recognition in Settings.'
            : 'To use voice input, allow SmartDocs to access the microphone in Settings.'
        }
        buttons={[
          {
            text: 'Not now',
            role: 'cancel',
          },
          {
            text: 'Open Settings',
            handler: () => {
              void openVoicePermissionSettings();
            },
          },
        ]}
      />

      {/*
        Ionic sheet modal — canonical pattern: single IonContent only.
        The delegate-host (.ion-page) is always sized to the MAX breakpoint height,
        not the current one, so any flex/height:100% child is always too tall.
        IonContent is the only child that knows how to scroll within the visible
        sheet viewport via the modal's own overflow clip. Actions go INSIDE it.
      */}
      <DownvoteFeedbackModal
        isOpen={showDownvoteModal}
        breakpoints={[0, downvoteBreakpoint, 1]}
        initialBreakpoint={downvoteBreakpoint}
        backdropBreakpoint={downvoteBreakpoint}
        onDidDismiss={() => {
          setShowDownvoteModal(false);
          setFeedbackMessageId(null);
          setSelectedDownvoteReasons([]);
          setDownvoteComment('');
          setFeedbackError(null);
        }}
      >
        <IonContent className="ion-padding">
          {/* Title row — replaces IonHeader so we stay in pure IonContent flow */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 4,
          }}>
            <IonText>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>What went wrong?</h2>
            </IonText>
            <IonButton
              fill="clear"
              size="small"
              aria-label="Close"
              disabled={isSubmittingFeedback}
              onClick={() => {
                setShowDownvoteModal(false);
                setFeedbackMessageId(null);
                setSelectedDownvoteReasons([]);
                setDownvoteComment('');
                setFeedbackError(null);
              }}
            >
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </div>
          <IonText color="medium">
            <p style={{ marginTop: 0, marginBottom: 12, fontSize: 14, lineHeight: 1.45 }}>
              Select at least one reason. Your feedback helps improve responses.
            </p>
          </IonText>
          <DownvoteReasonCard>
            {DOWNVOTE_REASON_OPTIONS.map((reason) => {
              const checked = selectedDownvoteReasons.includes(reason.value);
              return (
                <DownvoteReasonRow
                  key={reason.value}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleDownvoteReason(reason.value, !checked);
                    }
                  }}
                  onClick={(e) => {
                    const t = e.target as HTMLElement | null;
                    if (t?.closest?.('ion-checkbox')) return;
                    toggleDownvoteReason(reason.value, !checked);
                  }}
                >
                  <IonCheckbox
                    checked={checked}
                    onIonChange={(event) =>
                      toggleDownvoteReason(reason.value, Boolean(event.detail.checked))
                    }
                  />
                  <span>{reason.label}</span>
                </DownvoteReasonRow>
              );
            })}
          </DownvoteReasonCard>
          <IonText color="medium">
            <p style={{ margin: '12px 0 6px', fontSize: 13, fontWeight: 600 }}>
              Optional extra details
            </p>
          </IonText>
          <DownvoteDetailsTextarea
            value={downvoteComment}
            onIonInput={(event) => setDownvoteComment(event.detail.value ?? '')}
            placeholder="Add context (optional)"
            rows={3}
          />
          {feedbackError && (
            <IonText color="danger">
              <p style={{ marginTop: 10, marginBottom: 0, fontSize: 14 }}>{feedbackError}</p>
            </IonText>
          )}
          <DownvoteActionRow className="downvote-action-row">
            <IonButton
              expand="block"
              fill="outline"
              color="medium"
              disabled={isSubmittingFeedback}
              onClick={() => {
                setShowDownvoteModal(false);
                setFeedbackMessageId(null);
                setSelectedDownvoteReasons([]);
                setDownvoteComment('');
                setFeedbackError(null);
              }}
            >
              Cancel
            </IonButton>
            <IonButton
              expand="block"
              onClick={handleDownvoteSubmit}
              disabled={isSubmittingFeedback || selectedDownvoteReasons.length === 0}
            >
              {isSubmittingFeedback ? 'Saving...' : 'Submit'}
            </IonButton>
          </DownvoteActionRow>
        </IonContent>
      </DownvoteFeedbackModal>

      <IonToast
        isOpen={showCopyToast}
        message="Copied to clipboard"
        duration={2000}
        position="bottom"
        color="dark"
        onDidDismiss={() => setShowCopyToast(false)}
      />
      <IonToast
        isOpen={showFeedbackToast}
        message="Thank you for submitting your feedback!"
        duration={2500}
        position="bottom"
        color="success"
        onDidDismiss={() => setShowFeedbackToast(false)}
      />
      <IonToast
        isOpen={showSessionTitleToast}
        message={sessionTitleToastMessage}
        duration={2500}
        position="bottom"
        color="success"
        onDidDismiss={() => setShowSessionTitleToast(false)}
      />

      <IonFooter
        style={{
          paddingBottom: '0px',
          // Manual keyboard height calculations commented out since Ionic now handles it properly
          // transform: isKeyboardOpen ? `translateY(-${keyboardHeight}px)` : 'translateY(0)',
          // transition: 'transform 0.35s ease-out',
          backgroundColor: 'var(--ion-color-light)',
          borderTop: 'none'
        }}
      >
        <IonToolbar style={{ paddingBottom: '0px', minHeight: 'auto' }}>
          <AssistantScopeControls
            manufacturers={assistantManufacturers}
            manufacturerSlug={selectedManufacturerSlug}
            mode={assistantMode}
            loading={assistantOptionsLoading}
            error={assistantOptionsError}
            serviceTitan={serviceTitan}
            connectingServiceTitan={connectingServiceTitan}
            disabled={isAiRunActive}
            onManufacturerChange={handleManufacturerChange}
            onModeChange={setAssistantMode}
            onServiceTitanConnect={handleServiceTitanConnect}
          />
          {selectedImage && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '8px 16px 0 16px',
                padding: '8px',
                border: '1px solid var(--ion-color-medium-tint)',
                borderRadius: '10px',
                background: 'var(--ion-color-light)',
              }}
            >
              <img
                src={selectedImage.dataUrl}
                alt="Selected upload"
                style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', color: 'var(--ion-color-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedImage.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>
                  {formatBytes(selectedImage.size)}
                </div>
              </div>
              <IonButton fill="clear" onClick={() => setSelectedImage(null)} aria-label="Remove attached image">
                <IonIcon icon={closeCircleOutline} />
              </IonButton>
            </div>
          )}
          {queuedDraft && !hasActiveEscalation && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                margin: '8px 16px 0 16px',
                padding: '10px',
                border: '1px solid var(--ion-color-medium-tint)',
                borderRadius: '10px',
                background: '#ffffff',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#5f6b7a' }}>
                  Queued follow-up
                </div>
                <div style={{ marginTop: '4px', fontSize: '14px', color: 'var(--ion-color-dark)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {queuedDraft.text || '[Image attached]'}
                </div>
                {queuedDraft.image && (
                  <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <img
                      src={queuedDraft.image.dataUrl}
                      alt="Queued upload"
                      style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px' }}
                    />
                    <div style={{ fontSize: '12px', color: 'var(--ion-color-medium)' }}>{queuedDraft.image.name}</div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                <IonButton
                  size="small"
                  onClick={handleQueuedSend}
                  disabled={isQueueSubmitting}
                  title={isAiRunActive ? 'Steer the active run' : 'Send queued message'}
                >
                  <IonIcon slot="start" icon={send} />
                  {isQueueSubmitting ? 'Sending...' : (isAiRunActive ? 'Steer' : 'Send')}
                </IonButton>
                <IonButton
                  size="small"
                  fill="clear"
                  onClick={handleEditQueuedDraft}
                  disabled={isQueueSubmitting}
                  aria-label="Edit queued message"
                  title="Edit queued message"
                >
                  <IonIcon slot="icon-only" icon={createOutline} />
                </IonButton>
                <IonButton
                  size="small"
                  fill="clear"
                  color="danger"
                  onClick={handleDeleteQueuedDraft}
                  disabled={isQueueSubmitting}
                  aria-label="Delete queued message"
                  title="Delete queued message"
                >
                  <IonIcon slot="icon-only" icon={trashOutline} />
                </IonButton>
              </div>
            </div>
          )}
          {!isLoadingHistory && timeline.filter(m => m.sender === 'user').length === 0 && (
            <SuggestionChipsContainer>
              <SuggestionChipsLabel>Try asking:</SuggestionChipsLabel>
              {getSuggestionChips(user?.distributor_name, assistantMode).map((chip, i) => (
                <SuggestionChip
                  key={i}
                  onClick={() => {
                    setMessage(chip);
                  }}
                >
                  {chip}
                </SuggestionChip>
              ))}
            </SuggestionChipsContainer>
          )}
          <InputContainer ref={composerRef}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              capture="environment"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            <AttachButton
              fill="clear"
              onClick={handleAttachClick}
              disabled={isAiRunActive}
              aria-label="Add job context"
            >
              <IonIcon icon={addOutline} />
            </AttachButton>
            <MicButton
              fill="clear"
              onClick={toggleVoiceInput}
              disabled={isAiRunActive}
              aria-label={isListeningToVoice ? 'Stop voice input' : 'Start voice input'}
              title={isVoiceSupported ? 'Speak to type' : 'Voice input is not available on this device'}
              $listening={isListeningToVoice}
              $unsupported={!isVoiceSupported}
            >
              <IonIcon icon={isListeningToVoice ? stopCircleOutline : micOutline} />
            </MicButton>
            <InputWrapper>
              <IonTextarea
                ref={messageInputRef}
                value={message}
                onIonInput={(e) => handleComposerInput(String(e.detail.value ?? ''))}
                onIonChange={(e) => handleComposerInput(String(e.detail.value ?? ''))}
                onPaste={handlePasteImage}
                placeholder="Message SmartDocs"
                rows={1}
                maxlength={12000}
                auto-grow={true}
                onKeyDown={handleQueuedEnter}
                style={{
                  '--padding': '8px 12px',
                  '--border-radius': '20px',
                  '--border': '1px solid var(--ion-color-medium)',
                  '--background': 'var(--ion-color-light)',
                  '--color': 'var(--ion-color-dark)',
                  '--placeholder-color': 'var(--ion-color-medium)',
                  '--placeholder-opacity': '0.6',
                  '--min-height': '40px',
                }}
              />
              {(voiceStatus || voiceError) && (
                <VoiceStatusText $error={Boolean(voiceError)}>
                  {voiceError || voiceStatus}
                </VoiceStatusText>
              )}
            </InputWrapper>
            <SendButton
              onClick={() => handleSendMessage(message)}
              disabled={isStoppingRun || (!isAiRunActive && ((assistantMode === 'servicetitan' ? !serviceTitan.connected : !selectedManufacturerSlug) || (!message.trim() && !selectedImage)))}
              $stopping={isAiRunActive}
              aria-label={isAiRunActive ? 'Stop agent run' : 'Send message'}
              title={isAiRunActive ? 'Stop agent run' : 'Send message'}
            >
              <IonIcon icon={isAiRunActive ? stopCircleOutline : send} />
            </SendButton>
          </InputContainer>
          <DisclaimerText>
            SmartDocs can make mistakes. Check important info.
          </DisclaimerText>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default ChatSession;
